import {
  BackgroundImageSize,
  ButtonActionType,
  ButtonComponent,
  ButtonListComponent,
  ButtonListFormat,
  ButtonListProperties,
  CodeEditorComponent,
  ComponentAlignment,
  ComponentBorder,
  ComponentDescription,
  ComponentDescriptionGroup,
  ComponentGroup,
  ComponentListItem,
  ComponentRadius,
  ConditionsLabel,
  ConsentCheckboxComponent,
  CountryLabel,
  CountrySelectFilter,
  CountrySelectProperties,
  DataRepeaterComponent,
  DeadlineComponent,
  DeadlineComponentProperties,
  DeadlineDurationUnit,
  DeadlineFormat,
  DeadlineOccurrence,
  Direction,
  DiscountCodeComponent,
  FourSideStylingEntity,
  IFrameComponent,
  IFrameOverflow,
  ImageComponent,
  InternationalPhoneComponent,
  LabelPosition,
  LabelProperties,
  PositionCorner,
  SelectEffect,
  ShareComponent,
  SpacerComponent,
  SpinTheWheelProperties,
  SplittedStates,
  StepNavigationButtonDesign,
  StepNavigationLastButtonType,
  StepNavigationProgressBar,
  StepNavigationProperties,
  TextAlignment,
  TextProperties,
  Widget,
  WidgetComponent,
  WidgetComponentSubType,
  WidgetComponentType,
  WidgetConditionLanguages,
  WidgetContainer,
  WidgetDeviceView,
  WidgetFontDescription,
  WidgetLayout,
  WidgetPosition,
  WidgetState,
} from './widgets.model';
import { LanguageService } from '../core/language.service';
import {
  AccountCustomFont,
  AccountCustomFontFileType,
  AccountCustomFontType,
} from '../account-settings/account-settings-models';
import { Account } from 'app/back-office/account.model';
import { SessionConditionType } from '../audience/components/session-condition-group.model';
import { CriteriaElement } from '../search/global-criteria-list';
import { AttributionType, OnsiteCampaign, OnsiteCampaignType } from './campaign.model';
import TimeUtils from 'app/shared/utils/time-utils';
import { IntegrationType } from 'app/automations/integrations-models';
import ColorHelpers from 'app/shared/helpers/color-helpers';
import { TierAccess } from 'app/shared/account/tier.model';
import { WidgetComponentsService } from './widget-components.service';
import { AccountLanguageService } from '../core/account-language.service';
import { FontMetricsProps } from './widget-editor/shared-component/helper-editor';

export default class WidgetComponentHelpers {
  public static passwordMaxLength = 64;
  public static passwordMinLength = 8;
  public static subscriptionCheckboxIntegrations: IntegrationType[] = [
    IntegrationType.Rule,
    IntegrationType.Klaviyo,
    IntegrationType.Custobar,
    //TODO: Fix Brevo SCL. IntegrationType.Brevo,
  ];
  private static headerIcon = 'title';

  public static isWidgetComponent(arg: any): arg is WidgetComponent {
    if (this.isWidgetState(arg) || this.isWidgetContainer(arg)) {
      return false;
    }
    return (arg as WidgetComponent).type !== undefined && (arg as WidgetComponent).type in WidgetComponentType;
  }

  public static isFloatingComponent(arg: any): boolean {
    if (!arg) {
      return false;
    }
    return (
      arg.subType === WidgetComponentSubType.ButtonHide ||
      arg.subType === WidgetComponentSubType.ImageFloating ||
      arg.subType === WidgetComponentSubType.TextFloating
    );
  }

  public static isWidgetContainer(arg: any): arg is WidgetContainer {
    return (arg as WidgetContainer).components !== undefined;
  }

  public static isWidgetState(arg: any): arg is WidgetState {
    return (
      (arg as WidgetState).position !== undefined &&
      [WidgetComponentType.Success, WidgetComponentType.Teaser, WidgetComponentType.Regular].indexOf(
        (arg as WidgetState).type,
      ) !== -1
    );
  }

  public static isWidgetStatePanel(arg: any): arg is WidgetState {
    if (!this.isWidgetState(arg)) {
      return false;
    }
    return (
      (arg as WidgetState).position === WidgetPosition.Bottom || (arg as WidgetState).position === WidgetPosition.Top
    );
  }

  public static getWidgetComponentTitle(arg: any, isParentName?: boolean, isGrandparentName?: boolean) {
    if (!arg) {
      return undefined;
    }
    if (isParentName) {
      return this.getComponentDescriptionBySubtype((arg as WidgetComponent).parentComponentSubType)?.title;
    } else if (isGrandparentName) {
      return this.getComponentDescriptionBySubtype((arg as WidgetComponent).grandparentSubType)?.title;
    }
    if (this.isWidgetState(arg)) {
      if ((arg as WidgetState).isPrimaryCanvasState) {
        return '';
      }
      return this.getStateDescription().title;
    }
    if (this.isWidgetContainer(arg)) {
      return this.getContainerDescription(arg.type).title;
    }
    if (this.isWidgetComponent(arg)) {
      const component = arg as WidgetComponent;
      if (
        component.parentComponentType === WidgetComponentType.StepNavigation &&
        component.type === WidgetComponentType.Button
      ) {
        if (component.actionType === ButtonActionType.SwitchStatePrevious) {
          return 'campaign.widget.component.stepNavigation.previous-button-title';
        }
        if (component.actionType === ButtonActionType.SwitchStateNext) {
          return 'campaign.widget.component.stepNavigation.next-button-title';
        }
        // Last child - always "Last button" regardless of lastButtonType (Normal/Close/Submit).
        return 'campaign.widget.component.stepNavigation.last-button-title';
      }
      const isHeader = this.isHeader(arg);
      return this.getComponentDescriptionBySubtype(component.subType, isHeader).title;
    }
    return undefined;
  }

  public static getWidgetComponentIcon(arg: any) {
    if (!arg) {
      return undefined;
    }
    if (this.isWidgetState(arg)) {
      return this.getStateDescription().icon;
    }
    if (this.isWidgetContainer(arg)) {
      return this.getContainerDescription(arg.type).icon;
    }
    if (this.isWidgetComponent(arg)) {
      const isHeader = this.isHeader(arg);
      return this.getComponentDescriptionBySubtype((arg as WidgetComponent).subType, isHeader).icon;
    }
  }

  private static isHeader(component: WidgetComponent): boolean {
    let isHeader = false;
    if (component.subType === WidgetComponentSubType.TextRegular) {
      isHeader = component.textProperties?.text?.includes('<h');
    }
    return isHeader;
  }

  public static getWidgetComponentDescriptionText(componentItem: WidgetComponent) {
    if (!componentItem) {
      return undefined;
    }

    if (componentItem.subType === WidgetComponentSubType.ButtonShare) {
      return 'campaign.widget.component.share-campaign.button-description';
    }
    if (componentItem.subType === WidgetComponentSubType.TextCopy) {
      return 'campaign.widget.component.share-campaign.text-description';
    }
    return undefined;
  }

  public static getStateDescription() {
    return new ComponentDescription('campaign.widget.state.canvas', 'tb:canvas', undefined, undefined);
  }

  public static getContainerDescription(type?: WidgetComponentType, isAdminOnly?: boolean) {
    switch (type) {
      default:
        return new ComponentDescription(
          'campaign.widget.container.title',
          'chrome_reader_mode',
          WidgetComponentType.ContainerRegular,
          WidgetComponentSubType.ContainerRegular,
          true,
          isAdminOnly,
        );
    }
  }

  public static getComponentDescriptionBySubtype(
    componentSubtype: WidgetComponentSubType,
    isHeader = false,
  ): ComponentDescription {
    if (!componentSubtype) return;
    switch (componentSubtype) {
      case WidgetComponentSubType.ContainerRegular:
        return this.getContainerDescription(WidgetComponentType.ContainerRegular);
      case WidgetComponentSubType.TextRegular:
        return new ComponentDescription(
          isHeader ? 'campaign.widget.component.text.headline-title' : 'campaign.widget.component.text.title',
          isHeader ? this.headerIcon : 'tb:a-letter',
          WidgetComponentType.Text,
          componentSubtype,
        );
      case WidgetComponentSubType.TextFloating:
        return new ComponentDescription(
          'campaign.widget.component.text.floating-title',
          'sale-sticker.svg',
          WidgetComponentType.Text,
          componentSubtype,
        );
      case WidgetComponentSubType.ImageRegular:
        return new ComponentDescription(
          'campaign.widget.component.image.title',
          'photo',
          WidgetComponentType.Image,
          componentSubtype,
        );
      case WidgetComponentSubType.ImageFloating:
        return new ComponentDescription(
          'campaign.widget.component.image.floating-title',
          'photo',
          WidgetComponentType.Image,
          componentSubtype,
        );
      case WidgetComponentSubType.IFrameRegular:
        return new ComponentDescription(
          'campaign.widget.component.iframe.title',
          'tb:iframe-icon',
          WidgetComponentType.IFrame,
          componentSubtype,
        );
      case WidgetComponentSubType.ButtonRegular:
        return new ComponentDescription(
          'campaign.widget.component.button.title',
          'tb:button-editor',
          WidgetComponentType.Button,
          componentSubtype,
        );
      case WidgetComponentSubType.ButtonSubmit:
        return new ComponentDescription(
          'campaign.widget.component.button.submit-title',
          'tb:button-editor',
          WidgetComponentType.Button,
          componentSubtype,
        );
      case WidgetComponentSubType.ButtonInstagramFollow:
        return new ComponentDescription(
          'campaign.widget.component.button.instagram-title',
          'tb:instagram',
          WidgetComponentType.Button,
          componentSubtype,
        );
      case WidgetComponentSubType.ButtonHide:
        return new ComponentDescription(
          'campaign.widget.component.button.hide-title',
          'highlight_off',
          WidgetComponentType.Button,
          componentSubtype,
        );

      case WidgetComponentSubType.InputTextEmail:
        return new ComponentDescription(
          'campaign.widget.component.input-text-email.title',
          'tb:input-field',
          WidgetComponentType.InputText,
          componentSubtype,
        );
      case WidgetComponentSubType.InputTextPassword:
        return new ComponentDescription(
          'campaign.widget.component.input-text-password.title',
          'lock',
          WidgetComponentType.InputText,
          componentSubtype,
          true,
          false,
        );
      case WidgetComponentSubType.InputTextName:
        return new ComponentDescription(
          'campaign.widget.component.input-text-name.title',
          'tb:input-field',
          WidgetComponentType.InputText,
          componentSubtype,
        );
      case WidgetComponentSubType.InputTextFirstName:
        return new ComponentDescription(
          'campaign.widget.component.input-text-name.first-name-title',
          'tb:input-field',
          WidgetComponentType.InputText,
          componentSubtype,
        );
      case WidgetComponentSubType.InputTextLastName:
        return new ComponentDescription(
          'campaign.widget.component.input-text-name.last-name-title',
          'tb:input-field',
          WidgetComponentType.InputText,
          componentSubtype,
        );
      case WidgetComponentSubType.InputTextPhone:
        return new ComponentDescription(
          'campaign.widget.component.input-text-phone.title',
          'tb:telephone-dual',
          WidgetComponentType.InputText,
          componentSubtype,
        );
      case WidgetComponentSubType.InputTextArea:
        return new ComponentDescription(
          'campaign.widget.component.input-textarea.title',
          'tb:input-field',
          WidgetComponentType.InputText,
          componentSubtype,
        );
      case WidgetComponentSubType.InputTextRegular:
        return new ComponentDescription(
          'campaign.widget.component.input-text-regular.title',
          'tb:input-field',
          WidgetComponentType.InputText,
          componentSubtype,
        );
      case WidgetComponentSubType.DateRegular:
        return new ComponentDescription(
          'campaign.widget.component.input-date.title',
          'tb:calendar',
          WidgetComponentType.Date,
          componentSubtype,
        );
      case WidgetComponentSubType.ButtonListRadio:
        return new ComponentDescription(
          'campaign.widget.component.radioButtons.title',
          'tb:radio-buttons',
          WidgetComponentType.ButtonList,
          componentSubtype,
        );
      case WidgetComponentSubType.ButtonListChoices:
        return new ComponentDescription(
          'campaign.widget.component.radioButtons.title-choices',
          'tb:button-choices1',
          WidgetComponentType.ButtonList,
          componentSubtype,
          true,
        );
      case WidgetComponentSubType.ButtonListCheckbox:
        return new ComponentDescription(
          'campaign.widget.component.radioButtons.title-checkboxes',
          'tb:checkbox-list',
          WidgetComponentType.ButtonList,
          componentSubtype,
          true,
        );
      case WidgetComponentSubType.DropdownRegular:
        return new ComponentDescription(
          'campaign.widget.component.dropdown.title',
          'tb:dropdown-icon',
          WidgetComponentType.Dropdown,
          componentSubtype,
        );
      case WidgetComponentSubType.DropdownCountry:
        return new ComponentDescription(
          'campaign.widget.component.dropdown.title-country',
          'flag_circle',
          WidgetComponentType.Dropdown,
          componentSubtype,
        );
      case WidgetComponentSubType.ButtonListStarRating:
        return new ComponentDescription(
          'campaign.widget.component.buttonChoices.title-star',
          'star_outline',
          WidgetComponentType.ButtonList,
          componentSubtype,
        );
      case WidgetComponentSubType.ButtonListCsatStarRating:
        return new ComponentDescription(
          'campaign.widget.component.buttonChoices.title-csat-star',
          'star_outline',
          WidgetComponentType.ButtonList,
          componentSubtype,
        );
      case WidgetComponentSubType.ButtonListNumericalRating:
        return new ComponentDescription(
          'campaign.widget.component.buttonChoices.title-numerical',
          'looks_one',
          WidgetComponentType.ButtonList,
          componentSubtype,
        );
      case WidgetComponentSubType.ButtonListCsatNumericalRating:
        return new ComponentDescription(
          'campaign.widget.component.buttonChoices.title-csat-numerical',
          'looks_one',
          WidgetComponentType.ButtonList,
          componentSubtype,
        );
      case WidgetComponentSubType.ButtonListNpsRating:
        return new ComponentDescription(
          'campaign.widget.component.buttonChoices.title-nps',
          'looks_one',
          WidgetComponentType.ButtonList,
          componentSubtype,
        );
      case WidgetComponentSubType.ButtonListSmileyRating:
        return new ComponentDescription(
          'campaign.widget.component.buttonChoices.title-smile',
          'mood',
          WidgetComponentType.ButtonList,
          componentSubtype,
        );
      case WidgetComponentSubType.ButtonListCsatSmileyRating:
        return new ComponentDescription(
          'campaign.widget.component.buttonChoices.title-csat-smile',
          'mood',
          WidgetComponentType.ButtonList,
          componentSubtype,
        );
      case WidgetComponentSubType.ButtonListThumbRating:
        return new ComponentDescription(
          'campaign.widget.component.buttonChoices.title-thumb',
          'thumb_up_off_alt',
          WidgetComponentType.ButtonList,
          componentSubtype,
        );

      case WidgetComponentSubType.CsatPlaceholder:
        return new ComponentDescription(
          'campaign.widget.component.buttonChoices.title-csat',
          'mood',
          WidgetComponentType.ButtonList,
          componentSubtype,
        );

      case WidgetComponentSubType.ButtonListSubscriptionCheckbox:
        return new ComponentDescription(
          'campaign.widget.component.subscription-checkbox.title',
          'tb:checkbox-list',
          WidgetComponentType.ButtonList,
          componentSubtype,
          true,
          false,
        );

      case WidgetComponentSubType.SpacerRegular:
        return new ComponentDescription(
          'campaign.widget.component.spacer.title',
          'vertical_align_bottom',
          WidgetComponentType.Spacer,
          componentSubtype,
        );
      case WidgetComponentSubType.DiscountCodeRegular:
        return new ComponentDescription(
          'campaign.widget.component.discountCode.code',
          'tb:discount-code',
          WidgetComponentType.DiscountCode,
          componentSubtype,
          true,
        );
      case WidgetComponentSubType.ShareCouponCode:
        return new ComponentDescription(
          'campaign.widget.component.discountCode.single-use',
          'tb:discount-code',
          WidgetComponentType.Share,
          componentSubtype,
          true,
        );
      case WidgetComponentSubType.InternationalPhoneRegular:
        return new ComponentDescription(
          'campaign.widget.component.internationalPhone.title',
          'tb:telephone-dual',
          WidgetComponentType.InternationalPhone,
          componentSubtype,
          true,
        );
      case WidgetComponentSubType.CheckboxConsent:
        return new ComponentDescription(
          'campaign.widget.component.consent.checkbox',
          'security',
          WidgetComponentType.Checkbox,
          componentSubtype,
          true,
        );

      case WidgetComponentSubType.GroupRegular:
        return new ComponentDescription(
          'campaign.widget.component.group.title',
          'view_column',
          WidgetComponentType.Group,
          componentSubtype,
          true,
        );
      case WidgetComponentSubType.GroupTwoColumns:
        return new ComponentDescription(
          'campaign.widget.component.group.twoColumnTitle',
          'tb:columns-2',
          WidgetComponentType.Group,
          componentSubtype,
          true,
        );
      case WidgetComponentSubType.GroupThreeColumns:
        return new ComponentDescription(
          'campaign.widget.component.group.threeColumnTitle',
          'tb:columns-3',
          WidgetComponentType.Group,
          componentSubtype,
          true,
        );
      case WidgetComponentSubType.GroupFourColumns:
        return new ComponentDescription(
          'campaign.widget.component.group.fourColumnTitle',
          'tb:columns-3',
          WidgetComponentType.Group,
          componentSubtype,
          true,
        );
      case WidgetComponentSubType.GroupFiveColumns:
        return new ComponentDescription(
          'campaign.widget.component.group.fiveColumnTitle',
          'tb:columns-3',
          WidgetComponentType.Group,
          componentSubtype,
          true,
        );
      case WidgetComponentSubType.GroupFirstAndLastName:
        return new ComponentDescription(
          'campaign.widget.component.group.firstAndLastName',
          'tb:first-and-lastname',
          WidgetComponentType.Group,
          componentSubtype,
          true,
        );
      case WidgetComponentSubType.GroupEmailAndSubmit:
        return new ComponentDescription(
          'campaign.widget.component.group.emailAndSubmit',
          'tb:email-and-submit',
          WidgetComponentType.Group,
          componentSubtype,
          true,
        );
      case WidgetComponentSubType.GroupTwoButtons:
        return new ComponentDescription(
          'campaign.widget.component.group.twoButtons',
          'tb:yes-and-no',
          WidgetComponentType.Group,
          componentSubtype,
          true,
        );

      case WidgetComponentSubType.BulletListRegular:
        return new ComponentDescription(
          'campaign.widget.component.bulletList.title',
          'list',
          WidgetComponentType.BulletList,
          componentSubtype,
          true,
        );
      case WidgetComponentSubType.DeadlineRegular:
        return new ComponentDescription(
          'campaign.widget.component.deadline.title',
          'access_alarms',
          WidgetComponentType.Deadline,
          componentSubtype,
          true,
        );
      case WidgetComponentSubType.DeadlineCountdown:
        return new ComponentDescription(
          'campaign.widget.component.countdown.title',
          'timer',
          WidgetComponentType.Deadline,
          componentSubtype,
          true,
          false,
          true,
        );
      case WidgetComponentSubType.PlaceholderRegular:
        return new ComponentDescription(
          'campaign.widget.component.placeholder.title',
          'add_circle_outline',
          WidgetComponentType.Placeholder,
          componentSubtype,
          true,
        );

      case WidgetComponentSubType.ShareCampaignGroup:
        return new ComponentDescription(
          'campaign.widget.component.share-campaign.title',
          'share',
          WidgetComponentType.ShareCampaign,
          componentSubtype,
          true,
        );

      case WidgetComponentSubType.ButtonShare:
        return new ComponentDescription(
          'campaign.widget.component.share-campaign.button',
          'mobile_screen_share',
          WidgetComponentType.Button,
          componentSubtype,
          true,
        );

      case WidgetComponentSubType.TextCopy:
        return new ComponentDescription(
          'campaign.widget.component.share-campaign.text',
          'content_copy',
          WidgetComponentType.Text,
          componentSubtype,
          true,
        );

      case WidgetComponentSubType.CodeEditorHtml:
        return new ComponentDescription(
          'campaign.widget.component.code-editor.title',
          'code',
          WidgetComponentType.CodeEditor,
          componentSubtype,
          true,
        );

      case WidgetComponentSubType.SpinTheWheel:
        return new ComponentDescription(
          'campaign.widget.component.spin-the-wheel.title',
          'attractions',
          WidgetComponentType.SpinTheWheel,
          componentSubtype,
          true,
          false,
        );

      case WidgetComponentSubType.Memory:
        return new ComponentDescription(
          'campaign.widget.component.memory.title',
          'cards.svg',
          WidgetComponentType.Memory,
          componentSubtype,
          true,
          false,
        );
      case WidgetComponentSubType.DataRepeaterRegular:
        return new ComponentDescription(
          'campaign.widget.component.data-repeater.title',
          'dataset',
          WidgetComponentType.DataRepeater,
          WidgetComponentSubType.DataRepeaterRegular,
          true,
          false,
          true,
          undefined,
          false,
          true,
        );
      case WidgetComponentSubType.DataRepeaterLeaderboard:
        return new ComponentDescription(
          'data-sources.Leaderboard',
          'format_list_numbered',
          WidgetComponentType.DataRepeater,
          WidgetComponentSubType.DataRepeaterLeaderboard,
          true,
          false,
          true,
          undefined,
          false,
          true,
        );

      case WidgetComponentSubType.StepContainerRegular:
        return new ComponentDescription(
          'campaign.widget.component.step-container.title',
          'wysiwyg',
          WidgetComponentType.StepContainer,
          componentSubtype,
          true,
          false,
          true,
          undefined,
          true,
        );

      case WidgetComponentSubType.StepNavigationRegular:
        return new ComponentDescription(
          'campaign.widget.component.progressbar.title',
          'more_horiz',
          WidgetComponentType.StepNavigation,
          componentSubtype,
          true,
          false,
          true,
          undefined,
          true,
        );

      default:
        throw Error('Unknown component subtype found when getting a description: ' + componentSubtype);
    }
  }

  public static prepareDescriptionsForTiers(
    allGroupedDescriptions: Array<ComponentDescriptionGroup>,
    tierAccess: TierAccess,
  ): Array<ComponentDescriptionGroup> {
    allGroupedDescriptions.forEach((group) => {
      group.components.forEach((description) => {
        if (description.subType === WidgetComponentSubType.CodeEditorHtml && !tierAccess?.isScriptsAllowed) {
          description.isDisabled = true;
          description.disabledReason = 'subscription.new-usage.upgrade-needed';
        }
      });
    });
    return allGroupedDescriptions;
  }

  public static checkAvailablePasswordComp(activeIntegrations, groupedComponentsDescription) {
    let isClipBoardHasPasswordComp = false;
    if (
      activeIntegrations?.length &&
      !activeIntegrations.find((app) => app.type === IntegrationType.Centra || app.type === IntegrationType.Shopify)
    ) {
      groupedComponentsDescription.forEach((item) => {
        item.components = item.components?.filter((component) => {
          if (
            item.title === 'campaign.widget.componentSection.clip-board' &&
            component.subType === WidgetComponentSubType.InputTextPassword
          ) {
            isClipBoardHasPasswordComp = true;
          }
          return component.subType !== WidgetComponentSubType.InputTextPassword;
        });
      });
      if (isClipBoardHasPasswordComp) {
        const descriptionClipBoard = groupedComponentsDescription?.filter(
          (descr) => descr.title === 'campaign.widget.componentSection.clip-board',
        )[0];
        if (!descriptionClipBoard) return;
        const placeholderComps = descriptionClipBoard.components?.filter(
          (comp) => comp.subType === WidgetComponentSubType.ClipboardPlaceHolder,
        );
        if (descriptionClipBoard.components?.length === 3) {
          const placeholderDescription = new ComponentDescription(
            'campaign.widget.componentSection.clipboard-placeholder',
            'assignment',
            WidgetComponentType.ContainerRegular,
            WidgetComponentSubType.ClipboardPlaceHolder,
            true,
            false,
            false,
            ++placeholderComps[placeholderComps.length - 1].clipId,
          );
          placeholderDescription.isDisabled = true;
          placeholderDescription.isDropDisabled = true;
          descriptionClipBoard.components?.push(placeholderDescription);
        }
      }
    }
  }

  public static createBlankWidgetComponentFromSubtype(
    widget: Widget,
    componentSubtype: WidgetComponentSubType,
    languageService: LanguageService,
    isChild: boolean,
    currentAccount: Account,
    data?: any,
    description?: ComponentDescription,
    accountLanguageService?: AccountLanguageService,
  ): WidgetComponent {
    let componentDescription;
    if (description) {
      componentDescription = description;
    } else {
      componentDescription = this.getComponentDescriptionBySubtype(componentSubtype);
    }

    if (componentDescription.type === WidgetComponentType.ContainerRegular) {
      let blankContainer = {
        type: componentDescription.type,
        subType: componentDescription.subType,
      } as WidgetContainer;
      blankContainer = this.populateWidgetContainerWithDefaultValues(blankContainer);
      return blankContainer as unknown as WidgetComponent;
    }

    let blankComponent = {
      type: componentDescription.type,
      subType: componentDescription.subType,
    } as WidgetComponent;
    blankComponent.alignment = {
      horizontal: ComponentAlignment.Center,
      vertical: ComponentAlignment.Center,
    };

    switch (componentDescription.type) {
      case WidgetComponentType.Text:
        blankComponent = this.populateTextComponentWithDefaultValues(
          blankComponent as WidgetComponent,
          isChild,
          languageService,
          currentAccount,
          componentDescription.icon === this.headerIcon,
          accountLanguageService,
        );
        break;
      case WidgetComponentType.Image:
        blankComponent = this.populateImageComponentWithDefaultValues(blankComponent as ImageComponent, isChild, data);
        break;
      case WidgetComponentType.Button:
        blankComponent = this.populateButtonComponentWithDefaultValues(
          blankComponent as ButtonComponent,
          isChild,
          languageService,
          currentAccount,
          accountLanguageService,
        );
        break;
      case WidgetComponentType.InputText:
        blankComponent = this.populateInputTextComponentWithDefaultValues(
          blankComponent as WidgetComponent,
          data,
          languageService,
          currentAccount,
          accountLanguageService,
        );
        break;
      case WidgetComponentType.Date:
        blankComponent = this.populateInputDateComponentWithDefaultValues(
          blankComponent as WidgetComponent,
          data,
          languageService,
          currentAccount,
          accountLanguageService,
        );
        break;
      case WidgetComponentType.DiscountCode:
        blankComponent = this.populateDiscountCodeComponentWithDefaultValues(
          blankComponent as DiscountCodeComponent,
          widget,
          languageService,
          currentAccount,
          accountLanguageService,
        );
        break;

      case WidgetComponentType.ShareCampaign:
        blankComponent = this.populateShareCampaignComponentWithDefaultValues(
          blankComponent as ComponentGroup,
          languageService,
          currentAccount,
          accountLanguageService,
        );
        break;
      case WidgetComponentType.Share:
        blankComponent = this.populateShareComponentWithDefaultValues(
          blankComponent as ShareComponent,
          languageService,
          currentAccount,
          accountLanguageService,
        );
        break;
      case WidgetComponentType.CodeEditor:
        blankComponent = this.populateCodeEditorComponentWithDefaultValues(
          blankComponent as CodeEditorComponent,
          languageService,
        );
        break;
      case WidgetComponentType.Spacer:
        (blankComponent as SpacerComponent).height = '24px';
        break;
      case WidgetComponentType.Checkbox:
        blankComponent = this.populateConsentComponentWithDefaultValues(
          blankComponent as ConsentCheckboxComponent,
          isChild,
          languageService,
          currentAccount,
          accountLanguageService,
        );
        break;
      case WidgetComponentType.Group:
        blankComponent = this.populateGroupComponentWithDefaultValues(
          blankComponent as ComponentGroup,
          widget,
          languageService,
          currentAccount,
          data,
          accountLanguageService,
        );
        break;
      case WidgetComponentType.BulletList:
        blankComponent = this.populateBulletListComponentWithDefaultValues(
          blankComponent as WidgetComponent,
          languageService,
          currentAccount,
          accountLanguageService,
        );
        break;
      case WidgetComponentType.ButtonList:
        blankComponent = this.populateButtonListComponentWithDefaultValues(
          blankComponent as ButtonListComponent,
          widget,
          data,
          languageService,
          currentAccount,
          accountLanguageService,
        );
        break;
      case WidgetComponentType.Deadline:
        blankComponent = this.populateDeadlineComponentWithDefaultValues(
          blankComponent as DeadlineComponent,
          widget,
          languageService,
          currentAccount,
          accountLanguageService,
        );
        if (blankComponent.subType === WidgetComponentSubType.DeadlineCountdown) {
          (blankComponent as DeadlineComponent).deadlineProperties.duration = 1;
          (blankComponent as DeadlineComponent).deadlineProperties.durationUnit = DeadlineDurationUnit.Hours;
        }
        break;
      case WidgetComponentType.Dropdown:
        blankComponent = this.populateDropdownComponentWithDefaultValues(
          blankComponent as WidgetComponent,
          data,
          languageService,
          currentAccount,
          accountLanguageService,
        );
        break;
      case WidgetComponentType.IFrame:
        blankComponent = this.populateIFrameComponentWithDefaultValues(
          blankComponent as IFrameComponent,
          accountLanguageService,
        );
        break;
      case WidgetComponentType.InternationalPhone:
        blankComponent = this.populateInternationalPhoneComponentWithDefaultValues(
          widget,
          blankComponent as InternationalPhoneComponent,
          data,
          languageService,
          currentAccount,
          accountLanguageService,
        );
        break;
      case WidgetComponentType.SpinTheWheel:
        blankComponent = this.populateSpinTheWheelComponentWithDefaultValues(
          blankComponent as WidgetComponent,
          languageService,
          currentAccount,
          accountLanguageService,
        );
        break;
      case WidgetComponentType.Memory:
        blankComponent = this.populateMemoryComponentWithDefaultValues(
          blankComponent as WidgetComponent,
          languageService,
          currentAccount,
          accountLanguageService,
        );
        break;
      case WidgetComponentType.DataRepeater:
        blankComponent = this.populateDataRepeaterComponentWithDefaultValues(
          blankComponent as DataRepeaterComponent,
          languageService,
          currentAccount,
          accountLanguageService,
        );
        break;
      case WidgetComponentType.StepContainer:
        blankComponent = blankComponent as WidgetComponent;
        break;
      case WidgetComponentType.StepNavigation:
        blankComponent = this.populateStepNavigationComponentWithDefaultValues(
          blankComponent as DataRepeaterComponent,
          widget,
          languageService,
          currentAccount,
          accountLanguageService,
        );
        break;
      default:
        throw Error('Unknown component type found when getting a description: ' + componentDescription.type);
    }
    this.clearComponentForBrandVariables(widget, blankComponent);
    return blankComponent;
  }

  private static clearComponentForBrandVariables(widget: Widget, component: WidgetComponent) {
    if (widget.brandVariables) {
      const isConsentCheckbox =
        component.type === WidgetComponentType.Checkbox && component.subType === WidgetComponentSubType.CheckboxConsent;
      const isDeadline = component.type === WidgetComponentType.Deadline;
      const isButton =
        component.type === WidgetComponentType.Button && component.subType !== WidgetComponentSubType.ButtonHide;
      const isInput =
        component.type === WidgetComponentType.InputText || component.type === WidgetComponentType.InternationalPhone;
      const isDropDown = component.type === WidgetComponentType.Dropdown;
      const isCloseButton = component.subType === WidgetComponentSubType.ButtonHide;

      if (component.textProperties) {
        component.textProperties.color = undefined;
        component.textProperties.font = undefined;
        component.textProperties.fontWeight = undefined;
        if (!isConsentCheckbox) {
          component.textProperties.fontSize = undefined;
        }
        if (!isDeadline && !isConsentCheckbox) {
          component.textProperties.lineHeight = undefined;
        }
      }

      if (component.label) {
        component.label.color = undefined;
        component.label.font = undefined;
        component.label.lineHeight = undefined;
        component.label.fontWeight = undefined;
        component.label.fontSize = undefined;
      }
      if (component.buttonListProperties && !this.isSingleComponentSurvey(component)) {
        component.buttonListProperties.uncheckedFontColor = undefined;
        component.buttonListProperties.checkedFontColor = undefined;
        if (component.subType === WidgetComponentSubType.ButtonListChoices) {
          const bvBtnBackgroundColor = widget.brandVariables?.button.backgroundColor ?? '#636363';
          const bvBtnBorder = widget.brandVariables?.button.border;
          component.buttonListProperties.radius = undefined;
          component.buttonListProperties.checkedBorder = undefined;
          component.buttonListProperties.uncheckedBorder = undefined;
          component.buttonListProperties.checkedIconUrl = undefined;
          component.buttonListProperties.uncheckedIconUrl = undefined;
          component.buttonListProperties.format = ButtonListFormat.TextOnly;
        }
      }
      if (component.spinTheWheelProperties) {
        component.spinTheWheelProperties.prizeFont = undefined;
        component.spinTheWheelProperties.sliceFont = undefined;
        component.spinTheWheelProperties.spinButtonFont = undefined;
      }
      if (component.memoryProperties) {
        component.memoryProperties.resultFont = undefined;
      }

      if (isButton) {
        component.radius = undefined;
        component.border = undefined;
        component.background = {};
        component.height = undefined;
        if (widget.brandVariables.button.border?.width) {
          component.border = undefined;
        }
        if (widget.brandVariables.button.margin) {
          component.margin = undefined;
        }
        if (widget.brandVariables.button.padding) {
          component.padding = undefined;
        }
      }

      if (isInput) {
        component.radius = undefined;
        component.border = undefined;
        component.background = {};
        if (component.subType !== WidgetComponentSubType.InputTextArea) {
          component.height = undefined;
        }
        if (widget.brandVariables.input.border?.width) {
          component.border = undefined;
        }
        if (widget.brandVariables.input.margin) {
          component.margin = undefined;
        }
        if (widget.brandVariables.input.padding) {
          component.padding = undefined;
        }
      }

      if (isDropDown) {
        component.radius = undefined;
        component.border = undefined;
        component.background = {};
        component.height = undefined;
        if (widget.brandVariables.input.border?.width) {
          component.border = undefined;
        }
        if (widget.brandVariables.input.margin) {
          component.margin = undefined;
        }
        if (widget.brandVariables.input.padding) {
          component.padding = undefined;
        }
      }

      if (isCloseButton) {
        component.iconColor = undefined;
      }
    }
  }

  private static populateWidgetContainerWithDefaultValues(container: any) {
    container.percentWidth = 10;
    container.hideOnDesktop = false;
    container.hideOnMobile = false;
    return container;
  }

  private static populateImageComponentWithDefaultValues(component: ImageComponent, isChild: boolean, data: any) {
    component.url = data.uri;
    component.fileName = data.fileName;
    if (!isChild) {
      component.margin = {
        top: '8px',
        bottom: '8px',
        left: '0px',
        right: '0px',
      } as FourSideStylingEntity;
    }
    if (component.subType === WidgetComponentSubType.ImageFloating) {
      component.positionCorner = PositionCorner.TopLeft;
      component.positionX = 0;
      component.positionY = 0;
    }
    return component;
  }

  private static populateGroupComponentWithDefaultValues(
    component: ComponentGroup,
    widget: Widget,
    languageService: LanguageService,
    currentAccount: Account,
    data?: any,
    accountLanguageService?: AccountLanguageService,
  ) {
    if (component.subType === WidgetComponentSubType.GroupFirstAndLastName) {
      const firstNameField = this.createBlankWidgetComponentFromSubtype(
        widget,
        WidgetComponentSubType.InputTextFirstName,
        languageService,
        true,
        currentAccount,
        data,
        this.getComponentDescriptionBySubtype(WidgetComponentSubType.InputTextFirstName),
        accountLanguageService,
      );
      firstNameField.width = '50%';
      if (firstNameField.margin?.right) {
        firstNameField.margin.right = '4px';
      } else {
        firstNameField.margin = {
          top: '0px',
          bottom: '0px',
          left: '0px',
          right: '4px',
        } as FourSideStylingEntity;
      }
      const lastNameField = this.createBlankWidgetComponentFromSubtype(
        widget,
        WidgetComponentSubType.InputTextLastName,
        languageService,
        true,
        currentAccount,
        data,
        this.getComponentDescriptionBySubtype(WidgetComponentSubType.InputTextLastName),
        accountLanguageService,
      );
      lastNameField.width = '50%';
      if (lastNameField.margin?.left) {
        lastNameField.margin.left = '4px';
      } else {
        lastNameField.margin = {
          top: '0px',
          bottom: '0px',
          left: '4px',
          right: '0px',
        } as FourSideStylingEntity;
      }
      component.children = [firstNameField, lastNameField];
    } else if (component.subType === WidgetComponentSubType.GroupEmailAndSubmit) {
      const emailField = this.createBlankWidgetComponentFromSubtype(
        widget,
        WidgetComponentSubType.InputTextEmail,
        languageService,
        true,
        currentAccount,
        data,
        this.getComponentDescriptionBySubtype(WidgetComponentSubType.InputTextEmail),
        accountLanguageService,
      );
      emailField.width = '60%';
      emailField.radius = {
        topLeft: '4px',
        bottomLeft: '4px',
        topRight: '0px',
        bottomRight: '0px',
      };
      const submitButton = this.createBlankWidgetComponentFromSubtype(
        widget,
        WidgetComponentSubType.ButtonSubmit,
        languageService,
        true,
        currentAccount,
        data,
        this.getComponentDescriptionBySubtype(WidgetComponentSubType.ButtonSubmit),
        accountLanguageService,
      );
      submitButton.width = '40%';
      submitButton.radius = {
        topLeft: '0px',
        bottomLeft: '0px',
        topRight: '4px',
        bottomRight: '4px',
      };
      if (emailField.label) {
        emailField.label.text = '';
      }
      if (emailField.margin?.bottom) {
        emailField.margin.bottom = '8px';
      }
      emailField.label.margin = {
        top: '0px',
        right: '0px',
        bottom: '0px',
        left: '0px',
      } as FourSideStylingEntity;
      submitButton.margin = emailField.margin as FourSideStylingEntity;
      submitButton.height = '36px';
      if (widget.brandVariables) {
        const inputBorder = widget.brandVariables.input?.border;
        const buttonBorder = widget.brandVariables.button?.border;
        emailField.border = {
          color: inputBorder?.color ?? '#ccccccff',
          left: inputBorder?.width ?? '1px',
          right: '0px',
          top: inputBorder?.width ?? '1px',
          bottom: inputBorder?.width ?? '1px',
          style: 'solid',
        } as ComponentBorder;
        submitButton.border = {
          color: buttonBorder?.color ?? '#ccccccff',
          left: '0px',
          right: '0px',
          top: '0px',
          bottom: '0px',
          style: 'solid',
        } as ComponentBorder;
        this.setRightRadiusFromInputBrandVariables(emailField, widget);
        this.setLeftRadiusFromInputBrandVariables(submitButton, widget);
        emailField.margin = {
          top: '0px',
          right: '0px',
          bottom: '0px',
          left: '0px',
        };
        submitButton.margin = {
          top: '0px',
          right: '0px',
          bottom: '0px',
          left: '0px',
        };
        submitButton.height = emailField.height = widget.brandVariables.input.height;
        if (widget.brandVariables.input?.margin) {
          component.margin = widget.brandVariables.input.margin;
        }
      }
      component.children = [emailField, submitButton];
    } else if (component.subType === WidgetComponentSubType.GroupTwoButtons) {
      const noButton = this.createBlankWidgetComponentFromSubtype(
        widget,
        WidgetComponentSubType.ButtonRegular,
        languageService,
        true,
        currentAccount,
        data,
        this.getComponentDescriptionBySubtype(WidgetComponentSubType.ButtonRegular),
        accountLanguageService,
      );
      noButton.width = '50%';
      noButton.textProperties.text = accountLanguageService ? accountLanguageService.translate('generic.no') : 'No';
      noButton.textProperties.color = '#0073ffff';
      noButton.margin = {
        top: '0px',
        bottom: '0px',
        left: '0px',
        right: '4px',
      } as FourSideStylingEntity;
      noButton.background.color = '#FFFFFFFF';
      noButton.border = {
        color: '#0073ffff',
        roundValue: '1px',
        style: 'solid',
      } as ComponentBorder;
      noButton.actionType = ButtonActionType.CloseWidget;
      const yesButton = this.createBlankWidgetComponentFromSubtype(
        widget,
        WidgetComponentSubType.ButtonRegular,
        languageService,
        true,
        currentAccount,
        data,
        this.getComponentDescriptionBySubtype(WidgetComponentSubType.ButtonRegular),
        accountLanguageService,
      );
      yesButton.width = '50%';
      yesButton.textProperties.text = accountLanguageService ? accountLanguageService.translate('generic.yes') : 'Yes';
      yesButton.margin = {
        top: '0px',
        bottom: '0px',
        left: '4px',
        right: '0px',
      } as FourSideStylingEntity;
      yesButton.actionType = undefined;
      component.children = [noButton, yesButton];
    } else {
      component.children = [];
      const numberOfPlaceholders = this.calculateNumberOfPlaceholders(component);

      for (let i = 0; i < numberOfPlaceholders; i++) {
        component.children.push({
          type: WidgetComponentType.Placeholder,
          subType: WidgetComponentSubType.PlaceholderRegular,
          width: Math.round(100 / numberOfPlaceholders) + '%',
          order: i,
        } as WidgetComponent);
      }
    }
    component.subType = WidgetComponentSubType.GroupRegular;
    return component;
  }

  private static calculateNumberOfPlaceholders(component: WidgetComponent): number {
    switch (component.subType) {
      case WidgetComponentSubType.GroupTwoColumns:
        return 2;
      case WidgetComponentSubType.GroupThreeColumns:
        return 3;
      case WidgetComponentSubType.GroupFourColumns:
        return 4;
      case WidgetComponentSubType.GroupFiveColumns:
        return 5;
      default:
        return 0;
    }
  }

  private static populateDeadlineComponentWithDefaultValues(
    component: DeadlineComponent,
    widget: Widget,
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ) {
    component.listItems = [
      {
        value: accountLanguageService ? accountLanguageService.translate('deadline.days') : 'Days',
      },
      {
        value: accountLanguageService ? accountLanguageService.translate('deadline.hours') : 'Hours',
      },
      {
        value: accountLanguageService ? accountLanguageService.translate('deadline.minutes') : 'Minutes',
      },
      {
        value: accountLanguageService ? accountLanguageService.translate('deadline.seconds') : 'Seconds',
      },
    ];
    component.actionType = undefined;
    component.actionValue = undefined;
    component.actionScript = undefined;
    component.textProperties = { text: undefined };
    component.textProperties.fontSize = '30px';
    component.textProperties.font = this.getCurrentDefaultFontName(currentAccount);
    component.textProperties.lineHeight = '30px';
    component.textProperties.color = '#000000FF';
    component.textProperties.alignment = TextAlignment.Center;
    const dateInAWeek = TimeUtils.getDateAddtoDate(7, 'days');
    component.deadlineProperties = {
      deadlineDate: dateInAWeek,
      format: DeadlineFormat.DaysHoursMinutesSeconds,
      deadlineOccurrence: DeadlineOccurrence.OnlyOnce,
      delimiterText: ':',
      delimiterColor: '#000000FF',
    } as DeadlineComponentProperties;
    component.labelProperties = {
      showLabels: true,
      labelPosition: LabelPosition.Below,
      textProperties: {
        color: '#000000FF',
        font: this.getCurrentDefaultFontName(currentAccount),
        fontSize: '12px',
        lineHeight: '12px',
      },
      margin: {
        roundValue: '4px',
      } as FourSideStylingEntity,
    } as LabelProperties;
    this.clearComponentForBrandVariables(widget, component);
    if (widget.brandVariables) {
      component.labelProperties.textProperties.color = undefined;
      component.labelProperties.textProperties.font = undefined;
      component.labelProperties.textProperties.lineHeight = undefined;
      component.labelProperties.textProperties.fontWeight = undefined;
      component.deadlineProperties.delimiterColor = undefined;
    }
    return component;
  }

  public static populateButtonListComponentWithDefaultValues(
    component: ButtonListComponent,
    widget: Widget,
    data: { name: string },
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ): ButtonListComponent {
    if (component.subType !== WidgetComponentSubType.ButtonListSubscriptionCheckbox) {
      component.listItems = [
        {
          value: accountLanguageService ? accountLanguageService.translate('buttonList.firstItem') : 'Option 1',
          key: accountLanguageService ? accountLanguageService.translate('buttonList.firstItem') : 'Option 1',
        },
        {
          value: accountLanguageService ? accountLanguageService.translate('buttonList.secondItem') : 'Option 2',
          key: accountLanguageService ? accountLanguageService.translate('buttonList.secondItem') : 'Option 2',
        },
      ];
      if (component.subType === WidgetComponentSubType.ButtonListChoices) {
        component.listItems = [];
        component.listItems = [
          {
            value: accountLanguageService ? accountLanguageService.translate('buttonList.option1') : 'Choice 1',
            key: accountLanguageService ? accountLanguageService.translate('buttonList.option1') : 'Choice 1',
          },
          {
            value: accountLanguageService ? accountLanguageService.translate('buttonList.option2') : 'Choice 2',
            key: accountLanguageService ? accountLanguageService.translate('buttonList.option2') : 'Choice 2',
          },
          {
            value: accountLanguageService ? accountLanguageService.translate('buttonList.option3') : 'Choice 3',
            key: accountLanguageService ? accountLanguageService.translate('buttonList.option3') : 'Choice 3',
          },
        ];
      }
    } else {
      component.listItems = [
        {
          value: accountLanguageService
            ? accountLanguageService.translate('buttonList.subscriptionItem1')
            : 'Newsletter',
          key: null,
        },
        {
          value: accountLanguageService ? accountLanguageService.translate('buttonList.subscriptionItem2') : 'Updates',
          key: null,
        },
      ];
    }
    component.textProperties = { text: undefined };
    component.textProperties.fontSize = '14px';
    component.textProperties.font = this.getCurrentDefaultFontName(currentAccount);
    component.textProperties.lineHeight = '14px';
    component.textProperties.color = '#000000FF';
    component.textProperties.alignment = TextAlignment.Left;
    component.background = {};
    component.padding = { roundValue: '8px' };
    component.alignment = { horizontal: ComponentAlignment.Left };
    component.direction = Direction.Column;
    component.buttonListProperties = {
      iconSize: 14,
      checkedIconUrl: '$icon/material/radio_button_checked',
      checkedFontColor: '#000000FF',
      uncheckedIconUrl: '$icon/material/radio_button_unchecked',
      uncheckedFontColor: '#000000FF',
      spaceBetween: 8,
      spaceInside: 0,
      allowMultipleSelection: false,
      format: ButtonListFormat.IconAndText,
      fillPerRow: 3,
      selectEffect: SelectEffect.blink,
    } as ButtonListProperties;
    component.name = data.name;
    component.required = true;
    if (component.subType === WidgetComponentSubType.ButtonListChoices) {
      component.buttonListProperties.itemWidth = '100%';
      component.buttonListProperties.itemHeight = '64px';
      component.buttonListProperties.spaceInside = 8;
      component.direction = Direction.Row;
      component.buttonListProperties.radius = {
        roundValue: '4px',
      } as ComponentRadius;
      component.buttonListProperties.uncheckedBorder = {
        roundValue: '1px',
        color: '#0e69b4ff',
      } as ComponentBorder;
      component.buttonListProperties.checkedBorder = {
        roundValue: '1px',
        color: '#0e69b4ff',
      } as ComponentBorder;
      component.padding = {
        top: '8px',
        right: '0px',
        bottom: '8px',
        left: '0px',
      };
    } else if (
      component.subType === WidgetComponentSubType.ButtonListCheckbox ||
      component.subType === WidgetComponentSubType.ButtonListSubscriptionCheckbox
    ) {
      component.buttonListProperties.allowMultipleSelection = true;
      component.buttonListProperties.checkedIconUrl = '$icon/material/select_check_box';
      component.buttonListProperties.uncheckedIconUrl = '$icon/material/check_box_outline_blank';
    }
    component.label = { text: 'Label' };
    component.label.fontSize = '13px';
    component.label.font = this.getCurrentDefaultFontName(currentAccount);
    component.label.lineHeight = '14px';
    component.label.color = '#909090';
    component.label.fontWeight = 600;
    component.label.alignment = TextAlignment.Left;
    component.label.margin = {
      top: '0px',
      right: '0px',
      bottom: '5px',
      left: '0px',
    } as FourSideStylingEntity;
    if (component.subType === WidgetComponentSubType.ButtonListSubscriptionCheckbox) {
      component.label.text = accountLanguageService
        ? accountLanguageService.translate('buttonList.chooseSubscriptionTopics')
        : 'Choose subscription topics';
    } else {
      component.label.text = accountLanguageService ? accountLanguageService.translate('common.label') : 'Label';
    }
    if (component.subType === WidgetComponentSubType.ButtonListStarRating) {
      this.populateListStarRatingComponentWithDefaultValues(component, languageService, accountLanguageService);
    }
    if (component.subType === WidgetComponentSubType.ButtonListSmileyRating) {
      this.populateListSmileyRatingComponentWithDefaultValues(component, languageService, accountLanguageService);
    }
    if (component.subType === WidgetComponentSubType.ButtonListNumericalRating) {
      this.populateListNumericalRatingComponentWithDefaultValues(
        component,
        widget,
        languageService,
        10,
        accountLanguageService,
      );
    }
    if (component.subType === WidgetComponentSubType.ButtonListCsatStarRating) {
      this.populateListStarRatingComponentWithDefaultValues(component, languageService, accountLanguageService);
    }
    if (component.subType === WidgetComponentSubType.ButtonListCsatSmileyRating) {
      this.populateListSmileyRatingComponentWithDefaultValues(component, languageService, accountLanguageService);
    }
    if (component.subType === WidgetComponentSubType.ButtonListCsatNumericalRating) {
      this.populateListNumericalRatingComponentWithDefaultValues(
        component,
        widget,
        languageService,
        5,
        accountLanguageService,
      );
    }
    if (component.subType === WidgetComponentSubType.ButtonListThumbRating) {
      this.populateListThumbRatingComponentWithDefaultValues(component, languageService, accountLanguageService);
    }
    if (component.subType === WidgetComponentSubType.ButtonListNpsRating) {
      this.populateListNpsRatingComponentWithDefaultValues(component, widget, languageService, accountLanguageService);
    }
    if (component.subType === WidgetComponentSubType.ButtonListSubscriptionCheckbox) {
      component.margin = {
        top: '0px',
        bottom: '16px',
        left: '0px',
        right: '0px',
      } as FourSideStylingEntity;
    }
    return component;
  }

  private static populateListStarRatingComponentWithDefaultValues(
    component,
    languageService,
    accountLanguageService?: AccountLanguageService,
  ) {
    component.listItems = [];
    component.listItems = [
      {
        value: accountLanguageService ? accountLanguageService.translate('rating.veryUnsatisfied') : 'Very Unsatisfied',
        key: 1,
      },
      {
        value: accountLanguageService ? accountLanguageService.translate('rating.unsatisfied') : 'Unsatisfied',
        key: 2,
      },
      {
        value: accountLanguageService ? accountLanguageService.translate('rating.neutral') : 'Neutral',
        key: 3,
      },
      {
        value: accountLanguageService ? accountLanguageService.translate('rating.satisfied') : 'Satisfied',
        key: 4,
      },
      {
        value: accountLanguageService ? accountLanguageService.translate('rating.loveIt') : 'Love it!',
        key: 5,
      },
    ];
    component.textProperties.fontSize = '10px';
    component.textProperties.lineHeight = '10px';
    component.direction = Direction.Row;
    component.padding = {
      top: '8px',
      right: '0px',
      bottom: '8px',
      left: '0px',
    };
    ((component.label.text = accountLanguageService ? accountLanguageService.translate('rating.rateLabel') : 'Rate us'),
      (component.buttonListProperties = {
        iconSize: 36,
        checkedIconUrl: 'https://widget-resources.triggerbee.com/library/presets/choices/rating-star-yellow.svg',
        uncheckedIconUrl: 'https://widget-resources.triggerbee.com/library/presets/choices/rating-star-black.svg',
        spaceBetween: 8,
        allowMultipleSelection: false,
        checkedFontColor: '#FCCF0AFF',
        uncheckedFontColor: '#D3D3D3FF',
        format: ButtonListFormat.IconAndText,
        labelType: null,
        selectEffect: SelectEffect.fancyBlink,
        mobile: {
          size: '36px',
          gap: component.buttonListProperties.spaceBetween + 'px',
        },
      } as ButtonListProperties));
    return component;
  }

  private static populateListSmileyRatingComponentWithDefaultValues(
    component,
    languageService,
    accountLanguageService?: AccountLanguageService,
  ) {
    component.listItems = [
      {
        value: accountLanguageService ? accountLanguageService.translate('rating.veryUnsatisfied') : 'Very Unsatisfied',
        key: 1,
        checkedIconUrl: 'https://widget-resources.triggerbee.com/library/presets/smiley/disappointed-face-new.svg',
        uncheckedIconUrl:
          'https://widget-resources.triggerbee.com/library/presets/smiley/smiley-gray-very-disappointed.png',
      },
      {
        value: accountLanguageService ? accountLanguageService.translate('rating.unsatisfied') : 'Unsatisfied',
        key: 2,
        checkedIconUrl: 'https://widget-resources.triggerbee.com/library/presets/smiley/sad-face-new.svg',
        uncheckedIconUrl: 'https://widget-resources.triggerbee.com/library/presets/smiley/smiley-gray-disappointed.png',
      },
      {
        value: accountLanguageService ? accountLanguageService.translate('rating.neutral') : 'Neutral',
        key: 3,
        checkedIconUrl: 'https://widget-resources.triggerbee.com/library/presets/smiley/neutral-face-new.svg',
        uncheckedIconUrl: 'https://widget-resources.triggerbee.com/library/presets/smiley/smiley-gray-neutral.png',
      },
      {
        value: accountLanguageService ? accountLanguageService.translate('rating.satisfied') : 'Satisfied',
        key: 4,
        checkedIconUrl: 'https://widget-resources.triggerbee.com/library/presets/smiley/satisfied-face-new.svg',
        uncheckedIconUrl: 'https://widget-resources.triggerbee.com/library/presets/smiley/smiley-gray-satisfied.png',
      },
      {
        value: accountLanguageService ? accountLanguageService.translate('rating.loveIt') : 'Love it!',
        key: 5,
        checkedIconUrl: 'https://widget-resources.triggerbee.com/library/presets/smiley/happy-face-new.svg',
        uncheckedIconUrl:
          'https://widget-resources.triggerbee.com/library/presets/smiley/smiley-rating-outline loveit.png',
      },
    ];
    component.textProperties.fontSize = '10px';
    component.textProperties.lineHeight = '10px';
    component.direction = Direction.Row;
    component.padding = {
      top: '8px',
      right: '0px',
      bottom: '8px',
      left: '0px',
    };
    ((component.label.text = accountLanguageService ? accountLanguageService.translate('rating.rateLabel') : 'Rate us'),
      (component.buttonListProperties = {
        iconSize: 50,
        spaceBetween: 8,
        allowMultipleSelection: false,
        format: ButtonListFormat.IconAndText,
        labelType: ConditionsLabel.showLeftAndRightOnly,
        selectEffect: SelectEffect.fancyBlink,
        mobile: {
          size: '50px',
          gap: '8px',
        },
      } as ButtonListProperties));
    return component;
  }

  private static populateListThumbRatingComponentWithDefaultValues(
    component,
    languageService,
    accountLanguageService?: AccountLanguageService,
  ) {
    component.listItems = [
      {
        value: accountLanguageService ? accountLanguageService.translate('rating.unsatisfied') : 'Unsatisfied',
        key: accountLanguageService ? accountLanguageService.translate('rating.unsatisfied') : 'Unsatisfied',
        checkedIconUrl: 'https://widget-resources.triggerbee.com/library/presets/thumbs/rating-thumbs-down2.svg',
        uncheckedIconUrl:
          'https://widget-resources.triggerbee.com/library/presets/thumbs/rating-thumbs-down-grayoutline.svg',
      },
      {
        value: accountLanguageService ? accountLanguageService.translate('rating.loveIt') : 'Love it!',
        key: accountLanguageService ? accountLanguageService.translate('rating.loveIt') : 'Love it!',
        checkedIconUrl: 'https://widget-resources.triggerbee.com/library/presets/thumbs/rating-thumbs-up2.svg',
        uncheckedIconUrl:
          'https://widget-resources.triggerbee.com/library/presets/thumbs/rating-thumbs-up-grayoutline.svg',
      },
    ];
    component.textProperties.fontSize = '10px';
    component.textProperties.lineHeight = '10px';
    component.direction = Direction.Row;
    component.padding = {
      top: '8px',
      right: '0px',
      bottom: '8px',
      left: '0px',
    };
    ((component.label.text = accountLanguageService ? accountLanguageService.translate('rating.rateLabel') : 'Rate us'),
      (component.buttonListProperties = {
        iconSize: 36,
        spaceBetween: 20,
        allowMultipleSelection: false,
        format: ButtonListFormat.IconAndText,
        labelType: null,
        selectEffect: SelectEffect.fancyBlink,
        mobile: {
          size: '36px',
          gap: '20px',
        },
      } as ButtonListProperties));
    return component;
  }

  private static populateListNumericalRatingComponentWithDefaultValues(
    component,
    widget,
    languageService,
    maxItems = 10,
    accountLanguageService?: AccountLanguageService,
  ) {
    const color = widget.brandVariables?.button.backgroundColor ?? '#636363';

    component.listItems = [];
    for (let index = 1; index <= maxItems; index++) {
      component.listItems.push({
        value: index.toString(),
        key: index.toString(),
      });
    }
    component.direction = Direction.Row;
    component.padding = {
      top: '8px',
      right: '0px',
      bottom: '8px',
      left: '0px',
    };
    ((component.label.text = accountLanguageService ? accountLanguageService.translate('rating.rateLabel') : 'Rate us'),
      (component.buttonListProperties = {
        checkedBackgroundColor: ColorHelpers.getTransparentColor(color, 0.5),
        checkedFontColor: '#ffffff',
        iconSize: 36,
        itemHeight: '36px',
        itemWidth: '36px',
        spaceBetween: 8,
        allowMultipleSelection: false,
        uncheckedBackgroundColor: ColorHelpers.getTransparentColor(color, 0.2),
        uncheckedFontColor: '#636363',
        labelTextMinimum: accountLanguageService ? accountLanguageService.translate('rating.minValueLabel') : 'Low',
        labelTextMaximum: accountLanguageService ? accountLanguageService.translate('rating.maxValueLabel') : 'High',
        selectEffect: SelectEffect.fancyBlink,
        radius: {
          roundValue: '3px',
        },
        mobile: {
          size: '36px',
          fontSize: component.textProperties.fontSize,
          lineHeight: component.textProperties.lineHeight,
        },
        uncheckedBorder: {
          color: '#636363',
          roundValue: '1px',
          style: 'solid',
        },
        checkedBorder: {
          color: '#d90f0f',
          roundValue: '0px',
          style: 'solid',
        },
        format: ButtonListFormat.TextOnly,
      } as ButtonListProperties));
    return component;
  }

  private static populateListNpsRatingComponentWithDefaultValues(
    component,
    widget,
    languageService,
    accountLanguageService?: AccountLanguageService,
  ) {
    const color = widget.brandVariables?.button.backgroundColor ?? '#636363';
    component.listItems = [];
    for (let index = 0; index < 11; index++) {
      component.listItems.push({
        value: index.toString(),
        key: index.toString(),
      });
    }
    component.direction = Direction.Row;
    component.padding = {
      top: '8px',
      right: '0px',
      bottom: '8px',
      left: '0px',
    };
    ((component.label.text = accountLanguageService
      ? accountLanguageService.translate('rating.npsLabel')
      : 'How likely are you to recommend us?'),
      (component.buttonListProperties = {
        checkedBackgroundColor: ColorHelpers.getTransparentColor(color, 0.2),
        checkedFontColor: '#ffffff',
        iconSize: 36,
        itemHeight: '36px',
        itemWidth: '36px',
        spaceBetween: 8,
        allowMultipleSelection: false,
        uncheckedBackgroundColor: ColorHelpers.getTransparentColor(color, 0.5),
        uncheckedFontColor: '#636363',
        labelTextMinimum: accountLanguageService
          ? accountLanguageService.translate('rating.minValueLabelNps')
          : 'Not likely',
        labelTextMaximum: accountLanguageService
          ? accountLanguageService.translate('rating.maxValueLabelNps')
          : 'Very likely',
        selectEffect: SelectEffect.fancyBlink,
        radius: {
          roundValue: '3px',
        },
        mobile: {
          size: '36px',
          fontSize: component.textProperties.fontSize,
          lineHeight: component.textProperties.lineHeight,
        },
        uncheckedBorder: {
          color: '#636363',
          roundValue: '1px',
          style: 'solid',
        },
        checkedBorder: {
          color: '#d90f0f',
          roundValue: '0px',
          style: 'solid',
        },
        format: ButtonListFormat.TextOnly,
      } as ButtonListProperties));
    return component;
  }

  private static populateBulletListComponentWithDefaultValues(
    component: WidgetComponent,
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ) {
    component.listItems = [
      {
        value: accountLanguageService ? accountLanguageService.translate('bulletList.firstItem') : 'Benefit 1',
      } as ComponentListItem,
      {
        value: accountLanguageService ? accountLanguageService.translate('bulletList.secondItem') : 'Benefit 2',
      } as ComponentListItem,
    ];
    component.textProperties = { text: undefined };
    component.textProperties.fontSize = '14px';
    component.textProperties.font = this.getCurrentDefaultFontName(currentAccount);
    component.textProperties.lineHeight = '18px';
    component.textProperties.color = '#000000FF';
    component.textProperties.alignment = TextAlignment.Left;
    component.background = {};
    component.padding = { roundValue: '8px' };
    component.iconSize = 14;
    component.iconUrl = '$icon/material/check';
    return component;
  }

  // Builds the Previous/Next/Last Button children from StepNavigationProperties. Previous/Next
  // always get a child (design === None only hides it). Last only gets one once lastButtonType
  // leaves Normal. currentAccount/accountLanguageService may be omitted (e.g. legacy migration).
  private static populateStepNavigationChildren(
    properties: StepNavigationProperties,
    widget: Widget,
    languageService: LanguageService,
    currentAccount?: Account,
    accountLanguageService?: AccountLanguageService,
  ): WidgetComponent[] {
    const children: WidgetComponent[] = [
      this.populateStepNavigationButtonChild(
        ButtonActionType.SwitchStatePrevious,
        WidgetComponentSubType.ButtonRegular,
        '$icon/material/arrow_back_ios',
        'left of text',
        widget,
        languageService,
        currentAccount,
        accountLanguageService,
      ),
      this.populateStepNavigationButtonChild(
        ButtonActionType.SwitchStateNext,
        WidgetComponentSubType.ButtonRegular,
        '$icon/material/arrow_forward_ios',
        'right of text',
        widget,
        languageService,
        currentAccount,
        accountLanguageService,
      ),
    ];

    if (properties.lastButtonType !== StepNavigationLastButtonType.Normal) {
      const isSubmit = properties.lastButtonType === StepNavigationLastButtonType.Submit;
      children.push(
        this.populateStepNavigationButtonChild(
          isSubmit ? ButtonActionType.SubmitForm : ButtonActionType.CloseWidget,
          isSubmit ? WidgetComponentSubType.ButtonSubmit : WidgetComponentSubType.ButtonRegular,
          '$icon/material/arrow_forward_ios',
          isSubmit ? 'right of text' : 'left of text',
          widget,
          languageService,
          currentAccount,
          accountLanguageService,
        ),
      );
    }

    children.push(this.populateStepNavigationCounterChild(widget));

    children.forEach((child, index) => (child.order = index));
    return children;
  }

  // NumbersSlashAll ("1/N") counter as a real, styleable Text child.
  private static populateStepNavigationCounterChild(widget: Widget): WidgetComponent {
    const bvText = widget.brandVariables?.text;
    return {
      type: WidgetComponentType.Text,
      subType: WidgetComponentSubType.TextRegular,
      // Set upfront - setChildProperties only backfills this once the parent is next selected.
      parentComponentType: WidgetComponentType.StepNavigation,
      textProperties: {
        text: '<p><span>1/1</span></p>',
        alignment: TextAlignment.Center,
        color: bvText?.color,
        font: bvText?.font,
        fontSize: bvText?.fontSize,
        lineHeight: bvText?.lineHeight,
        fontWeight: bvText?.fontWeight,
        letterSpacing: bvText?.letterSpacing,
      },
    } as WidgetComponent;
  }

  // Creates a brand-new Previous/Next/Last child with default icon/text; after that it's edited
  // like any other Button and never touched again by StepNavigation sync.
  private static populateStepNavigationButtonChild(
    actionType: ButtonActionType,
    subType: WidgetComponentSubType,
    iconUrl: string,
    iconPosition: string,
    widget: Widget,
    languageService: LanguageService,
    currentAccount?: Account,
    accountLanguageService?: AccountLanguageService,
  ): ButtonComponent {
    let buttonComponent = {
      type: WidgetComponentType.Button,
      subType,
    } as ButtonComponent;
    buttonComponent = this.populateButtonComponentWithDefaultValues(
      buttonComponent,
      true,
      languageService,
      currentAccount as Account,
      accountLanguageService,
    );
    buttonComponent.actionType = actionType;
    buttonComponent.iconUrl = iconUrl;
    buttonComponent.iconPosition = iconPosition;
    buttonComponent.textProperties!.text = `<p><span>${this.getStepNavigationChildDefaultText(actionType, accountLanguageService)}</span></p>`;
    this.clearComponentForBrandVariables(widget, buttonComponent);
    return buttonComponent;
  }

  // Matches the fallback text the legacy flat stepNavigationProperties used for each slot
  // (see populateStepNavigationComponentWithDefaultValues / updateLastButtonType).
  private static getStepNavigationChildDefaultText(
    actionType: ButtonActionType,
    accountLanguageService?: AccountLanguageService,
  ): string {
    switch (actionType) {
      case ButtonActionType.SwitchStatePrevious:
        return accountLanguageService
          ? accountLanguageService.translate('stepNavigation.previousButtonText')
          : 'Previous';
      case ButtonActionType.SwitchStateNext:
        return accountLanguageService ? accountLanguageService.translate('stepNavigation.nextButtonText') : 'Next';
      case ButtonActionType.SubmitForm:
        return accountLanguageService ? accountLanguageService.translate('stepNavigation.submitButtonText') : 'Submit';
      case ButtonActionType.CloseWidget:
        return accountLanguageService ? accountLanguageService.translate('stepNavigation.lastButtonText') : 'Close';
      default:
        return '';
    }
  }

  // Syncs the Last child's actionType/subType to lastButtonType. Never removed once it exists -
  // updated in place (preserving id) so toggling lastButtonType never recreates it.
  public static syncStepNavigationLastButtonChild(
    component: WidgetComponent,
    widget: Widget,
    languageService: LanguageService,
    currentAccount?: Account,
    accountLanguageService?: AccountLanguageService,
  ): WidgetComponent | undefined {
    const properties = component.stepNavigationProperties;
    if (!properties) {
      return undefined;
    }

    const children = component.children ?? [];
    const isSubmit = properties.lastButtonType === StepNavigationLastButtonType.Submit;
    const isNormal = properties.lastButtonType === StepNavigationLastButtonType.Normal;
    const lastActionType = isSubmit ? ButtonActionType.SubmitForm : ButtonActionType.CloseWidget;
    const lastSubType = isSubmit ? WidgetComponentSubType.ButtonSubmit : WidgetComponentSubType.ButtonRegular;

    // Found by exclusion, not actionType - actionType is undefined while Normal. Must also
    // exclude the counter Text child, whose actionType is undefined too.
    const lastChild = children.find(
      (child) =>
        child.type === WidgetComponentType.Button &&
        child.actionType !== ButtonActionType.SwitchStatePrevious &&
        child.actionType !== ButtonActionType.SwitchStateNext,
    );

    if (lastChild) {
      // Never touches icon/text - those belong to the child exactly like on any other Button (see
      // populateStepNavigationButtonChild).
      lastChild.actionType = isNormal ? undefined : lastActionType;
      lastChild.subType = lastSubType;
      return lastChild;
    }

    if (isNormal) {
      return undefined;
    }

    const newLastChild = this.populateStepNavigationButtonChild(
      lastActionType,
      lastSubType,
      '$icon/material/arrow_forward_ios',
      isSubmit ? 'right of text' : 'left of text',
      widget,
      languageService,
      currentAccount,
      accountLanguageService,
    );
    newLastChild.order = children.length;
    component.children = [...children, newLastChild];
    return newLastChild;
  }

  private static populateStepNavigationComponentWithDefaultValues(
    component: WidgetComponent,
    widget: Widget,
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ) {
    component.height = '40px';
    component.actionType = ButtonActionType.SubmitForm;

    component.stepNavigationProperties = {
      previousButtonDesign: StepNavigationButtonDesign.IconAndText,
      previousButtonText: accountLanguageService
        ? accountLanguageService.translate('stepNavigation.previousButtonText')
        : 'Previous',
      nextButtonDesign: StepNavigationButtonDesign.IconAndText,
      nextButtonText: accountLanguageService
        ? accountLanguageService.translate('stepNavigation.nextButtonText')
        : 'Next',
      lastButtonType: StepNavigationLastButtonType.Submit,
      lastButtonText: accountLanguageService
        ? accountLanguageService.translate('stepNavigation.submitButtonText')
        : 'Submit',
      progressBar: StepNavigationProgressBar.NumbersSlashAll,
    };

    component.children = this.populateStepNavigationChildren(
      component.stepNavigationProperties,
      widget,
      languageService,
      currentAccount,
      accountLanguageService,
    );

    return component;
  }

  private static populateSpinTheWheelComponentWithDefaultValues(
    component: WidgetComponent,
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ) {
    component.width = '400px';
    component.height = '400px';
    component.alignment = {
      horizontal: ComponentAlignment.Center,
      vertical: ComponentAlignment.Center,
    };
    component.textProperties = { text: undefined };
    component.spinTheWheelProperties = {
      wheelSize: 400,
      wheelAutoStartSpin: false,
      wheelSpinDuration: 4,
      wheelBorderColor: '#ffcd2a',
      wheelBorderWidth: 6,
      wheelShadowColor: '#00000036',
      wheelShadowWidth: 10,
      wheelInnerShadowWidth: 0,
      wheelInnerShadowColor: '#00000036',
      sliceLabelRotation: 'horizontal',
      sliceFont: 'Montserrat',
      sliceFontSize: 24,
      wheelNailImage: 'https://widget-resources.triggerbee.com/resources/images/spinthewheel/black-pin.svg',
      wheelNailImageWidth: 25,
      spinButtonLabel: accountLanguageService
        ? accountLanguageService.translate('spinTheWheel.spinButtonLabel')
        : 'SPIN',
      spinButtonFont: 'Montserrat',
      spinButtonFontSize: 14,
      spinButtonTextColor: '#ffffff',
      spinButtonBackgroundColor: '#222831ff',
      spinButtonImage: '',
      spinButtonHideOnSpin: false,
      mobile: {
        wheelNailImageWidth: 10,
        spinButtonFontSize: 10,
        sliceFontSize: 18,
        prizeFontSize: 18,
      },
      sliceItems: [
        {
          label: '15%',
          backgroundColor: '#c0daffff',
          image: '',
          weight: 1,
          isWinner: true,
          resultCouponCode: 'WELCOME15',
          resultCouponSetId: null,
          resultCouponSetName: null,
          resultCouponCodeFallback: null,
          resultStateId: null,
        },
        {
          label: '10%',
          backgroundColor: '#8fbff7ff',
          image: '',
          weight: 1,
          isWinner: true,
          resultCouponCode: 'WELCOME10',
          resultCouponSetId: null,
          resultCouponSetName: null,
          resultCouponCodeFallback: null,
          resultStateId: null,
        },
        {
          label: '5%',
          backgroundColor: '#59a4efff',
          image: '',
          weight: 1,
          isWinner: true,
          resultCouponCode: 'WELCOME5',
          resultCouponSetId: null,
          resultCouponSetName: null,
          resultCouponCodeFallback: null,
          resultStateId: null,
        },
        {
          label: '10%',
          backgroundColor: '#c0daffff',
          image: '',
          weight: 1,
          isWinner: true,
          resultCouponCode: 'WELCOME10',
          resultCouponSetId: null,
          resultCouponSetName: null,
          resultCouponCodeFallback: null,
          resultStateId: null,
        },
        {
          label: '0%',
          backgroundColor: '#8fbff7ff',
          image: '',
          weight: 1,
          isWinner: false,
          resultCouponCode: null,
          resultCouponSetId: null,
          resultCouponSetName: null,
          resultCouponCodeFallback: null,
          resultStateId: null,
        },
        {
          label: '10%',
          backgroundColor: '#59a4efff',
          image: '',
          weight: 1,
          isWinner: true,
          resultCouponCode: 'WELCOME10',
          resultCouponSetId: null,
          resultCouponSetName: null,
          resultCouponCodeFallback: null,
          resultStateId: null,
        },
      ],
      prizeFont: this.getCurrentDefaultFontName(currentAccount),
      prizeFontSize: 26,
      prizeTextColor: '#000000ff',
      prizeBackgroundColor: '#ffffffcc',
      prizeButtonTextColor: '#222831ff',
      prizeButtonBackgroundColor: '#dca737ff',
      prizeWonText: accountLanguageService
        ? accountLanguageService.translate('spinTheWheel.prizeWonText')
        : 'Congratulations!',
      prizeWonButtonText: accountLanguageService
        ? accountLanguageService.translate('spinTheWheel.prizeWonButtonText')
        : 'Claim your prize',
      prizeCopyMessage: accountLanguageService
        ? accountLanguageService.translate('discountCode.copyMessage')
        : 'Copied to clipboard!',
      prizeLostText: accountLanguageService
        ? accountLanguageService.translate('spinTheWheel.prizeLostText')
        : 'Ahhh, too bad! :-(',
      prizeLostButtonText: accountLanguageService
        ? accountLanguageService.translate('spinTheWheel.prizeLostButtonText')
        : 'Click to close',
      prizeAnimateConfetti: true,
    } as SpinTheWheelProperties;

    return component;
  }

  private static populateMemoryComponentWithDefaultValues(
    component: WidgetComponent,
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ) {
    component.width = '500px';
    component.height = '500px';
    component.alignment = {
      horizontal: ComponentAlignment.Center,
      vertical: ComponentAlignment.Center,
    };
    component.shadow = {
      horizontalLength: '0px',
      verticalLength: '3px',
      blur: '10px',
      color: '#E6E6E6',
      spread: '0px',
    };
    component.border = {
      color: '#abababff',
      style: 'solid',
      roundValue: '0px',
    } as ComponentBorder;
    component.background = { color: '#ffffff' };
    component.radius = { roundValue: '7px' };
    component.textProperties = { text: undefined };
    component.memoryProperties = {
      cardBackside: 'https://widget-resources.triggerbee.com/resources/images/memory/que_icon.png ',
      cardCenterCard: { image: '' },
      resultFont: this.getCurrentDefaultFontName(currentAccount),
      resultFontSize: 14,
      resultTextColor: '#000000',
      resultBackgroundColor: '#ffffff',
      resultButtonTextColor: '#ffffff',
      resultButtonBackgroundColor: '#c60000',
      resultWonText: accountLanguageService
        ? accountLanguageService.translate('memory.resultWonText')
        : 'Good job! Your time was {elapsedTime}',
      resultWonButtonText: accountLanguageService
        ? accountLanguageService.translate('memory.resultWonButtonText')
        : 'Claim your prize',
      resultCopyMessage: accountLanguageService
        ? accountLanguageService.translate('discountCode.copyMessage')
        : 'Copied to clipboard!',
      resultAnimateConfetti: true,
      resultCouponCode: 'WELCOME',
      resultStateId: null,
      cardImageWidth: 43,
      cardRowGap: 10,
      cardColumnGap: 10,
      mobile: {
        resultFontSize: 10,
        cardRowGap: 6,
        cardColumnGap: 6,
      },
      cards: [
        {
          image: 'https://widget-resources.triggerbee.com/resources/images/memory/img-1.png',
        },
        {
          image: 'https://widget-resources.triggerbee.com/resources/images/memory/img-2.png',
        },
        {
          image: 'https://widget-resources.triggerbee.com/resources/images/memory/img-3.png',
        },
        {
          image: 'https://widget-resources.triggerbee.com/resources/images/memory/img-4.png',
        },
      ],
    };
    return component;
  }

  private static populateConsentComponentWithDefaultValues(
    component: ConsentCheckboxComponent,
    isChild: boolean,
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ) {
    component.textProperties = {
      text: accountLanguageService
        ? accountLanguageService.translate('consent.defaultLabelText')
        : 'By submitting to this form you agree to our ',
    };
    component.textProperties.fontSize = '12px';
    component.textProperties.font = this.getCurrentDefaultFontName(currentAccount);
    component.textProperties.lineHeight = '12px';
    component.textProperties.color = '#000000';
    component.textProperties.alignment = TextAlignment.Left;
    component.linkText = accountLanguageService
      ? accountLanguageService.translate('consent.defaultLabelLink')
      : 'privacy policy';
    component.hideCheckbox = false;
    component.innerColor = '#00BFFFFF';
    if (!isChild) {
      component.margin = {
        top: '0px',
        bottom: '8px',
        left: '0px',
        right: '0px',
      } as FourSideStylingEntity;
    }
    return component;
  }

  private static populateTextComponentWithDefaultValues(
    component: WidgetComponent,
    isChild: boolean,
    languageService: LanguageService,
    currentAccount: Account,
    isHeader = false,
    accountLanguageService?: AccountLanguageService,
  ) {
    const defaultText = isHeader
      ? accountLanguageService
        ? accountLanguageService.translate('text.defaultHeadlineText')
        : 'Welcome!'
      : accountLanguageService
        ? accountLanguageService.translate('text.defaultText')
        : 'Add your text here';

    const text = isHeader ? `<h1>${defaultText}</h1>` : `<p><span>${defaultText}</span></p>`;

    component.textProperties = {
      text: text,
    };
    component.background = { color: undefined };
    component.textProperties.fontSize = '14px';
    component.textProperties.font = this.getCurrentDefaultFontName(currentAccount);
    component.textProperties.lineHeight = '14px';
    component.textProperties.fontWeight = 400;
    component.textProperties.color = '#000000';
    component.textProperties.alignment = TextAlignment.Center;
    if (!isChild) {
      component.margin = {
        top: '0px',
        bottom: '8px',
        left: '0px',
        right: '0px',
      } as FourSideStylingEntity;
    }
    if (component.subType === WidgetComponentSubType.TextFloating) {
      component.positionCorner = PositionCorner.TopLeft;
      component.positionX = 0;
      component.positionY = -50;
      component.padding = { roundValue: '10px' };
      component.width = '100px';
      component.height = '100px';
      component.radius = { roundValue: '100px' };
      component.background.size = BackgroundImageSize.Contain;
      ((component.background.imageUrl =
        'https://widget-resources.triggerbee.com/library/presets/stickers/sticker-star-red.svg'),
        (component.margin = {
          top: '0px',
          bottom: '0px',
          left: '0px',
          right: '0px',
        } as FourSideStylingEntity));
      component.textProperties = {
        fontWeight: 700,
        text:
          '<p>' +
          (accountLanguageService ? accountLanguageService.translate('text.floatingDefaultText') : 'NEW!') +
          '</p>',
        alignment: TextAlignment.Center,
        font: '"Oswald"',
        fontSize: '16px',
        color: '#ffffff',
      };
      component.wcagProperties = {
        role: accountLanguageService ? accountLanguageService.translate('wcag.banner') : 'Promotional banner',
      };
    }
    return component;
  }

  private static populateHideButtonComponentWithDefaultValues(
    component: ButtonComponent,
    accountLanguageService?: AccountLanguageService,
  ) {
    component.textProperties = {
      text: '<p><span></span></p>',
    } as TextProperties;
    component.iconUrl = '$icon/material/close';
    component.background = { color: undefined };
    component.width = '20px';
    component.height = '20px';

    const defaultPositionValue = 5;
    component.positionX = defaultPositionValue;
    component.positionY = defaultPositionValue;
    component.positionCorner = PositionCorner.TopRight;
    (component as ButtonComponent).actionType = ButtonActionType.CloseWidget;

    component.wcagProperties = {
      ariaLabel: accountLanguageService ? accountLanguageService.translate('wcag.closeButton') : 'Close',
    };

    return component;
  }

  public static populateButtonComponentWithDefaultValues(
    component: ButtonComponent,
    isChild: boolean,
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ) {
    component.textProperties = { text: '' };
    component.textProperties.fontSize = '14px';
    component.textProperties.font = this.getCurrentDefaultFontName(currentAccount);
    component.textProperties.lineHeight = '14px';
    component.textProperties.color = '#ffffff';
    component.textProperties.alignment = TextAlignment.Center;
    component.textProperties.fontWeight = 700;
    component.textProperties.text = '<p><span>';

    if (component.subType === WidgetComponentSubType.ButtonSubmit) {
      component.textProperties.color = '#FFFFFF';
      component.textProperties.text += accountLanguageService
        ? accountLanguageService.translate('button.submitDefaultText')
        : 'Submit';
      // No aria-label here: the visible text names this button, and a fixed label would override it
      // as soon as an author edits that text.
    } else if (component.subType === WidgetComponentSubType.ButtonInstagramFollow) {
      component.textProperties.color = '#e74c3c';
      component.textProperties.text += accountLanguageService
        ? accountLanguageService.translate('button.instagramDefaultText')
        : 'Follow us';
    } else {
      component.textProperties.color = '#FFFFFF';
      component.textProperties.text += accountLanguageService
        ? accountLanguageService.translate('button.defaultText')
        : 'Click here';
    }
    component.textProperties.text += '</span></p>';
    (component as ButtonComponent).actionType = ButtonActionType.CloseWidget;

    if (component.subType === WidgetComponentSubType.ButtonInstagramFollow) {
      (component as ButtonComponent).actionType = ButtonActionType.OpenUrlNewTabAndSwitchState;
    } else {
      component.background = { color: '#0073ffff' };
      component.radius = { roundValue: '4px' };
      if (component.subType === WidgetComponentSubType.ButtonSubmit) {
        (component as ButtonComponent).actionType = ButtonActionType.SubmitForm;
      }
    }
    if (component.subType === WidgetComponentSubType.ButtonHide) {
      return this.populateHideButtonComponentWithDefaultValues(component, accountLanguageService);
    }

    component.iconSize = 24;
    component.iconPosition = 'left of text';
    component.height = '32px';
    component.padding = { roundValue: '10px' };
    if (!isChild) {
      component.margin = {
        top: '0px',
        bottom: '8px',
        left: '0px',
        right: '0px',
      } as FourSideStylingEntity;
    }
    return component;
  }

  public static populateShareComponentWithDefaultValues(
    component: DiscountCodeComponent,
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ) {
    component = this.populateTextComponentWithDefaultValues(
      component,
      true,
      languageService,
      currentAccount,
      false,
      accountLanguageService,
    );
    component.order = 0;
    component.radius = { roundValue: '4px' } as ComponentRadius;
    component.border = {
      color: '#575757ff',
      style: 'solid',
      roundValue: '2px',
    } as ComponentBorder;
    component.padding = { roundValue: '8px' };
    component.textProperties.fontWeight = 700;
    component.textProperties.font = this.getCurrentDefaultFontName(currentAccount);
    component.textProperties.alignment = TextAlignment.Left;
    component.textProperties.color = '#575757ff';
    component.textProperties.fontSize = '22px';
    component.hover = {};
    component.hover.border = {
      color: '#FFC0CB',
      style: 'solid',
      roundValue: '4px',
    } as ComponentBorder;
    component.hover.backgroundColor = '#F89880';
    component.textProperties.lineHeight = '22px';
    component.textProperties.text =
      '<p><span>' +
      (accountLanguageService ? accountLanguageService.translate('discountCode.fallback') : 'WELCOME10') +
      '</span></p>';
    component.margin = {
      top: '0px',
      bottom: '16px',
      left: '0px',
      right: '0px',
    } as FourSideStylingEntity;
    component.couponCodeProperties = {
      copyMessage: accountLanguageService
        ? accountLanguageService.translate('discountCode.copyMessage')
        : 'Copied to clipboard!',
      fallback: accountLanguageService ? accountLanguageService.translate('discountCode.fallback') : 'WELCOME10',
    };
    return component;
  }

  private static populateCodeEditorComponentWithDefaultValues(
    component: CodeEditorComponent,
    languageService: LanguageService,
  ) {
    component.order = 0;
    component.radius = undefined;
    component.border = undefined;
    component.padding = undefined;
    component.margin = undefined;
    component.codeEditorProperties = {
      html: '<p>Code Element</p>',
    };
    return component;
  }

  private static populateDiscountCodeComponentWithDefaultValues(
    component: DiscountCodeComponent,
    widget: Widget,
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ) {
    let textComponent = {
      type: WidgetComponentType.Text,
      subType: WidgetComponentSubType.TextRegular,
    } as WidgetComponent;
    textComponent = this.populateTextComponentWithDefaultValues(
      textComponent,
      true,
      languageService,
      currentAccount,
      false,
      accountLanguageService,
    );
    textComponent.order = 0;
    textComponent.width = '65%';
    textComponent.height = '100%';
    textComponent.radius = {
      topLeft: '4px',
      topRight: '0px',
      bottomRight: '0px',
      bottomLeft: '4px',
    };
    textComponent.padding = { roundValue: '10px' };
    textComponent.textProperties.fontWeight = 700;
    textComponent.textProperties.text =
      '<p><span>' +
      (accountLanguageService ? accountLanguageService.translate('discountCode.textDefault') : 'Your discount code') +
      '</span></p>';
    textComponent.textProperties.font = this.getCurrentDefaultFontName(currentAccount);
    textComponent.textProperties.color = '#696969ff';
    textComponent.margin = {
      top: '0px',
      bottom: '0px',
      left: '0px',
      right: '0px',
    } as FourSideStylingEntity;
    this.clearComponentForBrandVariables(widget, textComponent);

    let buttonComponent = {
      type: WidgetComponentType.Button,
      subType: WidgetComponentSubType.ButtonRegular,
    } as ButtonComponent;
    buttonComponent = this.populateButtonComponentWithDefaultValues(
      buttonComponent,
      true,
      languageService,
      currentAccount,
      accountLanguageService,
    );
    buttonComponent.order = 1;
    buttonComponent.width = '35%';
    buttonComponent.height = '100%';
    buttonComponent.margin = {
      top: '0px',
      bottom: '0px',
      left: '0px',
      right: '0px',
    } as FourSideStylingEntity;
    buttonComponent.padding = { roundValue: '10px' };
    buttonComponent.background.color = '#2189f9ff';
    buttonComponent.actionType = ButtonActionType.CopyToClipboard;
    buttonComponent.textProperties.fontWeight = 700;
    buttonComponent.textProperties.text =
      '<p><span>' +
      (accountLanguageService ? accountLanguageService.translate('discountCode.buttonDefault') : 'Copy') +
      '</span></p>';
    buttonComponent.textProperties.font = this.getCurrentDefaultFontName(currentAccount);

    this.clearComponentForBrandVariables(widget, buttonComponent);
    buttonComponent.radius = {
      topLeft: '0px',
      topRight: '4px',
      bottomRight: '4px',
      bottomLeft: '0px',
    };
    if (widget.brandVariables) {
      WidgetComponentHelpers.setRadiusFromBrandVariables(buttonComponent, widget);
      buttonComponent.margin = {
        top: '0px',
        right: '0px',
        bottom: '0px',
        left: '0px',
      };
      buttonComponent.height = textComponent.height = widget.brandVariables.button.height;
    }

    const borderWidth =
      WidgetComponentHelpers.extractNumberFromStringWithUnit(buttonComponent.border?.roundValue) ||
      WidgetComponentHelpers.extractNumberFromStringWithUnit(widget.brandVariables?.button?.border?.width) ||
      WidgetComponentHelpers.extractNumberFromStringWithUnit(widget.brandVariables?.input?.border?.width);

    textComponent.border = {
      color:
        (WidgetComponentHelpers.extractNumberFromStringWithUnit(buttonComponent.border?.roundValue) &&
          buttonComponent.border?.color) ||
        (WidgetComponentHelpers.extractNumberFromStringWithUnit(widget.brandVariables?.button?.border?.width) &&
          widget.brandVariables?.button?.border?.color) ||
        buttonComponent.background?.color ||
        widget.brandVariables?.button?.backgroundColor,
      style: 'solid',
      top: (borderWidth || '1') + 'px',
      left: (borderWidth || '1') + 'px',
      bottom: (borderWidth || '1') + 'px',
      right: '0px',
    } as ComponentBorder;

    // TODO: Add appearance settings once it's available on back-end
    component.children = [textComponent, buttonComponent];
    component.margin = {
      top: '0px',
      bottom: '8px',
      left: '0px',
      right: '0px',
    } as FourSideStylingEntity;
    return component;
  }

  private static populateShareCampaignComponentWithDefaultValues(
    component: ComponentGroup,
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ) {
    let buttonComponent = {
      type: WidgetComponentType.Button,
      subType: WidgetComponentSubType.ButtonShare,
    } as ButtonComponent;
    buttonComponent = this.populateReferFriendMobileComponentWithDefaultValues(
      buttonComponent,
      languageService,
      currentAccount,
      accountLanguageService,
    );

    let textComponent = {
      type: WidgetComponentType.Text,
      subType: WidgetComponentSubType.TextCopy,
    } as WidgetComponent;
    textComponent = this.populateTextComponentWithDefaultValues(
      textComponent,
      true,
      languageService,
      currentAccount,
      false,
      accountLanguageService,
    );
    textComponent.textProperties.text = undefined;
    textComponent.textProperties.alignment = TextAlignment.Left;
    textComponent.border = {
      color: '#000000',
      style: 'solid',
      roundValue: '1px',
    };
    textComponent.padding = {
      top: '5px',
      bottom: '5px',
      left: '5px',
      right: '5px',
    } as FourSideStylingEntity;
    textComponent.radius = { roundValue: '5px' };
    textComponent.copyTextProperties = {
      copyMessage: accountLanguageService
        ? accountLanguageService.translate('discountCode.copyMessage')
        : 'Copied to clipboard!',
    };

    component.children = [buttonComponent, textComponent];
    return component;
  }

  public static populateInternationalPhoneComponentWithDefaultValues(
    widget: Widget,
    component: WidgetComponent,
    data: { name: string },
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ) {
    let countrySelectComponent = {
      type: WidgetComponentType.Dropdown,
      subType: WidgetComponentSubType.DropdownCountry,
    } as WidgetComponent;
    countrySelectComponent = this.populateDropdownComponentWithDefaultValues(
      countrySelectComponent,
      data,
      languageService,
      currentAccount,
      accountLanguageService,
    );
    countrySelectComponent.countrySelectProperties.include = [CountrySelectFilter.All];
    countrySelectComponent.countrySelectProperties.subLabels = [CountryLabel.Native, CountryLabel.DialCode];
    countrySelectComponent.padding = {
      top: '0px',
      bottom: '0px',
      left: '10px',
      right: '5px',
    } as FourSideStylingEntity;
    countrySelectComponent.iconUrl = 'https://widget-resources.triggerbee.com/resources/images/small-triangle-down.png';
    countrySelectComponent.background = null;
    countrySelectComponent.border = null;
    countrySelectComponent.label = null;
    countrySelectComponent.width = '65px';
    countrySelectComponent.margin = {
      roundValue: '0px',
    } as FourSideStylingEntity;
    countrySelectComponent.radius = null;
    countrySelectComponent.textProperties.color = '#393e46ff';
    countrySelectComponent.name = this.generateWidgetComponentTextFieldName(
      widget,
      WidgetComponentSubType.DropdownCountry,
    );
    component.label = { text: 'Label' };
    component.label.fontSize = '13px';
    component.label.font = this.getCurrentDefaultFontName(currentAccount);
    component.label.lineHeight = '14px';
    component.label.color = '#909090';
    component.label.fontWeight = 600;
    component.label.alignment = TextAlignment.Left;
    component.label.margin = {
      top: '0px',
      right: '0px',
      bottom: '5px',
      left: '0px',
    } as FourSideStylingEntity;
    component.label.text = accountLanguageService ? accountLanguageService.translate('input.phoneLabel') : 'Phone';
    let inputPhoneComponent = {
      type: WidgetComponentType.InputText,
      subType: WidgetComponentSubType.InputTextPhone,
    } as WidgetComponent;
    inputPhoneComponent = this.populateInputTextComponentWithDefaultValues(
      inputPhoneComponent,
      data,
      languageService,
      currentAccount,
      accountLanguageService,
    );
    inputPhoneComponent.label = null;
    inputPhoneComponent.border = null;
    inputPhoneComponent.padding = {
      top: '0px',
      bottom: '0px',
      left: '5px',
      right: '10px',
    } as FourSideStylingEntity;
    inputPhoneComponent.iconUrl = 'phone.svg';
    inputPhoneComponent.background = null;
    // inputPhoneComponent.width = '100%';
    inputPhoneComponent.margin = { roundValue: '0px' } as FourSideStylingEntity;
    inputPhoneComponent.textProperties.text = '+46';
    inputPhoneComponent.textProperties.color = '#393e46ff';
    inputPhoneComponent.textProperties.font = this.getCurrentDefaultFontName(currentAccount);
    inputPhoneComponent.radius = null;
    inputPhoneComponent.iconPosition = 'right';
    inputPhoneComponent.iconSize = 16;
    component.height = '36px';
    component.width = '300px';
    component.background = { color: '#ffffff' };
    component.border = {
      color: '#ccccccff',
      style: 'solid',
      roundValue: '1px',
    };
    component.radius = { roundValue: '4px' };
    component.children = [countrySelectComponent, inputPhoneComponent];
    return component;
  }

  public static populateInputTextComponentWithDefaultValues(
    component: WidgetComponent,
    data: { name: string },
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ) {
    component.textProperties = { text: '' };
    component.textProperties.text = ' ';
    component.textProperties.fontSize = '14px';
    component.textProperties.font = this.getCurrentDefaultFontName(currentAccount);
    component.textProperties.lineHeight = '14px';
    component.textProperties.color = '#000000';
    component.textProperties.alignment = TextAlignment.Left;
    component.padding = { roundValue: '10px' } as FourSideStylingEntity;
    component.iconSize = 24;
    component.iconPosition = 'left';
    component.label = { text: 'Label' };
    component.label.fontSize = '13px';
    component.label.font = this.getCurrentDefaultFontName(currentAccount);
    component.label.lineHeight = '14px';
    component.label.color = '#909090';
    component.label.fontWeight = 600;
    component.label.alignment = TextAlignment.Left;
    component.label.margin = {
      top: '0px',
      right: '0px',
      bottom: '5px',
      left: '0px',
    } as FourSideStylingEntity;

    switch (component.subType) {
      case WidgetComponentSubType.InputTextEmail:
        // component.textProperties.text = languageService.translateInstantly('campaign.widget.component.input-text-email.placeholder');
        component.label.text = accountLanguageService ? accountLanguageService.translate('input.emailLabel') : 'Email';
        component.iconUrl = '$icon/material/mail';
        component.padding = {
          top: '5px',
          bottom: '5px',
          left: '10px',
          right: '10px',
        } as FourSideStylingEntity;
        component.required = true;
        component.wcagProperties = {
          ariaLabel: accountLanguageService
            ? accountLanguageService.translate('input.emailPlaceholder')
            : 'Enter your email',
        };
        break;
      case WidgetComponentSubType.InputTextPassword:
        component.label.text = accountLanguageService
          ? accountLanguageService.translate('input.passwordLabel')
          : 'Password';
        component.iconUrl = '$icon/material/lock';
        component.padding = {
          top: '5px',
          bottom: '5px',
          left: '10px',
          right: '10px',
        } as FourSideStylingEntity;
        component.required = true;
        component.passwordProperties = {
          minimumLength: this.passwordMinLength,
          maximumLength: this.passwordMaxLength,
          toggleVisibility: true,
        };
        component.wcagProperties = {
          ariaLabel: accountLanguageService
            ? accountLanguageService.translate('input.passwordPlaceholder')
            : 'Enter password',
        };
        break;
      case WidgetComponentSubType.InputTextName:
        // component.textProperties.text = languageService.translateInstantly('campaign.widget.component.input-text-name.placeholder');
        component.label.text = accountLanguageService ? accountLanguageService.translate('input.nameLabel') : 'Name';
        component.iconUrl = '$icon/material/person';
        component.padding = {
          top: '5px',
          bottom: '5px',
          left: '10px',
          right: '10px',
        } as FourSideStylingEntity;
        component.wcagProperties = {
          ariaLabel: accountLanguageService
            ? accountLanguageService.translate('input.namePlaceholder')
            : 'Enter your name',
        };
        break;
      case WidgetComponentSubType.InputTextFirstName:
        // component.textProperties.text = languageService.translateInstantly('campaign.widget.component.input-text-name.first-name-placeholder');
        component.label.text = accountLanguageService
          ? accountLanguageService.translate('input.firstNameLabel')
          : 'First Name';
        component.padding = {
          top: '5px',
          bottom: '5px',
          left: '10px',
          right: '10px',
        } as FourSideStylingEntity;
        component.iconUrl = '$icon/material/person';
        component.wcagProperties = {
          ariaLabel: accountLanguageService
            ? accountLanguageService.translate('input.firstNamePlaceholder')
            : 'Enter first name',
        };
        break;
      case WidgetComponentSubType.InputTextLastName:
        // component.textProperties.text =
        //   languageService.translateInstantly('campaign.widget.component.input-text-name.last-name-placeholder');
        component.label.text = accountLanguageService
          ? accountLanguageService.translate('input.lastNameLabel')
          : 'Last Name';
        component.padding = {
          top: '5px',
          bottom: '5px',
          left: '10px',
          right: '10px',
        } as FourSideStylingEntity;
        component.iconUrl = '$icon/material/person';
        component.wcagProperties = {
          ariaLabel: accountLanguageService
            ? accountLanguageService.translate('input.lastNamePlaceholder')
            : 'Enter last name',
        };
        break;
      case WidgetComponentSubType.InputTextPhone:
        // component.textProperties.text = languageService.translateInstantly('campaign.widget.component.input-text-phone.placeholder');
        component.label.text = accountLanguageService ? accountLanguageService.translate('input.phoneLabel') : 'Phone';
        component.iconUrl = 'phone.svg';
        component.padding = {
          top: '5px',
          bottom: '5px',
          left: '10px',
          right: '10px',
        } as FourSideStylingEntity;
        component.wcagProperties = {
          ariaLabel: accountLanguageService
            ? accountLanguageService.translate('input.phonePlaceholder')
            : 'Enter phone number',
        };
        break;
      case WidgetComponentSubType.InputTextArea:
        component.name = data.name;
        // component.textProperties.text = languageService.translateInstantly('campaign.widget.component.input-textarea.placeholder');
        component.label.text = accountLanguageService
          ? accountLanguageService.translate('input.textareaLabel')
          : 'Message';
        component.wcagProperties = {
          ariaLabel: accountLanguageService
            ? accountLanguageService.translate('input.textareaPlaceholder')
            : 'Enter your message',
        };
        break;
      case WidgetComponentSubType.InputTextRegular:
        component.name = data.name;
        // component.textProperties.text = languageService.translateInstantly('campaign.widget.component.input-text-regular.placeholder');
        component.label.text = accountLanguageService ? accountLanguageService.translate('input.regularLabel') : 'Text';
        component.wcagProperties = {
          ariaLabel: accountLanguageService
            ? accountLanguageService.translate('input.regularPlaceholder')
            : 'Enter text',
        };
        break;
      default:
        // component.textProperties = {text: languageService.translateInstantly('campaign.widget.component.input-text.placeholder')};
        component.label = {
          text: accountLanguageService ? accountLanguageService.translate('input.defaultLabel') : 'Label',
        };
        component.wcagProperties = {
          ariaLabel: accountLanguageService
            ? accountLanguageService.translate('input.defaultPlaceholder')
            : 'Placeholder',
        };
    }
    component.border = {
      color: '#CCCCCCFF',
      style: 'solid',
      roundValue: '1px',
    } as ComponentBorder;
    component.background = { color: '#FFFFFFFF' };
    component.height = component.subType === WidgetComponentSubType.InputTextArea ? '65px' : '36px';
    component.radius = { roundValue: '4px' } as ComponentRadius;
    component.margin = {
      top: '8px',
      bottom: '16px',
      left: '0px',
      right: '0px',
    } as FourSideStylingEntity;
    return component;
  }

  public static populateInputDateComponentWithDefaultValues(
    component: WidgetComponent,
    data: { name: string },
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ) {
    component.textProperties = { text: 'Placeholder' };
    component.textProperties.fontSize = '14px';
    component.textProperties.font = this.getCurrentDefaultFontName(currentAccount);
    component.textProperties.lineHeight = '14px';
    component.textProperties.color = '#000000';
    component.textProperties.alignment = TextAlignment.Left;
    component.label = { text: 'Label' };
    component.label.fontSize = '13px';
    component.label.font = this.getCurrentDefaultFontName(currentAccount);
    component.label.lineHeight = '14px';
    component.label.color = '#909090';
    component.label.fontWeight = 600;
    component.label.alignment = TextAlignment.Left;
    component.label.margin = {
      top: '0px',
      right: '0px',
      bottom: '5px',
      left: '0px',
    } as FourSideStylingEntity;

    component.name = data.name;
    component.textProperties.text = accountLanguageService
      ? accountLanguageService.translate('input.datePlaceholder')
      : 'Select date';
    component.label.text = accountLanguageService ? accountLanguageService.translate('common.label') : 'Label';
    component.border = {
      color: '#CCCCCCFF',
      style: 'solid',
      roundValue: '1px',
    } as ComponentBorder;
    component.background = { color: '#FFFFFFFF' };
    component.height = component.subType === WidgetComponentSubType.InputTextArea ? '65px' : '32px';
    component.radius = { roundValue: '4px' } as ComponentRadius;
    component.padding = { roundValue: '10px' } as FourSideStylingEntity;
    component.margin = {
      top: '8px',
      bottom: '8px',
      left: '0px',
      right: '0px',
    } as FourSideStylingEntity;
    return component;
  }

  public static populateDropdownComponentWithDefaultValues(
    component: WidgetComponent,
    data: { name: string },
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ) {
    component.textProperties = { text: 'Placeholder' };
    component.textProperties.fontSize = '14px';
    component.textProperties.font = this.getCurrentDefaultFontName(currentAccount);
    component.textProperties.lineHeight = '14px';
    component.textProperties.color = '#000000';
    component.textProperties.alignment = TextAlignment.Left;
    component.label = { text: 'Label' };
    component.label.fontSize = '13px';
    component.label.font = this.getCurrentDefaultFontName(currentAccount);
    component.label.lineHeight = '14px';
    component.label.color = '#909090';
    component.label.fontWeight = 600;
    component.label.alignment = TextAlignment.Left;
    component.label.margin = {
      top: '0px',
      right: '0px',
      bottom: '5px',
      left: '0px',
    } as FourSideStylingEntity;

    component.name = data.name;
    component.iconUrl = 'https://widget-resources.triggerbee.com/library/presets/dropdown/arrow_down.png';
    component.textProperties.text = accountLanguageService
      ? accountLanguageService.translate('dropdown.placeholder')
      : 'Select an option';
    if (component.subType === WidgetComponentSubType.DropdownCountry) {
      component.label.text = accountLanguageService
        ? accountLanguageService.translate('dropdown.chooseCountry')
        : 'Country';
    } else {
      component.label.text = accountLanguageService ? accountLanguageService.translate('common.label') : 'Label';
    }
    component.border = {
      color: '#CCCCCCFF',
      style: 'solid',
      roundValue: '1px',
    } as ComponentBorder;
    component.background = { color: '#FFFFFFFF' };
    component.height = component.subType === WidgetComponentSubType.InputTextArea ? '65px' : '32px';
    component.radius = { roundValue: '4px' } as ComponentRadius;
    component.padding = { roundValue: '10px' } as FourSideStylingEntity;
    component.margin = {
      top: '8px',
      bottom: '8px',
      left: '0px',
      right: '0px',
    } as FourSideStylingEntity;

    if (component.subType == WidgetComponentSubType.DropdownRegular) {
      const firstItem = accountLanguageService ? accountLanguageService.translate('dropdown.firstItem') : 'Option 1';
      const secondItem = accountLanguageService ? accountLanguageService.translate('dropdown.secondItem') : 'Option 2';
      component.listItems = [
        {
          key: firstItem,
          value: firstItem,
        } as ComponentListItem,
        {
          key: secondItem,
          value: secondItem,
        } as ComponentListItem,
      ];
    }
    if (component.subType == WidgetComponentSubType.DropdownCountry) {
      component.listItems = [];
      component.textProperties.lineHeight = '24px';
      component.textProperties.color = '#393e46ff';
      component.countrySelectProperties = {
        label: CountryLabel.Name,
        subLabels: [CountryLabel.Native],
        include: [CountrySelectFilter.Sovereign],
        useBrowserLanguage: false,
        defaultCountry: this.getDefaultCountryFromAccount(currentAccount),
      } as CountrySelectProperties;
      component.background.color = '#ffffff';
    }

    return component;
  }

  private static getDefaultCountryFromAccount(currentAccount: Account): string {
    // Create mapping from account language to country ISO codes
    const languageToCountryMap: { [key: string]: string } = {
      en: 'GB', // English -> Great Britain
      sv: 'SE', // Swedish -> Sweden
      da: 'DK', // Danish -> Denmark
      nl: 'NL', // Dutch -> Netherlands
      fi: 'FI', // Finnish -> Finland
      fr: 'FR', // French -> France
      de: 'DE', // German -> Germany
      hu: 'HU', // Hungarian -> Hungary
      it: 'IT', // Italian -> Italy
      no: 'NO', // Norwegian -> Norway
      pl: 'PL', // Polish -> Poland
      pt: 'PT', // Portuguese -> Portugal
      ro: 'RO', // Romanian -> Romania
      es: 'ES', // Spanish -> Spain
      uk: 'UA', // Ukrainian -> Ukraine
    };

    if (currentAccount?.language && languageToCountryMap[currentAccount.language]) {
      return languageToCountryMap[currentAccount.language];
    }

    return 'SE';
  }
  private static populateDataRepeaterComponentWithDefaultValues(
    component: DataRepeaterComponent,
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ) {
    if (component.subType === WidgetComponentSubType.DataRepeaterLeaderboard) {
      return this.populateDataRepeaterLeaderboardComponentWithDefaultValues(
        component,
        languageService,
        currentAccount,
        accountLanguageService,
      );
    } else {
      return this.populateDataRepeaterRegularComponentWithDefaultValues(
        component,
        languageService,
        currentAccount,
        accountLanguageService,
      );
    }
  }

  private static populateDataRepeaterRegularComponentWithDefaultValues(
    component: DataRepeaterComponent,
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ) {
    let titleTextComponent = {
      type: WidgetComponentType.Text,
      subType: WidgetComponentSubType.TextRegular,
    } as WidgetComponent;
    titleTextComponent = this.populateTextComponentWithDefaultValues(
      titleTextComponent,
      true,
      languageService,
      currentAccount,
      false,
      accountLanguageService,
    );
    titleTextComponent.order = 0;
    titleTextComponent.textProperties.fontWeight = 700;
    titleTextComponent.textProperties.text = '<p><span>' + '{{example}}' + '</span></p>';
    titleTextComponent.textProperties.font = this.getCurrentDefaultFontName(currentAccount);
    titleTextComponent.textProperties.color = '#000000';
    titleTextComponent.width = '100%';
    titleTextComponent.margin = {
      top: '8px',
      bottom: '8px',
      left: '0px',
      right: '0px',
    } as FourSideStylingEntity;

    let colorTextComponent = {
      type: WidgetComponentType.Text,
      subType: WidgetComponentSubType.TextRegular,
    } as WidgetComponent;
    colorTextComponent = this.populateTextComponentWithDefaultValues(
      colorTextComponent,
      true,
      languageService,
      currentAccount,
      false,
      accountLanguageService,
    );
    colorTextComponent.order = 1;
    colorTextComponent.textProperties.text = '<p><span>' + '{{example}}' + '</span></p>';
    colorTextComponent.textProperties.font = this.getCurrentDefaultFontName(currentAccount);
    colorTextComponent.textProperties.color = '#000000';
    colorTextComponent.width = '100%';
    colorTextComponent.margin = {
      top: '8px',
      bottom: '8px',
      left: '0px',
      right: '0px',
    } as FourSideStylingEntity;

    let priceTextComponent = {
      type: WidgetComponentType.Text,
      subType: WidgetComponentSubType.TextRegular,
    } as WidgetComponent;
    priceTextComponent = this.populateTextComponentWithDefaultValues(
      priceTextComponent,
      true,
      languageService,
      currentAccount,
      false,
      accountLanguageService,
    );
    priceTextComponent.order = 2;
    priceTextComponent.textProperties.text = '<p><span>' + '{{example}}' + '</span></p>';
    priceTextComponent.textProperties.font = this.getCurrentDefaultFontName(currentAccount);
    priceTextComponent.textProperties.color = '#000000';
    priceTextComponent.width = '100%';
    priceTextComponent.margin = {
      top: '8px',
      bottom: '8px',
      left: '0px',
      right: '0px',
    } as FourSideStylingEntity;

    component.children = [titleTextComponent, colorTextComponent, priceTextComponent];
    component.margin = {
      top: '8px',
      bottom: '8px',
      left: '0px',
      right: '0px',
    } as FourSideStylingEntity;
    return component;
  }

  private static populateDataRepeaterLeaderboardComponentWithDefaultValues(
    component: DataRepeaterComponent,
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ) {
    component.dataRepeaterSettings = {
      maxItems: 10,
      flexDirection: Direction.Column,
    };

    let textComponent = {
      type: WidgetComponentType.Text,
      subType: WidgetComponentSubType.TextRegular,
    } as WidgetComponent;
    textComponent = this.populateTextComponentWithDefaultValues(
      textComponent,
      true,
      languageService,
      currentAccount,
      false,
      accountLanguageService,
    );
    textComponent.margin = {
      top: '0px',
      bottom: '8px',
      left: '0px',
      right: '0px',
    } as FourSideStylingEntity;
    textComponent.width = '33%';

    const groupComponent = {
      type: WidgetComponentType.Group,
      subType: WidgetComponentSubType.GroupRegular,
    } as WidgetComponent;
    groupComponent.children = [
      structuredClone(textComponent),
      structuredClone(textComponent),
      structuredClone(textComponent),
    ];

    groupComponent.children[0].textProperties.text = '<p><span>{{position}}</span></p>';
    groupComponent.children[0].order = 0;
    groupComponent.children[1].textProperties.text = '<p><span>{{name}}</span></p>';
    groupComponent.children[1].order = 1;
    groupComponent.children[2].textProperties.text = '<p><span>{{score}}</span></p>';
    groupComponent.children[2].order = 2;

    component.children = [groupComponent];

    return component;
  }

  private static populateIFrameComponentWithDefaultValues(
    component: IFrameComponent,
    accountLanguageService?: AccountLanguageService,
  ) {
    component.iFrameUrl = 'https://triggerbee.com/';
    component.overflow = IFrameOverflow.Hide;
    component.width = '100%';
    component.height = '200px';
    component.margin = {
      top: '8px',
      bottom: '8px',
      left: '0px',
      right: '0px',
    } as FourSideStylingEntity;
    return component;
  }

  private static populateReferFriendMobileComponentWithDefaultValues(
    component: ShareComponent,
    languageService: LanguageService,
    currentAccount: Account,
    accountLanguageService?: AccountLanguageService,
  ) {
    component.textProperties = {
      text: accountLanguageService ? accountLanguageService.translate('share.buttonText') : 'Share',
      color: '#000000',
      font: this.getCurrentDefaultFontName(currentAccount),
      fontSize: '12px',
      lineHeight: '12px',
      alignment: TextAlignment.Center,
    };
    component.width = undefined;
    component.padding = {
      top: '8px',
      right: '8px',
      bottom: '8px',
      left: '8px',
    };
    component.background = { color: '#ffffff' };
    component.border = {
      color: '#abababff',
      style: 'solid',
      roundValue: '1px',
    } as ComponentBorder;
    component.iconUrl = 'share.svg';
    component.iconSize = 24;
    component.iconPosition = 'left of text';

    return component;
  }

  public static filterVisibleComponents(
    currentDeviceView: WidgetDeviceView,
    components: Array<WidgetComponent>,
  ): Array<WidgetComponent> {
    if (components) {
      return components.filter(
        (component) =>
          (currentDeviceView === WidgetDeviceView.Desktop && component.hideOnDesktop !== true) ||
          (currentDeviceView === WidgetDeviceView.Mobile && component.hideOnMobile !== true),
      );
    } else {
      return undefined;
    }
  }

  // Border-radius never clips children, so a container background paints square corners over the
  // state's rounded corners. Corner-touching containers inherit the state radius on their outer corners.
  public static deriveContainerRadiusFromState(
    stateRadius: ComponentRadius,
    containers: Array<WidgetContainer>,
    containerId: number,
    currentDeviceView: WidgetDeviceView,
    direction?: Direction,
    stateBorder?: ComponentBorder,
    statePadding?: FourSideStylingEntity,
  ): ComponentRadius | undefined {
    if (!stateRadius) {
      return undefined;
    }
    const hasRadiusValue =
      !!stateRadius.roundValue ||
      !!stateRadius.topLeft ||
      !!stateRadius.topRight ||
      !!stateRadius.bottomLeft ||
      !!stateRadius.bottomRight;
    if (!hasRadiusValue) {
      return undefined;
    }

    const isMobileView = currentDeviceView === WidgetDeviceView.Mobile;
    const visibleContainers = (containers || [])
      .filter((container) => (isMobileView ? container.hideOnMobile !== true : container.hideOnDesktop !== true))
      .sort((a, b) => a.order - b.order);

    const containerIndex = visibleContainers.findIndex((container) => container.id === containerId);
    if (containerIndex === -1) {
      return undefined;
    }

    const isFirst = containerIndex === 0;
    const isLast = containerIndex === visibleContainers.length - 1;
    if (!isFirst && !isLast) {
      return undefined;
    }

    const corners: ComponentRadius = stateRadius.roundValue
      ? {
          topLeft: stateRadius.roundValue,
          topRight: stateRadius.roundValue,
          bottomLeft: stateRadius.roundValue,
          bottomRight: stateRadius.roundValue,
        }
      : stateRadius;

    // Without an explicit direction, fall back to the legacy assumption: desktop lays
    // containers out in a row, mobile stacks them in a column.
    const isColumnLayout =
      direction != null ? direction === Direction.Column || direction === Direction.ColumnReverse : isMobileView;
    const isReversed = direction === Direction.RowReverse || direction === Direction.ColumnReverse;
    const visualFirst = isReversed ? isLast : isFirst;
    const visualLast = isReversed ? isFirst : isLast;

    // A corner with no state padding shrinks by the border width to follow the border's inner curve
    const borderPx = (side: 'top' | 'right' | 'bottom' | 'left') => WidgetComponentHelpers.getSidePx(stateBorder, side);
    const paddingPx = (side: 'top' | 'right' | 'bottom' | 'left') =>
      WidgetComponentHelpers.getSidePx(statePadding, side);
    const innerCorner = (
      radius: string | number,
      vertical: 'top' | 'bottom',
      horizontal: 'left' | 'right',
    ): string | number | undefined => {
      if (!radius) {
        return undefined;
      }
      const border = Math.max(borderPx(vertical), borderPx(horizontal));
      const touchesBorder = paddingPx(vertical) === 0 && paddingPx(horizontal) === 0;
      const radiusPx = WidgetComponentHelpers.extractNumberFromPixelString(radius);
      if (border === 0 || !touchesBorder || !Number.isFinite(radiusPx)) {
        return radius;
      }
      const innerRadius = radiusPx - border;
      return innerRadius > 0 ? `${innerRadius}px` : undefined;
    };

    const derived: ComponentRadius = isColumnLayout
      ? {
          topLeft: visualFirst ? innerCorner(corners.topLeft, 'top', 'left') : undefined,
          topRight: visualFirst ? innerCorner(corners.topRight, 'top', 'right') : undefined,
          bottomLeft: visualLast ? innerCorner(corners.bottomLeft, 'bottom', 'left') : undefined,
          bottomRight: visualLast ? innerCorner(corners.bottomRight, 'bottom', 'right') : undefined,
        }
      : {
          topLeft: visualFirst ? innerCorner(corners.topLeft, 'top', 'left') : undefined,
          bottomLeft: visualFirst ? innerCorner(corners.bottomLeft, 'bottom', 'left') : undefined,
          topRight: visualLast ? innerCorner(corners.topRight, 'top', 'right') : undefined,
          bottomRight: visualLast ? innerCorner(corners.bottomRight, 'bottom', 'right') : undefined,
        };

    const hasDerivedCorner = !!(derived.topLeft || derived.topRight || derived.bottomLeft || derived.bottomRight);
    return hasDerivedCorner ? derived : undefined;
  }

  public static getSidePx(value: FourSideStylingEntity, side: 'top' | 'right' | 'bottom' | 'left'): number {
    if (!value) {
      return 0;
    }
    const px = WidgetComponentHelpers.extractNumberFromPixelString(value.roundValue || value[side]);
    return Number.isFinite(px) ? px : 0;
  }

  public static hasWidgetSomeInput(widget: Widget): boolean {
    if (!widget || !widget.states) {
      return false;
    }
    for (let i = 0; i < widget.states.length; i++) {
      if (widget.states[i].containers) {
        for (let j = 0; j < widget.states[i].containers.length; j++) {
          if (widget.states[i].containers[j].components) {
            if (
              widget.states[i].containers[j].components.some(
                (component) =>
                  this.isComponentInputField(component) || component.subType === WidgetComponentSubType.ButtonSubmit,
              )
            ) {
              return true;
            }
          }
        }
      }
    }
    return false;
  }

  public static hasValidStep(widget: Widget): boolean {
    if (!widget || !widget.states || widget.states.length === 0) {
      return false;
    }

    // Get outcome state IDs
    const outcomeStateIds = widget.outcomes?.map((outcome) => outcome.stateId) ?? [];

    // Check if there's at least one valid state
    // A valid state is one that is NOT a primary canvas state and NOT an outcome state
    return widget.states.some((state) => !state.isPrimaryCanvasState && !outcomeStateIds.includes(state.id));
  }

  public static hasWidgetStateSomeInput(widgetState: WidgetState): boolean {
    if (!widgetState || !widgetState.containers) {
      return false;
    }
    for (let i = 0; i < widgetState.containers.length; i++) {
      if (widgetState.containers[i].components) {
        if (
          widgetState.containers[i].components.some(
            (component) => this.isComponentInputField(component) || this.isComponentSubmit(component),
          )
        ) {
          return true;
        }
      }
    }
    return false;
  }

  public static stateHasProperty(
    statesWithInputFields: WidgetState[],
    predicate: (value: WidgetComponent) => unknown,
  ): boolean {
    return !!statesWithInputFields.find((state) =>
      state.containers.find(
        (container) =>
          container?.components.find(predicate) || container?.components.find((x) => x.children?.find(predicate)),
      ),
    );
  }

  public static hasWidgetSomeSurvey(widget: Widget): boolean {
    if (!widget || !widget.states) {
      return false;
    }
    for (let i = 0; i < widget.states.length; i++) {
      if (widget.states[i].containers) {
        for (let j = 0; j < widget.states[i].containers.length; j++) {
          if (widget.states[i].containers[j].components) {
            if (widget.states[i].containers[j].components.some((component) => this.isComponentSurvey(component))) {
              return true;
            }
          }
        }
      }
    }
    return false;
  }

  public static hasWidgetStateNoInputButSubmitButton(widgetState: WidgetState): boolean {
    if (!widgetState || !widgetState.containers) {
      return false;
    }
    let hasSubmitButton = false;
    let hasAnyInputComponent = false;
    for (let i = 0; i < widgetState.containers.length; i++) {
      if (widgetState.containers[i].components) {
        widgetState.containers[i].components.forEach((component) => {
          if (this.isComponentInputField(component)) {
            hasAnyInputComponent = true;
          } else if (component.subType === WidgetComponentSubType.ButtonSubmit) {
            hasSubmitButton = true;
          }
        });
      }
    }
    return hasSubmitButton && !hasAnyInputComponent;
  }

  public static hasWidgetStateEmailInput(widgetState: WidgetState): boolean {
    if (!widgetState || !widgetState.containers) {
      return false;
    }
    for (let i = 0; i < widgetState.containers.length; i++) {
      if (widgetState.containers[i].components) {
        const isInputTextEmail = widgetState.containers[i].components.some((component) => {
          if (component.children) {
            return component.children.some(
              (child) => this.isComponentInputField(child) && child.subType === WidgetComponentSubType.InputTextEmail,
            );
          } else {
            return this.isComponentInputField(component) && component.subType === WidgetComponentSubType.InputTextEmail;
          }
        });
        if (isInputTextEmail) {
          return true;
        }
      }
    }
    return false;
  }

  public static isComponentInputField(component: any): boolean {
    if (
      component.type === WidgetComponentType.InputText ||
      component.type === WidgetComponentType.Date ||
      component.type === WidgetComponentType.Dropdown ||
      component.type === WidgetComponentType.ButtonList ||
      component.subType === WidgetComponentSubType.CheckboxConsent ||
      (component.children &&
        component.children.some(
          (child) =>
            child.type === WidgetComponentType.InputText ||
            child.type === WidgetComponentType.Date ||
            child.type === WidgetComponentType.Dropdown ||
            child.type === WidgetComponentType.ButtonList ||
            child.subType === WidgetComponentSubType.CheckboxConsent,
        ))
    ) {
      return true;
    }
    return false;
  }

  public static isSingleComponentSurvey(component) {
    if (
      component.subType === WidgetComponentSubType.ButtonListStarRating ||
      component.subType === WidgetComponentSubType.ButtonListNumericalRating ||
      component.subType === WidgetComponentSubType.ButtonListNpsRating ||
      component.subType === WidgetComponentSubType.ButtonListSmileyRating ||
      component.subType === WidgetComponentSubType.ButtonListThumbRating ||
      component.subType === WidgetComponentSubType.CsatPlaceholder ||
      component.subType === WidgetComponentSubType.ButtonListCsatStarRating ||
      component.subType === WidgetComponentSubType.ButtonListCsatSmileyRating ||
      component.subType === WidgetComponentSubType.ButtonListCsatNumericalRating
    ) {
      return true;
    }
  }

  public static isComponentSurvey(component: any): boolean {
    if (
      this.isSingleComponentSurvey(component) ||
      (component.children && component.children.some((child) => this.isSingleComponentSurvey(child)))
    ) {
      return true;
    }
    return false;
  }

  public static hasWidgetAnExpiredDeadline(widget: Widget): boolean {
    if (!widget || !widget.states) {
      return false;
    }
    for (const state of widget.states) {
      const deadlinesOnState = this.getAllComponentsOfTypes(
        [WidgetComponentType.Deadline],
        this.getAllComponentsOnState(state),
      );
      if (deadlinesOnState && deadlinesOnState.length > 0) {
        if (deadlinesOnState.some((d) => this.isDeadlineComponentExpired(d))) {
          return true;
        }
      }
    }
    return false;
  }

  public static hasNoResultOnSpinTheWheelOrMemory(widget: Widget): boolean {
    if (!widget || !widget.states) {
      return false;
    }
    for (const state of widget.states) {
      const spinTheWheelOnState = this.getAllComponentsOfTypes(
        [WidgetComponentType.SpinTheWheel],
        this.getAllComponentsOnState(state),
      );
      const memoryOnState = this.getAllComponentsOfTypes(
        [WidgetComponentType.Memory],
        this.getAllComponentsOnState(state),
      );
      if (spinTheWheelOnState && spinTheWheelOnState.length > 0) {
        if (
          spinTheWheelOnState.some((el) =>
            el.spinTheWheelProperties.sliceItems.some(
              (item) => item.isWinner && !item.resultStateId && !item.resultCouponCode && !item.resultCouponSetId,
            ),
          )
        ) {
          return true;
        }
      }
      if (memoryOnState && memoryOnState.length > 0) {
        if (memoryOnState.some((el) => !el.memoryProperties.resultStateId && !el.memoryProperties.resultCouponCode)) {
          return true;
        }
      }
    }
    return false;
  }

  public static isSubscriptionCheckboxListInvalid(widget: Widget): boolean {
    if (!widget || !widget.states) {
      return false;
    }
    for (const state of widget.states) {
      const subscriptionCheckboxComponents = this.getAllComponentsOfTypes(null, this.getAllComponentsOnState(state), [
        WidgetComponentSubType.ButtonListSubscriptionCheckbox,
      ]);
      if (subscriptionCheckboxComponents && subscriptionCheckboxComponents.length > 0) {
        if (
          subscriptionCheckboxComponents.some((el) => el.listItems.some((item) => !item.key)) ||
          subscriptionCheckboxComponents.some((el) => !el.listItems.length) ||
          subscriptionCheckboxComponents.some((el) => !el.subscriptionCheckboxProperties?.sourceIntegration)
        ) {
          return true;
        }
      }
    }
    return false;
  }

  public static isCustobarCombinesWithEmailInSubscriptionCheckboxList(widget: Widget): boolean {
    if (!widget || !widget.states) {
      return false;
    }
    let isCustobarCombinesWithEmail;
    widget.states.forEach((state) => {
      if (this.hasWidgetStateEmailInput(state)) {
        const subscriptionCheckboxComponents = this.getAllComponentsOfTypes(
          null,
          this.getAllComponentsOnWidget(widget),
          [WidgetComponentSubType.ButtonListSubscriptionCheckbox],
        );
        if (subscriptionCheckboxComponents && subscriptionCheckboxComponents.length > 0) {
          if (
            subscriptionCheckboxComponents.some(
              (el) => el.subscriptionCheckboxProperties.sourceIntegration === IntegrationType.Custobar,
            )
          ) {
            isCustobarCombinesWithEmail = true;
          }
        }
      }
    });
    return isCustobarCombinesWithEmail;
  }

  public static hasWidgetAnExpiredScheduling(campaign: OnsiteCampaign): boolean {
    if (!campaign?.scheduling?.endDate) {
      return false;
    }
    // Publishing an A/B test restamps its window as today plus the chosen days, so a draft's dates never go live.
    if (campaign.type === OnsiteCampaignType.ABTesting) {
      return false;
    }
    const nowMillis = TimeUtils.getMoment().valueOf();
    const deadlineMillis = TimeUtils.getMoment(campaign.scheduling.endDate).valueOf();
    return deadlineMillis < nowMillis;
  }

  public static isDeadlineComponentExpired(deadlineComponent: WidgetComponent): boolean {
    if (!deadlineComponent || deadlineComponent.type !== WidgetComponentType.Deadline) {
      return false;
    }
    if (
      deadlineComponent.subType === WidgetComponentSubType.DeadlineCountdown ||
      (deadlineComponent as DeadlineComponent).deadlineProperties?.deadlineOccurrence === DeadlineOccurrence.EveryDay
    ) {
      return false;
    }
    const nowMillis = TimeUtils.getMoment().valueOf();
    const deadlineMillis = TimeUtils.getMoment(
      (deadlineComponent as DeadlineComponent).deadlineProperties.deadlineDate,
    ).valueOf();
    if (deadlineMillis < nowMillis) return true;
    if ((deadlineComponent as DeadlineComponent).deadlineProperties?.format === DeadlineFormat.Hours) {
      return !TimeUtils.getDuration(deadlineMillis - nowMillis).hours();
    }
    if ((deadlineComponent as DeadlineComponent).deadlineProperties?.format === DeadlineFormat.Days) {
      return !TimeUtils.getDuration(deadlineMillis - nowMillis).days();
    }
    if (
      (deadlineComponent as DeadlineComponent).deadlineProperties?.format === DeadlineFormat.DaysHoursMinutesSeconds ||
      (deadlineComponent as DeadlineComponent).deadlineProperties?.format === DeadlineFormat.HoursMinutesSeconds ||
      (deadlineComponent as DeadlineComponent).deadlineProperties?.format === DeadlineFormat.MinutesSeconds
    ) {
      return !TimeUtils.getDuration(deadlineMillis - nowMillis).milliseconds();
    }
    if ((deadlineComponent as DeadlineComponent).deadlineProperties?.format === DeadlineFormat.DaysHoursMinutes) {
      return !TimeUtils.getDuration(deadlineMillis - nowMillis).minutes();
    }
    return !TimeUtils.getDuration(deadlineMillis - nowMillis).days();
  }

  public static hasWidgetSubmitButton(widget: Widget): boolean {
    return (
      widget.states?.some((state) =>
        state.containers?.some((container) =>
          container.components?.some((component) => this.isComponentSubmit(component)),
        ),
      ) ?? false
    );
  }

  /** True when a visitor can minimize the widget, the only way its Minimize repetition setting applies. */
  public static hasWidgetMinimizeAction(widget: Widget): boolean {
    const isMinimize = (actionType?: string) => actionType === ButtonActionType.MinimizeWidget;
    return (
      widget.states?.some((state) => isMinimize(state.actionType)) ||
      this.getAllComponentsOnWidget(widget)?.some(
        (component) =>
          isMinimize(component.actionType) ||
          (component.subType === WidgetComponentSubType.ButtonHide &&
            component.actionType === ButtonActionType.SwitchState) ||
          component.listItems?.some((item) => isMinimize(item.action?.actionType)),
      ) ||
      false
    );
  }

  public static isComponentSubmit(component: any): boolean {
    return this.isSubmitType(component) || component.children?.some((child) => this.isSubmitType(child));
  }

  public static isSubmitType(c: WidgetComponent) {
    return (
      c.subType === WidgetComponentSubType.ButtonSubmit ||
      c.stepNavigationProperties?.lastButtonType === StepNavigationLastButtonType.Submit
    );
  }

  public static hasOnlyConsentAndNoOtherInputs(widgets: any[]): boolean {
    if (!Array.isArray(widgets)) return false;
    for (const widget of widgets) {
      let hasConsent = false;
      let hasOtherInputs = false;
      let hasAnyRelevantComponent = false;
      for (const state of widget.states ?? []) {
        for (const container of state.containers ?? []) {
          for (const comp of container.components ?? []) {
            const isInputField = this.isComponentInputField(comp);
            const isConsent = comp.subType === WidgetComponentSubType.CheckboxConsent;
            const isSubmit = comp.subType === WidgetComponentSubType.ButtonSubmit;
            if (isInputField) {
              hasAnyRelevantComponent = true;
              if (isConsent) {
                hasConsent = true;
              } else {
                hasOtherInputs = true;
              }
            } else if (isSubmit) {
              hasAnyRelevantComponent = true;
            }
          }
        }
      }
      if ((hasConsent && !hasOtherInputs) || (!hasAnyRelevantComponent && hasConsent)) {
        return true;
      }
    }
    return false;
  }

  public static getComponentFromStates(states, subType): any {
    let targetState;
    let targetComponent: any = undefined;
    if (states?.length) {
      states.forEach((state) =>
        state.containers.forEach((container) => {
          if (container.components) {
            container.components.forEach((component: WidgetComponent) => {
              if (component.subType === subType) {
                targetComponent = component;
                targetState = state.id;
              }
              if (component.children) {
                const compIndex = component.children.findIndex((child) => child.subType === subType);
                if (compIndex !== -1) {
                  targetComponent = component.children[compIndex];
                  targetState = state.id;
                }
              }
            });
          }
        }),
      );
    }
    return { targetComponent, targetState };
  }

  public static extractInputFieldComponentsFromState(
    widgetStates: WidgetState[],
    excludedInputFields = [WidgetComponentSubType.InputTextPassword],
    excludeFromIntlPhone?,
  ): Array<WidgetComponent> {
    const inputFieldComponents = [];
    widgetStates?.forEach((widgetState) => {
      if (widgetState.containers) {
        widgetState.containers.forEach((container: WidgetContainer) => {
          if (container.components) {
            container.components.forEach((component: WidgetComponent) => {
              let isComponentExcluded = excludedInputFields.includes(component.subType);
              if (component.children) {
                isComponentExcluded = component.children.some((component) =>
                  excludedInputFields.includes(component.subType),
                );
              }
              if (!isComponentExcluded) {
                if (
                  component.type === WidgetComponentType.InputText ||
                  component.type === WidgetComponentType.ButtonList ||
                  component.type === WidgetComponentType.Dropdown ||
                  component.type === WidgetComponentType.Date
                ) {
                  inputFieldComponents.push(component);
                } else if (component.children) {
                  component.children.forEach((childComponent: WidgetComponent) => {
                    if (
                      childComponent.type === WidgetComponentType.InputText ||
                      childComponent.type === WidgetComponentType.Date ||
                      childComponent.type === WidgetComponentType.Dropdown ||
                      childComponent.type === WidgetComponentType.ButtonList
                    ) {
                      if (
                        component.subType === WidgetComponentSubType.InternationalPhoneRegular &&
                        childComponent.subType === WidgetComponentSubType.DropdownCountry
                      ) {
                        if (!excludeFromIntlPhone) inputFieldComponents.push(childComponent);
                      } else {
                        inputFieldComponents.push(childComponent);
                      }
                    }
                  });
                }
              }
            });
          }
        });
      }
    });
    return inputFieldComponents;
  }

  public static conditionsToConditionsConfig(
    option: CriteriaElement,
    value?,
    operator?: string,
  ): WidgetConditionTypeConfig {
    const name = option.type ? option.type : option.name.charAt(0).toUpperCase() + option.name.slice(1);
    const conditionTypeConfig = {} as WidgetConditionTypeConfig;
    switch (name.trim()) {
      case SessionConditionType.UTMSource:
        conditionTypeConfig.type = SessionConditionType.UTMSource;
        conditionTypeConfig.icon = option.icon;
        conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
        conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.utm-source-descr';
        conditionTypeConfig.defaultShow = true;
        conditionTypeConfig.defaultValue = value;
        conditionTypeConfig.operator = operator;
        break;
      case SessionConditionType.UTMMedium:
        conditionTypeConfig.type = SessionConditionType.UTMMedium;
        conditionTypeConfig.icon = option.icon;
        conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
        conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.utm-medium-descr';
        conditionTypeConfig.defaultShow = true;
        conditionTypeConfig.defaultValue = value;
        conditionTypeConfig.operator = operator;
        break;
      case SessionConditionType.UTMCampaign:
        conditionTypeConfig.type = SessionConditionType.UTMCampaign;
        conditionTypeConfig.icon = option.icon;
        conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
        conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.utm-campaign-descr';
        conditionTypeConfig.defaultShow = true;
        conditionTypeConfig.defaultValue = value;
        conditionTypeConfig.operator = operator;
        break;
      case SessionConditionType.MarketingEmailVisit:
        conditionTypeConfig.type = SessionConditionType.MarketingEmailVisit;
        conditionTypeConfig.icon = option.icon;
        conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
        conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.email-marketing-descr';
        conditionTypeConfig.defaultShow = true;
        conditionTypeConfig.defaultValue = null;
        break;
      case SessionConditionType.FacebookVisit:
        conditionTypeConfig.type = SessionConditionType.FacebookVisit;
        conditionTypeConfig.svgIcon = option.svgIcon;
        conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
        conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.facebook-descr';
        conditionTypeConfig.defaultShow = true;
        conditionTypeConfig.defaultValue = null;
        break;
      case SessionConditionType.InstagramVisit:
        conditionTypeConfig.type = SessionConditionType.InstagramVisit;
        conditionTypeConfig.svgIcon = option.svgIcon;
        conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
        conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.instagram-descr';
        conditionTypeConfig.defaultShow = true;
        conditionTypeConfig.defaultValue = null;
        break;
      case SessionConditionType.BrowserLanguage:
        conditionTypeConfig.type = SessionConditionType.BrowserLanguage;
        conditionTypeConfig.icon = option.icon;
        conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
        conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.browser-lang-descr';
        conditionTypeConfig.defaultShow = true;
        conditionTypeConfig.defaultValue = value;
        conditionTypeConfig.operator = operator;
        break;
      case SessionConditionType.OtherSocialMediaVisit:
        conditionTypeConfig.type = SessionConditionType.OtherSocialMediaVisit;
        conditionTypeConfig.icon = option.icon;
        conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
        conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.social-media-descr';
        conditionTypeConfig.defaultShow = true;
        conditionTypeConfig.defaultValue = null;
        break;
      case SessionConditionType.Query:
        conditionTypeConfig.type = SessionConditionType.Query;
        conditionTypeConfig.icon = option.icon;
        conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
        conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.query-param-descr';
        conditionTypeConfig.defaultShow = true;
        conditionTypeConfig.defaultValue = value || ' ';
        conditionTypeConfig.inlineEditKey1 = 'campaign.settings.conditions.query-param-edit1';
        conditionTypeConfig.operator = 'Equals';
        break;
      case SessionConditionType.QueryParam:
        conditionTypeConfig.type = SessionConditionType.Query;
        conditionTypeConfig.icon = option.icon;
        conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.query`;
        conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.query-param-descr';
        conditionTypeConfig.defaultShow = true;
        conditionTypeConfig.defaultValue = value || ' ';
        conditionTypeConfig.inlineEditKey1 = 'campaign.settings.conditions.query-param-edit1';
        conditionTypeConfig.operator = 'Equals';
        break;
      case SessionConditionType.CookieExists:
        conditionTypeConfig.type = SessionConditionType.CookieExists;
        conditionTypeConfig.icon = option.icon;
        conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.cookieExists`;
        conditionTypeConfig.defaultShow = true;
        conditionTypeConfig.defaultValue = value || ' ';
        conditionTypeConfig.operator = operator;
        conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.cookie-exist-descr';
        break;
      case SessionConditionType.CookieEquals:
        conditionTypeConfig.type = SessionConditionType.CookieEquals;
        conditionTypeConfig.icon = option.icon;
        conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.cookieEquals`;
        conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.cookie-value-descr';
        conditionTypeConfig.defaultShow = true;
        conditionTypeConfig.defaultValue = value || ' ';
        conditionTypeConfig.operator = 'Equals';
        break;
      case SessionConditionType.JsExpression:
        conditionTypeConfig.type = SessionConditionType.JsExpression;
        conditionTypeConfig.icon = option.icon;
        conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.jsExpression`;
        conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.js-expr-descr';
        conditionTypeConfig.defaultShow = true;
        conditionTypeConfig.defaultValue = value;
        conditionTypeConfig.operator = null;
        break;
      case SessionConditionType.OnlyDesktop:
        conditionTypeConfig.type = SessionConditionType.OnlyDesktop;
        conditionTypeConfig.icon = option.icon;
        conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
        conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.desktop-only-descr';
        conditionTypeConfig.defaultShow = true;
        conditionTypeConfig.defaultValue = value;
        conditionTypeConfig.operator = operator;
        break;
      case SessionConditionType.OnlyMobile:
        conditionTypeConfig.type = SessionConditionType.OnlyMobile;
        conditionTypeConfig.icon = option.icon;
        conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
        conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.mobile-only-descr';
        conditionTypeConfig.defaultShow = true;
        conditionTypeConfig.defaultValue = value;
        conditionTypeConfig.operator = operator;
        break;
      case SessionConditionType.Domain:
        conditionTypeConfig.type = SessionConditionType.Domain;
        conditionTypeConfig.icon = option.icon;
        conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
        conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.domain-descr';
        conditionTypeConfig.defaultShow = true;
        conditionTypeConfig.defaultValue = value;
        conditionTypeConfig.operator = operator;
        break;
      case SessionConditionType.NewVisitor:
        conditionTypeConfig.type = SessionConditionType.NewVisitor;
        conditionTypeConfig.icon = option.icon;
        conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
        conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.new-visitor-descr';
        conditionTypeConfig.defaultShow = true;
        conditionTypeConfig.defaultValue = value;
        conditionTypeConfig.operator = operator;
        break;
      case SessionConditionType.ReturningVisitor:
        conditionTypeConfig.type = SessionConditionType.ReturningVisitor;
        conditionTypeConfig.icon = option.icon;
        conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
        conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.returning-visitor-descr';
        conditionTypeConfig.defaultShow = true;
        conditionTypeConfig.defaultValue = value;
        conditionTypeConfig.operator = 'MoreThan';
        break;
      case SessionConditionType.CampaignOpened:
        {
          const config = this.parseCampaignEventSettings(value);
          conditionTypeConfig.type = SessionConditionType.CampaignOpened;
          conditionTypeConfig.icon = option.icon;
          conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
          conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.campaign-opened-descr';
          conditionTypeConfig.defaultShow = true;
          conditionTypeConfig.defaultValue = config ? config.times.toString() : null;
          conditionTypeConfig.defaultValue2 = config ? config.campaignId.toString() : null;
          conditionTypeConfig.operator = operator;
        }
        break;
      case SessionConditionType.CampaignDismissal:
        {
          const config = this.parseCampaignEventSettings(value);
          conditionTypeConfig.type = SessionConditionType.CampaignDismissal;
          conditionTypeConfig.icon = option.icon;
          conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
          conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.campaign-dismissal-descr';
          conditionTypeConfig.defaultShow = true;
          conditionTypeConfig.defaultValue = config ? config.times.toString() : null;
          conditionTypeConfig.defaultValue2 = config ? config.campaignId.toString() : null;
          conditionTypeConfig.operator = operator;
        }
        break;
      case SessionConditionType.CampaignConversion:
        {
          const config = this.parseCampaignEventSettings(value);
          conditionTypeConfig.type = SessionConditionType.CampaignConversion;
          conditionTypeConfig.icon = option.icon;
          conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
          conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.campaign-conversion-descr';
          conditionTypeConfig.defaultShow = true;
          conditionTypeConfig.defaultValue = config ? config.times.toString() : null;
          conditionTypeConfig.defaultValue2 = config ? config.campaignId.toString() : null;
          conditionTypeConfig.operator = operator;
        }
        break;
      case SessionConditionType.CampaignClickthrough:
        {
          const config = this.parseCampaignEventSettings(value);
          conditionTypeConfig.type = SessionConditionType.CampaignClickthrough;
          conditionTypeConfig.icon = option.icon;
          conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
          conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.campaign-clickthrough-descr';
          conditionTypeConfig.defaultShow = true;
          conditionTypeConfig.defaultValue = config ? config.times.toString() : null;
          conditionTypeConfig.defaultValue2 = config ? config.campaignId.toString() : null;
          conditionTypeConfig.operator = operator;
        }
        break;
      case SessionConditionType.CampaignSurveyResponse:
        {
          const config = this.parseCampaignEventSettings(value);
          conditionTypeConfig.type = SessionConditionType.CampaignSurveyResponse;
          conditionTypeConfig.icon = option.icon;
          conditionTypeConfig.titleKey = `campaign.widget.audience.conditions.${option.name}`;
          conditionTypeConfig.descriptionKey = 'campaign.settings.conditions.campaign-survey-response-descr';
          conditionTypeConfig.defaultShow = true;
          conditionTypeConfig.defaultValue = config ? config.times.toString() : null;
          conditionTypeConfig.defaultValue2 = config ? config.campaignId.toString() : null;
          conditionTypeConfig.operator = operator;
        }
        break;
    }
    return conditionTypeConfig;
  }

  private static parseCampaignEventSettings(value: any): CampaignEventConfig {
    if (value) {
      return JSON.parse(value);
    }
    return null;
  }

  public static prettifyOperator(operator: string): string {
    switch (operator) {
      case '0':
        return 'Equals';
      case '10':
        return 'StartsWith';
      case '11':
        return 'EndsWith';
      case '2':
        return 'Contains';
      case '12':
        return 'AnyOf';
      case '1':
        return 'NotEquals';
    }
  }

  public static ugliffyOperator(operator: string): string {
    switch (operator) {
      case 'Equals':
        return '0';
      case 'StartsWith':
        return '10';
      case 'EndsWith':
        return '11';
      case 'Contains':
        return '2';
      case 'AnyOf':
        return '12';
      case 'NotEquals':
        return '1';
      default:
        return operator;
    }
  }

  public static getLanguagesForAudience(): Array<{
    id: string;
    displayName: string;
  }> {
    const map: Array<{ id: string; displayName: string }> = [];

    for (const n in WidgetConditionLanguages) {
      if (typeof WidgetConditionLanguages[n] === 'string') {
        map.push({
          id: WidgetConditionLanguages[n],
          displayName: WidgetConditionLanguages[n],
        });
      }
    }
    return map;
  }

  public static extractNumberFromStringWithUnit(inputString: string | number): number {
    if (!inputString) {
      return undefined;
    }
    if (typeof inputString === 'string') {
      if (inputString.endsWith('px')) {
        return this.extractNumberFromUnitString(inputString, 'px');
      } else if (inputString.endsWith('rem')) {
        return this.extractNumberFromUnitString(inputString, 'rem');
      } else if (inputString.endsWith('em')) {
        return this.extractNumberFromUnitString(inputString, 'em');
      } else {
        return Number(inputString);
      }
    }
    return inputString;
  }

  public static extractNumberFromPixelString(inputString: string | number): number {
    return this.extractNumberFromUnitString(inputString, 'px');
  }

  public static extractNumberFromEmString(inputString: string | number): number {
    return this.extractNumberFromUnitString(inputString, 'em');
  }

  public static extractNumberFromUnitString(inputString: string | number, unit: string): number {
    if (!inputString) {
      return undefined;
    }
    if (typeof inputString === 'string') {
      const extracted = inputString.toLowerCase().replace(' ', '').replace(unit, '');
      return Number(extracted);
    }
    return inputString;
  }

  public static extractDimensionUnit(inputString: string): string {
    if (!inputString || typeof inputString !== 'string') {
      return undefined;
    }
    if (inputString.toLowerCase().indexOf('px') !== -1) {
      return 'px';
    }
    if (inputString.toLowerCase().indexOf('rem') !== -1) {
      return 'rem';
    }
    if (inputString.toLowerCase().indexOf('em') !== -1) {
      return 'em';
    } else if (inputString.toLowerCase().indexOf('%') !== -1) {
      return '%';
    }
    return undefined;
  }

  public static extractNumberFromPixelOrPercentString(inputString: string | number): number {
    if (!inputString) {
      return undefined;
    }
    if (typeof inputString === 'string') {
      const extracted = inputString.toLowerCase().replace(' ', '').replace('px', '').replace('%', '');
      return Number(extracted);
    }
    return inputString;
  }

  public static getSupportedFontFamilies(customFonts?: Array<AccountCustomFont>): Array<WidgetFontDescription> {
    const customFontDescriptions = [];
    if (customFonts) {
      customFonts.forEach((customFont) =>
        customFontDescriptions.push({
          displayName: customFont.displayName,
          name: customFont.name,
          cssLink: customFont.cssLink,
          fontFiles: customFont.fontFiles,
          type: customFont.type,
          isDefault: false,
        } as WidgetFontDescription),
      );
    }
    const defaultFonts = this.getDefaultFonts();
    return customFontDescriptions.concat(defaultFonts);
  }

  public static isFontDefault(fontFamily: string): boolean {
    return this.getDefaultFonts().some((font) => font.displayName === fontFamily);
  }

  public static getDefaultFonts(): Array<WidgetFontDescription> {
    return [
      {
        displayName: 'Arial',
        name: 'arial,helvetica,sans-serif',
        isDefault: true,
      },
      {
        displayName: 'Courier New',
        name: 'courier new,courier,monospace',
        isDefault: true,
      },
      {
        displayName: 'Times New Roman',
        name: 'Times New Roman, serif',
        isDefault: true,
      },
      {
        displayName: 'Courier',
        name: 'Courier,courier,monospace',
        isDefault: true,
      },
      { displayName: 'Verdana', name: 'Verdana, serif', isDefault: true },
      { displayName: 'Georgia', name: 'Georgia, serif', isDefault: true },
      { displayName: 'Palatino', name: 'Palatino, serif', isDefault: true },
      { displayName: 'Garamond', name: 'Garamond, serif', isDefault: true },
      { displayName: 'Bookman', name: 'Bookman, serif', isDefault: true },
      {
        displayName: 'Comic Sans MS',
        name: 'Comic Sans MS, serif',
        isDefault: true,
      },
      {
        displayName: 'Trebuchet MS',
        name: 'Trebuchet MS, serif',
        isDefault: true,
      },
      {
        displayName: 'Arial Black',
        name: 'Arial Black, serif',
        isDefault: true,
      },
      { displayName: 'Impact', name: 'Impact, serif', isDefault: true },
      {
        displayName: 'Roboto',
        name: '"Roboto"',
        isDefault: true,
        cssLink:
          'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap',
      },
      {
        displayName: 'Open Sans',
        name: '"Open Sans"',
        isDefault: true,
        cssLink:
          'https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap',
      },
      {
        displayName: 'Montserrat',
        name: '"Montserrat"',
        isDefault: true,
        cssLink:
          'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
      },
      {
        displayName: 'Quicksand',
        name: '"Quicksand"',
        isDefault: true,
        cssLink: 'https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700&display=swap',
      },
      {
        displayName: 'Nunito',
        name: '"Nunito"',
        isDefault: true,
        cssLink:
          'https://fonts.googleapis.com/css2?family=Nunito:ital,wght@0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
      },
      {
        displayName: 'Merriweather',
        name: '"Merriweather"',
        isDefault: true,
        cssLink:
          'https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;0,900;1,300;1,400;1,700;1,900&display=swap',
      },
      {
        displayName: 'Playfair Display',
        name: '"Playfair Display"',
        isDefault: true,
        cssLink:
          'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
      },
      {
        displayName: 'Satisfy',
        name: '"Satisfy"',
        isDefault: true,
        cssLink: 'https://fonts.googleapis.com/css2?family=Satisfy&display=swap',
      },
      {
        displayName: 'Muli',
        name: '"Muli"',
        isDefault: true,
        cssLink: 'https://fonts.googleapis.com/css2?family=Muli:ital,wght@0,400;0,700;1,400;1,700&display=swap',
      },
      {
        displayName: 'Mulish',
        name: '"Mulish"',
        isDefault: true,
        cssLink:
          'https://fonts.googleapis.com/css2?family=Mulish:ital,wght@0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
      },
      {
        displayName: 'Caveat',
        name: '"Caveat"',
        isDefault: true,
        cssLink: 'https://fonts.googleapis.com/css2?family=Caveat:wght@400;500;600;700&display=swap',
      },
      {
        displayName: 'Shadows Into Light',
        name: '"Shadows Into Light"',
        isDefault: true,
        cssLink: 'https://fonts.googleapis.com/css2?family=Shadows+Into+Light&display=swap',
      },
      {
        displayName: 'Special Elite',
        name: '"Special Elite"',
        isDefault: true,
        cssLink: 'https://fonts.googleapis.com/css2?family=Special+Elite&display=swap',
      },
      {
        displayName: 'Abril Fatface',
        name: '"Abril Fatface"',
        isDefault: true,
        cssLink: 'https://fonts.googleapis.com/css2?family=Abril+Fatface&display=swap',
      },
      {
        displayName: 'Lato',
        name: '"Lato"',
        isDefault: true,
        cssLink:
          'https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,100;0,300;0,400;0,700;0,900;1,100;1,300;1,400;1,700;1,900&display=swap',
      },
      {
        displayName: 'Oswald',
        name: '"Oswald"',
        isDefault: true,
        cssLink: 'https://fonts.googleapis.com/css2?family=Oswald:wght@200;300;400;500;600;700&display=swap',
      },
      {
        displayName: 'PT Sans',
        name: '"PT Sans"',
        isDefault: true,
        cssLink: 'https://fonts.googleapis.com/css2?family=PT+Sans:ital,wght@0,400;0,700;1,400;1,700&display=swap',
      },
      {
        displayName: 'Raleway',
        name: '"Raleway"',
        isDefault: true,
        cssLink:
          'https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
      },
      {
        displayName: 'Roboto Condensed',
        name: '"Roboto Condensed"',
        isDefault: true,
        cssLink:
          'https://fonts.googleapis.com/css2?family=Roboto+Condensed:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap',
      },
      {
        displayName: 'Slabo 27px',
        name: '"Slabo 27px"',
        isDefault: true,
        cssLink: 'https://fonts.googleapis.com/css2?family=Slabo+27px&display=swap',
      },
      {
        displayName: 'Suez One',
        name: '"Suez One"',
        isDefault: true,
        cssLink: 'https://fonts.googleapis.com/css2?family=Suez+One&display=swap',
      },
      {
        displayName: 'Creepster',
        name: '"Creepster"',
        isDefault: true,
        cssLink: 'https://fonts.googleapis.com/css2?family=Creepster&display=swap',
      },
      {
        displayName: 'Graduate',
        name: '"Graduate"',
        isDefault: true,
        cssLink: 'https://fonts.googleapis.com/css2?family=Graduate&display=swap',
      },
      {
        displayName: 'Butcherman',
        name: '"Butcherman"',
        isDefault: true,
        cssLink: 'https://fonts.googleapis.com/css2?family=Butcherman&display=swap',
      },
      {
        displayName: 'Alfa Slab One',
        name: '"Alfa Slab One"',
        isDefault: true,
        cssLink: 'https://fonts.googleapis.com/css2?family=Alfa+Slab+One&display=swap',
      },
      {
        displayName: 'Mountains of Christmas',
        name: '"Mountains of Christmas"',
        isDefault: true,
        cssLink: 'https://fonts.googleapis.com/css2?family=Mountains+of+Christmas:wght@400;700&display=swap',
      },
      {
        displayName: 'Square Peg',
        name: '"Square Peg"',
        isDefault: true,
        cssLink: 'https://fonts.googleapis.com/css2?family=Square+Peg&display=swap',
      },
      {
        displayName: 'Source Sans Pro',
        name: '"Source Sans Pro"',
        isDefault: true,
        cssLink:
          'https://fonts.googleapis.com/css2?family=Source+Sans+Pro:ital,wght@0,200;0,300;0,400;0,600;0,700;0,900;1,200;1,300;1,400;1,600;1,700;1,900&display=swap',
      },
      {
        displayName: 'Parisienne',
        name: '"Parisienne"',
        isDefault: true,
        cssLink: 'https://fonts.googleapis.com/css2?family=Parisienne&display=swap',
      },
    ] as Array<WidgetFontDescription>;
  }

  public static parseTextAlignmentFromString(text: string): TextAlignment {
    if (!text) {
      return undefined;
    }
    if (new RegExp('left', 'i').test(text)) {
      return TextAlignment.Left;
    }
    if (new RegExp('right', 'i').test(text)) {
      return TextAlignment.Right;
    }
    if (new RegExp('center', 'i').test(text)) {
      return TextAlignment.Center;
    }
    if (new RegExp('justify', 'i').test(text)) {
      return TextAlignment.Justify;
    }
    console.warn('Unknown text string to create alignment: ' + text);
    return undefined;
  }

  public static splitWidgetStatesByType(states: Array<WidgetState>): SplittedStates {
    if (!states) {
      return;
    }
    const statesSplit = new SplittedStates();

    const teasers = states.filter((state) => state.type === WidgetComponentType.Teaser);
    if (teasers && teasers.length === 1) {
      statesSplit.teaser = teasers[0];
    }
    const successes = states.filter((state) => state.type === WidgetComponentType.Success);
    if (successes && successes.length === 1) {
      statesSplit.success = successes[0];
    }
    statesSplit.regulars = states.filter((state) => state.type === WidgetComponentType.Regular);
    if (statesSplit.regulars && statesSplit.regulars.length > 1) {
      statesSplit.regulars = statesSplit.regulars.sort((a, b) => (a.order > b.order ? 1 : -1));
    }
    return statesSplit;
  }

  public static splitAndNameWidgetStatesByType(
    states: Array<WidgetState>,
    languageService: LanguageService,
  ): SplittedStates {
    if (!states) {
      return;
    }
    const statesSplit = this.splitWidgetStatesByType(states);
    if (statesSplit.teaser) {
      statesSplit.teaser.name = statesSplit.getStateDisplayName(statesSplit.teaser.id, languageService);
    }
    if (statesSplit.success) {
      statesSplit.success.name = statesSplit.getStateDisplayName(statesSplit.success.id, languageService);
    }
    if (statesSplit.regulars) {
      statesSplit.regulars.forEach((regularState: WidgetState) => {
        regularState.name = statesSplit.getStateDisplayName(regularState.id, languageService);
      });
    }
    return statesSplit;
  }

  public static setNameWidgetStatesByType(states: Array<WidgetState>, languageService: LanguageService) {
    if (!states) {
      return;
    }
    const statesSplit = this.splitWidgetStatesByType(states);
    states = states
      .map((state) => {
        return {
          ...state,
          name: statesSplit.getStateDisplayName(state.id, languageService),
        };
      })
      .sort((a, b) => a.order - b.order);
    return states;
  }

  public static generateCampaignImageArray(states: Array<WidgetState>, languageService: LanguageService) {
    const statesSplit = WidgetComponentHelpers.splitAndNameWidgetStatesByType(states, languageService);
    const imageArray = [];
    if (statesSplit.regulars && statesSplit.regulars.length > 0) {
      statesSplit.regulars.sort((a, b) => a.order - b.order);
      statesSplit.regulars
        .filter((state) => state.previewImageUrl)
        .forEach((state: WidgetState) =>
          imageArray.push({
            imageUrl: state.previewImageUrl,
            position: state.position,
          }),
        );
    }
    if (statesSplit.success && statesSplit.success.previewImageUrl) {
      imageArray.push({
        imageUrl: statesSplit.success.previewImageUrl,
        position: statesSplit.success.position,
      });
    }
    if (statesSplit.teaser && statesSplit.teaser.previewImageUrl) {
      imageArray.push({
        imageUrl: statesSplit.teaser.previewImageUrl,
        position: statesSplit.teaser.position,
      });
    }
    return imageArray;
  }

  public static generateWidgetComponentTextFieldName(widget: Widget, subType: WidgetComponentSubType): string {
    let fieldNamePrefix: string;
    switch (subType) {
      case WidgetComponentSubType.InputTextArea:
        fieldNamePrefix = 'Text';
        break;
      case WidgetComponentSubType.InputTextRegular:
        fieldNamePrefix = 'TextInput';
        break;
      case WidgetComponentSubType.ButtonListRadio:
        fieldNamePrefix = 'Radio';
        break;
      case WidgetComponentSubType.ButtonListChoices:
        fieldNamePrefix = 'Choices';
        break;
      case WidgetComponentSubType.ButtonListStarRating:
        fieldNamePrefix = 'StarRating';
        break;
      case WidgetComponentSubType.ButtonListCheckbox:
        fieldNamePrefix = 'CheckboxList';
        break;
      case WidgetComponentSubType.ButtonListSubscriptionCheckbox:
        fieldNamePrefix = 'SubscriptionCheckboxList';
        break;
      case WidgetComponentSubType.DateRegular:
        fieldNamePrefix = 'Date';
        break;
      case WidgetComponentSubType.DropdownRegular:
        fieldNamePrefix = 'Dropdown';
        break;
      case WidgetComponentSubType.DropdownCountry:
        fieldNamePrefix = 'Country';
        break;
      case WidgetComponentSubType.ButtonListSmileyRating:
        fieldNamePrefix = 'SmileyRating';
        break;
      case WidgetComponentSubType.ButtonListNumericalRating:
        fieldNamePrefix = 'NumericalRating';
        break;
      case WidgetComponentSubType.ButtonListNpsRating:
        fieldNamePrefix = 'NPS';
        break;
      case WidgetComponentSubType.ButtonListThumbRating:
        fieldNamePrefix = 'ThumbRating';
        break;
      case WidgetComponentSubType.ButtonListCsatStarRating:
        fieldNamePrefix = 'CsatStarRating';
        break;
      case WidgetComponentSubType.ButtonListCsatSmileyRating:
        fieldNamePrefix = 'CsatSmileyRating';
        break;
      case WidgetComponentSubType.ButtonListCsatNumericalRating:
        fieldNamePrefix = 'CsatNumericalRating';
        break;
      default:
        return undefined; // Sub type shouldn't have generated name
    }

    // Get existing fields by sub type with auto generated name
    let existingFields: WidgetComponent[] = [];
    widget.states.forEach((state) => {
      state.containers.forEach((container) => {
        container.components.forEach((component: WidgetComponent) => {
          if (component.subType === subType && !!component.name && component.name.indexOf(fieldNamePrefix) === 0) {
            existingFields.push(component);
          } else if (component.children) {
            const matchingChildren = component.children.filter(
              (child) => child.subType === subType && !!child.name && child.name.indexOf(fieldNamePrefix) === 0,
            );
            if (matchingChildren && matchingChildren.length > 0) {
              existingFields = existingFields.concat(matchingChildren);
            }
          }
        });
      });
    });

    let highestFieldNumber = 0;
    if (existingFields.length > 0) {
      // Order so highest number comes first
      existingFields = existingFields.sort((n1, n2) => {
        const componentNumber1 = this.getComponentFieldNumber(n1);
        const componentNumber2 = this.getComponentFieldNumber(n2);
        if (isNaN(componentNumber1) || componentNumber2 > componentNumber1) {
          return 1;
        }
        if (isNaN(componentNumber2) || componentNumber2 < componentNumber1) {
          return -1;
        }
        return 0;
      });

      highestFieldNumber = this.getComponentFieldNumber(existingFields[0]);
    }
    if (isNaN(highestFieldNumber)) {
      highestFieldNumber = 0;
    }

    // Set name combined with highest number of text area increased by 1
    return `${fieldNamePrefix}${highestFieldNumber + 1}`;
  }

  private static getComponentFieldNumber(component: WidgetComponent): number {
    // Remove everything (except number) and parse to typed number
    return Number(component.name.replace(/^\D+/g, ''));
  }

  private static getCurrentDefaultFontName(currentAccount: Account): string {
    if (currentAccount && currentAccount.defaultFontFamily) {
      return currentAccount.defaultFontFamily;
    }
    return this.getSupportedFontFamilies()[0].name;
  }

  // Finding widget component index just by id anywhere
  public static getWidgetComponentIndexById(id: number, widgetState: WidgetState) {
    if ((!id && !widgetState) || !widgetState.containers) {
      return undefined;
    }
    let foundIndex = undefined;
    widgetState.containers.forEach((container: WidgetContainer) => {
      if (container.components) {
        container.components.forEach((component: WidgetComponent, componentIndex: number) => {
          if (component.id === id) {
            foundIndex = componentIndex;
            return;
          } else if (component.children) {
            component.children.forEach((child: WidgetComponent, childIndex: number) => {
              if (child.id === id) {
                foundIndex = childIndex;
                return;
              }
            });
          }
        });
      }
    });
    if (!foundIndex && widgetState.floatingContainer && widgetState.floatingContainer.components) {
      widgetState.floatingContainer.components.forEach((component: WidgetComponent, componentIndex: number) => {
        if (component.id === id) {
          foundIndex = componentIndex;
          return;
        }
      });
    }
    return foundIndex;
  }

  public static emptyAllBackendTypePropertiesOnComponent(component: WidgetComponent): WidgetComponent {
    if (!component) {
      return component;
    }

    const objectKeys = Object.keys(component);
    for (const key of objectKeys) {
      if (component[key] && component[key]['$type']) {
        component[key]['$type'] = undefined;
      }
    }
    if (component.children) {
      component.children.forEach((child: WidgetComponent) => {
        const childKeys = Object.keys(child);
        for (const key of childKeys) {
          if (child[key] && child[key]['$type']) {
            child[key]['$type'] = undefined;
          }
        }
      });
    }
    return component;
  }

  public static getDeadlineFormats(languageService: LanguageService): Array<{ name: string; value: DeadlineFormat }> {
    return [
      {
        value: DeadlineFormat.DaysHoursMinutesSeconds,
        name: languageService.translateInstantly('campaign.widget.component.deadline.daysHoursMinutesSeconds'),
      },
      {
        value: DeadlineFormat.DaysHoursMinutes,
        name: languageService.translateInstantly('campaign.widget.component.deadline.daysHoursMinutes'),
      },
      {
        value: DeadlineFormat.HoursMinutesSeconds,
        name: languageService.translateInstantly('campaign.widget.component.deadline.hoursMinutesSeconds'),
      },
      {
        value: DeadlineFormat.MinutesSeconds,
        name: languageService.translateInstantly('campaign.widget.component.deadline.minutesSeconds'),
      },
      {
        value: DeadlineFormat.Days,
        name: languageService.translateInstantly('campaign.widget.component.deadline.days'),
      },
      {
        value: DeadlineFormat.Hours,
        name: languageService.translateInstantly('campaign.widget.component.deadline.hours'),
      },
    ] as Array<{ name: string; value: DeadlineFormat }>;
  }

  public static getButtonListFormats(
    languageService: LanguageService,
  ): Array<{ name: string; value: ButtonListFormat }> {
    return [
      {
        value: ButtonListFormat.IconAndText,
        name: languageService.translateInstantly('campaign.widget.sidebar.edit.radioButtons.iconAndText'),
      },
      {
        value: ButtonListFormat.IconOnly,
        name: languageService.translateInstantly('campaign.widget.sidebar.edit.radioButtons.iconOnly'),
      },
      {
        value: ButtonListFormat.TextOnly,
        name: languageService.translateInstantly('campaign.widget.sidebar.edit.radioButtons.textOnly'),
      },
    ] as Array<{ name: string; value: ButtonListFormat }>;
  }

  public static getDeadlineOccurrences(
    languageService: LanguageService,
  ): Array<{ name: string; value: DeadlineOccurrence }> {
    return [
      {
        value: DeadlineOccurrence.OnlyOnce,
        name: languageService.translateInstantly('campaign.widget.component.deadline.onlyOnce'),
      },
      {
        value: DeadlineOccurrence.EveryDay,
        name: languageService.translateInstantly('campaign.widget.component.deadline.everyDay'),
      },
    ] as Array<{ name: string; value: DeadlineOccurrence }>;
  }

  public static getDeadlineDurationUnits(
    languageService: LanguageService,
  ): Array<{ name: string; value: DeadlineDurationUnit }> {
    return [
      {
        value: DeadlineDurationUnit.Minutes,
        name: languageService.translateInstantly('campaign.widget.component.deadline.minutes'),
      },
      {
        value: DeadlineDurationUnit.Hours,
        name: languageService.translateInstantly('campaign.widget.component.deadline.hours'),
      },
      {
        value: DeadlineDurationUnit.Days,
        name: languageService.translateInstantly('campaign.widget.component.deadline.days'),
      },
    ];
  }

  public static getCountdownLengthInMs(properties: DeadlineComponentProperties): number {
    const unitInMs = {
      [DeadlineDurationUnit.Minutes]: 60000,
      [DeadlineDurationUnit.Hours]: 3600000,
      [DeadlineDurationUnit.Days]: 86400000,
    };
    return (properties?.duration ?? 0) * (unitInMs[properties?.durationUnit] ?? unitInMs[DeadlineDurationUnit.Hours]);
  }

  public static isCountdownShorterThanFormat(properties: DeadlineComponentProperties, format: DeadlineFormat): boolean {
    const smallestShownUnitInMs: Partial<Record<DeadlineFormat, number>> = {
      [DeadlineFormat.Days]: 86400000,
      [DeadlineFormat.Hours]: 3600000,
      [DeadlineFormat.DaysHoursMinutes]: 60000,
    };
    return WidgetComponentHelpers.getCountdownLengthInMs(properties) < (smallestShownUnitInMs[format] ?? 1000);
  }

  public static getItemDirections(languageService: LanguageService): Array<{ name: string; value: Direction }> {
    return [
      {
        value: Direction.Row,
        name: languageService.translateInstantly('generic.row'),
      },
      {
        value: Direction.Column,
        name: languageService.translateInstantly('generic.column'),
      },
    ] as Array<{ name: string; value: Direction }>;
  }

  // Returns null (not 'row') when unset, so old widgets keep their SCSS default direction.
  public static getCssFlexDirection(direction: Direction): string | null {
    switch (direction) {
      case Direction.Row:
        return 'row';
      case Direction.Column:
        return 'column';
      case Direction.RowReverse:
        return 'row-reverse';
      case Direction.ColumnReverse:
        return 'column-reverse';
      default:
        return null;
    }
  }

  public static getDeadlineDelimiters(): Array<{ name: string; value: string }> {
    return [
      { value: ':', name: ':' },
      { value: '-', name: '-' },
    ] as Array<{ name: string; value: string }>;
  }

  public static getLabelPositions(languageService: LanguageService): Array<{ name: string; value: LabelPosition }> {
    return [
      {
        value: LabelPosition.Above,
        name: languageService.translateInstantly('campaign.widget.sidebar.edit.labels.position-above'),
      },
      {
        value: LabelPosition.Below,
        name: languageService.translateInstantly('campaign.widget.sidebar.edit.labels.position-below'),
      },
      {
        value: LabelPosition.Left,
        name: languageService.translateInstantly('campaign.widget.sidebar.edit.labels.position-left'),
      },
      {
        value: LabelPosition.Right,
        name: languageService.translateInstantly('campaign.widget.sidebar.edit.labels.position-right'),
      } /*,
      {
        value: LabelPosition.AboveBox,
        name: languageService.translateInstantly('campaign.widget.sidebar.edit.labels.position-above-box')
      },
      {
        value: LabelPosition.BelowBox,
        name: languageService.translateInstantly('campaign.widget.sidebar.edit.labels.position-below-box')
      }*/,
    ] as Array<{ name: string; value: LabelPosition }>;
  }

  public static updateComponentsLayoutFromSimilarComponents(
    widgetComponent: WidgetComponent,
    widgetState: WidgetState,
    widget: Widget,
    languageService,
    addToGroup?: boolean,
  ): WidgetComponent {
    if (!widgetComponent || !widgetComponent.type || !widgetState) {
      return widgetComponent;
    }
    // Exceptions
    if (
      [
        WidgetComponentSubType.ButtonFloating,
        WidgetComponentSubType.TextFloating,
        WidgetComponentSubType.ImageFloating,
      ].indexOf(widgetComponent.subType) !== -1
    ) {
      return widgetComponent;
    }

    const allComponentsOnState = WidgetComponentHelpers.getAllComponentsOnState(
      widgetState,
      [WidgetComponentType.Button, WidgetComponentType.Text].indexOf(widgetComponent.type) !== -1,
    );
    const allComponentsOnOtherStates = WidgetComponentHelpers.getAllComponentsOnWidget(
      widget,
      widgetState.id,
      [WidgetComponentType.Button, WidgetComponentType.Text].indexOf(widgetComponent.type) !== -1,
    );
    let existingComponentsWithLabel = this.getExistingComponents(
      [
        WidgetComponentType.ButtonList,
        WidgetComponentType.InputText,
        WidgetComponentType.Dropdown,
        WidgetComponentType.Date,
        WidgetComponentType.InternationalPhone,
      ],
      allComponentsOnState,
      allComponentsOnOtherStates,
    );
    existingComponentsWithLabel = existingComponentsWithLabel.filter(
      (comp) => comp.parentComponentType !== WidgetComponentType.InternationalPhone,
    );
    if (!allComponentsOnState && !allComponentsOnOtherStates) {
      return widgetComponent;
    }
    if (
      [
        WidgetComponentType.InputText,
        WidgetComponentType.Dropdown,
        WidgetComponentType.Date,
        WidgetComponentType.ButtonList,
        WidgetComponentType.InternationalPhone,
      ].indexOf(widgetComponent.type) !== -1
    ) {
      widgetComponent = !existingComponentsWithLabel.length
        ? widgetComponent
        : this.updateLabelStylingFromExisting(
            widgetComponent,
            existingComponentsWithLabel[existingComponentsWithLabel.length - 1],
          );
    }
    if (widgetComponent.type === WidgetComponentType.Text) {
      if (widgetComponent.subType === WidgetComponentSubType.TextRegular) {
        const regularTexts = this.getExistingComponents(
          [WidgetComponentType.Text],
          allComponentsOnState,
          allComponentsOnOtherStates,
          [WidgetComponentSubType.TextRegular],
        );
        return regularTexts.length === 0
          ? widgetComponent
          : WidgetComponentHelpers.updateComponentsStylingFromExisting(
              widgetComponent,
              regularTexts[regularTexts.length - 1],
              languageService,
              widget,
              null,
            );
      } else if (widgetComponent.subType === WidgetComponentSubType.TextFloating) {
        const floatingTexts = this.getExistingComponents(
          [WidgetComponentType.Text],
          allComponentsOnState,
          allComponentsOnOtherStates,
          [WidgetComponentSubType.TextFloating],
        );
        return floatingTexts.length === 0
          ? widgetComponent
          : WidgetComponentHelpers.updateComponentsStylingFromExisting(
              widgetComponent,
              floatingTexts[floatingTexts.length - 1],
              languageService,
              widget,
              null,
            );
      }
    } else if (widgetComponent.type === WidgetComponentType.Button) {
      if (widgetComponent.subType === WidgetComponentSubType.ButtonHide) {
        const closeButtons = this.getExistingComponents(
          [WidgetComponentType.Button],
          allComponentsOnState,
          allComponentsOnOtherStates,
          [WidgetComponentSubType.ButtonHide],
        );
        return closeButtons.length === 0
          ? widgetComponent
          : WidgetComponentHelpers.updateCloseButtonStylingFromExisting(widgetComponent, closeButtons[0]);
      } else if (widgetComponent.subType === WidgetComponentSubType.ButtonInstagramFollow) {
        const instagramButtons = this.getExistingComponents(
          [WidgetComponentType.Button],
          allComponentsOnState,
          allComponentsOnOtherStates,
          [WidgetComponentSubType.ButtonInstagramFollow],
        );
        return instagramButtons.length === 0
          ? widgetComponent
          : WidgetComponentHelpers.updateInstagramButtonStylingFromExisting(widgetComponent, instagramButtons[0]);
      } else {
        const buttonComponents = this.getExistingComponents(
          [WidgetComponentType.Button],
          allComponentsOnState,
          allComponentsOnOtherStates,
          [WidgetComponentSubType.ButtonRegular, WidgetComponentSubType.ButtonSubmit],
        );
        return buttonComponents.length === 0
          ? widgetComponent
          : WidgetComponentHelpers.updateComponentsStylingFromExisting(
              widgetComponent,
              buttonComponents[buttonComponents.length - 1],
              languageService,
              widget,
              null,
            );
      }
    } else if (widgetComponent.type === WidgetComponentType.Group) {
      if (
        widgetComponent.children.length &&
        widgetComponent.children.every((child) => child.subType === WidgetComponentSubType.ButtonRegular)
      ) {
        const buttonComponents = this.getExistingComponents(
          [WidgetComponentType.Button],
          allComponentsOnState,
          allComponentsOnOtherStates,
          [WidgetComponentSubType.ButtonRegular, WidgetComponentSubType.ButtonSubmit],
        );
        return buttonComponents.length === 0
          ? widgetComponent
          : WidgetComponentHelpers.updateComponentInGroupStylingFromExisting(
              widgetComponent,
              buttonComponents[buttonComponents.length - 1],
              widget,
            );
      }
      if (
        widgetComponent.children.length &&
        widgetComponent.children.every((child) => child.type === WidgetComponentType.InputText)
      ) {
        const inputComponents = this.getExistingComponents(
          [
            WidgetComponentType.InputText,
            WidgetComponentType.Dropdown,
            WidgetComponentType.Date,
            WidgetComponentType.InternationalPhone,
          ],
          allComponentsOnState,
          allComponentsOnOtherStates,
        );
        if (!inputComponents.length && existingComponentsWithLabel.length) {
          widgetComponent.children.forEach(
            (child) =>
              (child = WidgetComponentHelpers.updateLabelStylingFromExisting(
                child,
                existingComponentsWithLabel[existingComponentsWithLabel.length - 1],
              )),
          );
          return widgetComponent;
        }
        return inputComponents.length === 0
          ? widgetComponent
          : WidgetComponentHelpers.updateComponentInGroupStylingFromExisting(
              widgetComponent,
              inputComponents[inputComponents.length - 1],
              widget,
              [
                WidgetComponentType.InputText,
                WidgetComponentType.Dropdown,
                WidgetComponentType.Date,
                WidgetComponentType.InternationalPhone,
              ],
              existingComponentsWithLabel[existingComponentsWithLabel.length - 1],
            );
      }
      if (
        widgetComponent.children.length &&
        widgetComponent.children.some((child) => child.type === WidgetComponentType.InputText) &&
        widgetComponent.children.some((child) => child.type === WidgetComponentType.Button)
      ) {
        const inputComponents = this.getExistingComponents(
          [
            WidgetComponentType.InputText,
            WidgetComponentType.Dropdown,
            WidgetComponentType.Date,
            WidgetComponentType.InternationalPhone,
          ],
          allComponentsOnState,
          allComponentsOnOtherStates,
        );
        const buttonComponents = this.getExistingComponents(
          [WidgetComponentType.Button],
          allComponentsOnState,
          allComponentsOnOtherStates,
          [WidgetComponentSubType.ButtonRegular, WidgetComponentSubType.ButtonSubmit],
        );
        const modifyButton =
          buttonComponents.length === 0
            ? widgetComponent
            : WidgetComponentHelpers.updateComponentInGroupStylingFromExisting(
                widgetComponent,
                buttonComponents[buttonComponents.length - 1],
                widget,
              );
        if (!inputComponents.length && existingComponentsWithLabel.length) {
          modifyButton.children.forEach(
            (child) =>
              (child =
                child.type === WidgetComponentType.InputText &&
                WidgetComponentHelpers.updateLabelStylingFromExisting(
                  child,
                  existingComponentsWithLabel[existingComponentsWithLabel.length - 1],
                )),
          );
        }
        const modifyInput =
          inputComponents.length === 0
            ? modifyButton
            : WidgetComponentHelpers.updateComponentInGroupStylingFromExisting(
                widgetComponent,
                inputComponents[inputComponents.length - 1],
                widget,
                [
                  WidgetComponentType.InputText,
                  WidgetComponentType.Dropdown,
                  WidgetComponentType.Date,
                  WidgetComponentType.InternationalPhone,
                ],
                existingComponentsWithLabel[existingComponentsWithLabel.length - 1],
              );
        return modifyInput;
      }
    } else if (widgetComponent.type === WidgetComponentType.DiscountCode) {
      if (widgetComponent.subType === WidgetComponentSubType.DiscountCodeRegular) {
        const discountComponents = this.getExistingComponents(
          [WidgetComponentType.DiscountCode],
          allComponentsOnState,
          allComponentsOnOtherStates,
          [WidgetComponentSubType.DiscountCodeRegular],
        );
        if (!discountComponents.length) {
          this.updateSeparateDiscountStylingFromExisting(
            widgetComponent,
            widget,
            allComponentsOnOtherStates,
            allComponentsOnState,
            languageService,
          );
        } else {
          return WidgetComponentHelpers.updateDiscountStylingFromExisting(
            widgetComponent,
            discountComponents[discountComponents.length - 1],
          );
        }
        return widgetComponent;
      }
    } else if (widgetComponent.type === WidgetComponentType.InternationalPhone) {
      const inputComponents = this.getExistingComponents(
        [
          WidgetComponentType.InternationalPhone,
          WidgetComponentType.InputText,
          WidgetComponentType.Dropdown,
          WidgetComponentType.Date,
        ],
        allComponentsOnState,
        allComponentsOnOtherStates,
      );
      if (!inputComponents.length) return widgetComponent;
      return WidgetComponentHelpers.updateIntlPhoneStylingFromExisting(
        widgetState,
        widgetComponent,
        inputComponents[inputComponents.length - 1],
      );
    } else if (widgetComponent.type === WidgetComponentType.Share) {
      if (widgetComponent.subType === WidgetComponentSubType.ShareCouponCode) {
        const discountComponents = this.getExistingComponents(
          [WidgetComponentType.Share],
          allComponentsOnState,
          allComponentsOnOtherStates,
          [WidgetComponentSubType.ShareCouponCode],
        );
        if (!discountComponents.length) {
          this.updateSeparateShareStylingFromExisting(
            widgetComponent,
            allComponentsOnOtherStates,
            allComponentsOnState,
            languageService,
            widget,
          );
        } else {
          return WidgetComponentHelpers.updateShareStylingFromExisting(
            widgetComponent,
            discountComponents[discountComponents.length - 1],
          );
        }
        return widgetComponent;
      }
    } else if (
      [
        WidgetComponentType.InputText,
        WidgetComponentType.Dropdown,
        WidgetComponentType.Date,
        WidgetComponentType.InternationalPhone,
      ].indexOf(widgetComponent.type) !== -1
    ) {
      const existingComponents = this.getExistingComponents(
        [
          WidgetComponentType.InputText,
          WidgetComponentType.Dropdown,
          WidgetComponentType.Date,
          WidgetComponentType.InternationalPhone,
        ],
        allComponentsOnState,
        allComponentsOnOtherStates,
      );
      const existingComponentLast = existingComponents.length
        ? existingComponents[existingComponents.length - 1]
        : null;
      if (
        existingComponentLast &&
        ((existingComponentLast.subType === 'InputTextArea' && widgetComponent.subType !== 'InputTextArea') ||
          (existingComponentLast.subType !== 'InputTextArea' && widgetComponent.subType === 'InputTextArea'))
      ) {
        const originalWidgetComponent = JSON.parse(JSON.stringify(widgetComponent));
        const onlyTextAreas = existingComponents.filter((comp) => comp.subType === 'InputTextArea');
        const notTextAreas = existingComponents.filter((comp) => comp.subType !== 'InputTextArea');
        const modifyComponent = WidgetComponentHelpers.updateComponentsStylingFromExisting(
          widgetComponent,
          existingComponents[existingComponents.length - 1],
          languageService,
          widget,
          null,
        );
        return WidgetComponentHelpers.updateInputWithDifTypeStylingFromExisting(
          originalWidgetComponent,
          modifyComponent,
          onlyTextAreas[onlyTextAreas.length - 1],
          notTextAreas[notTextAreas.length - 1],
        );
      } else {
        let modifyComponent =
          existingComponents.length === 0
            ? widgetComponent
            : WidgetComponentHelpers.updateComponentsStylingFromExisting(
                widgetComponent,
                existingComponents[existingComponents.length - 1],
                languageService,
                widget,
                addToGroup,
              );
        if (existingComponentLast?.parentComponentType === WidgetComponentType.InternationalPhone) {
          const intlPhone = existingComponents.filter(
            (comp) => comp.type === WidgetComponentType.InternationalPhone,
          )[0];
          modifyComponent = WidgetComponentHelpers.updateComponentsStylingFromExisting(
            modifyComponent || widgetComponent,
            {
              intlComp: intlPhone,
              inputComp: existingComponents.filter((comp) => {
                return (
                  comp?.parentComponentType !== WidgetComponentType.InternationalPhone &&
                  comp?.type !== WidgetComponentType.InternationalPhone
                );
              })[0],
            },
            languageService,
            widget,
            null,
          );
        }
        if (existingComponents.some((comp) => comp.type === WidgetComponentType.InternationalPhone)) {
          const compIntPhone = existingComponents.filter(
            (comp) => comp.type === WidgetComponentType.InternationalPhone,
          )[0];
          if (compIntPhone.children.some((child) => child.id === existingComponentLast.id)) {
            return WidgetComponentHelpers.updateIntlPhoneStylingFromExisting(
              widgetState,
              modifyComponent,
              compIntPhone,
            );
          }
        } else {
          return modifyComponent;
        }
      }
    } else if ([WidgetComponentType.ButtonList].indexOf(widgetComponent.type) !== -1) {
      let modifyComponent;
      const existingButtonLists = this.getExistingComponents(
        [WidgetComponentType.ButtonList],
        allComponentsOnState,
        allComponentsOnOtherStates,
        [widgetComponent.subType],
      );
      modifyComponent =
        existingButtonLists.length === 0
          ? widgetComponent
          : WidgetComponentHelpers.updateButtonListStylingFromExisting(widgetComponent, existingButtonLists, undefined);

      //ButtonListChoices inherits from form-component width, alignment, margin
      const existingForm = this.getExistingComponents(
        [
          WidgetComponentType.ButtonList,
          WidgetComponentType.InputText,
          WidgetComponentType.Dropdown,
          WidgetComponentType.Date,
          WidgetComponentType.InternationalPhone,
        ],
        allComponentsOnState,
        allComponentsOnOtherStates,
      );
      modifyComponent =
        existingForm.length === 0
          ? widgetComponent
          : WidgetComponentHelpers.updateButtonListStylingFromExisting(widgetComponent, existingForm, true);

      return modifyComponent;
    } else if (widgetComponent.subType === WidgetComponentSubType.DeadlineCountdown) {
      const countdowns = this.getExistingComponents(
        [WidgetComponentType.Deadline],
        allComponentsOnState,
        allComponentsOnOtherStates,
        [WidgetComponentSubType.DeadlineCountdown],
      );
      return countdowns.length === 0
        ? widgetComponent
        : WidgetComponentHelpers.updateDeadlineStylingFromExisting(widgetComponent, countdowns[countdowns.length - 1]);
    }
    return widgetComponent;
  }

  public static updateIntlPhoneStylingFromExisting(
    widgetState: WidgetState,
    componentToUpdate: WidgetComponent,
    existingComponent: WidgetComponent,
  ) {
    if (!componentToUpdate) return;
    const deepCopyOfExisting = JSON.parse(JSON.stringify(existingComponent));
    if (componentToUpdate.children) {
      const countryComponent = componentToUpdate.children.filter(
        (child) => child.type === WidgetComponentType.Dropdown,
      )[0];
      const phoneComponent = componentToUpdate.children.filter(
        (child) => child.type === WidgetComponentType.InputText,
      )[0];
      phoneComponent.textProperties = {
        ...deepCopyOfExisting.textProperties,
        text: phoneComponent.textProperties.text,
      };
      if (!countryComponent.label) countryComponent.label = {} as TextProperties;
      if (!phoneComponent.label) phoneComponent.label = {} as TextProperties;
      countryComponent.label = {
        ...deepCopyOfExisting.label,
        text: countryComponent.label.text,
      };
      phoneComponent.label = {
        ...deepCopyOfExisting.label,
        text: phoneComponent.label.text,
      };
      if (deepCopyOfExisting.iconUrl && deepCopyOfExisting.iconPosition) {
        phoneComponent.iconPosition = deepCopyOfExisting.iconPosition;
        phoneComponent.iconSize = deepCopyOfExisting.iconSize || phoneComponent.iconSize;
      }
      phoneComponent.alignment = deepCopyOfExisting.alignment;
      phoneComponent.height = deepCopyOfExisting.height;
      countryComponent.height = deepCopyOfExisting.height;
    }

    if (!componentToUpdate.label) componentToUpdate.label = {} as TextProperties;
    if (!deepCopyOfExisting.label?.text) {
      componentToUpdate.label.text = '';
    }
    if (deepCopyOfExisting.label) {
      componentToUpdate.label = { ...deepCopyOfExisting.label, text: componentToUpdate.label?.text };
    }
    if (existingComponent.children) {
      const countryComponent = existingComponent.children.filter(
        (child) => child.type === WidgetComponentType.Dropdown,
      )[0];
      const phoneComponent = existingComponent.children.filter(
        (child) => child.type === WidgetComponentType.InputText,
      )[0];
      if (phoneComponent.height || countryComponent.height) {
        componentToUpdate.height =
          Math.max(
            this.extractNumberFromPixelString(phoneComponent.height),
            this.extractNumberFromPixelString(countryComponent.height),
          ) + 'px';
      }
    } else if (deepCopyOfExisting.height && componentToUpdate.type === WidgetComponentType.InternationalPhone) {
      componentToUpdate.height = deepCopyOfExisting.height;
    }
    let width = deepCopyOfExisting.width as string;
    if (existingComponent.parentId) {
      const parent = WidgetComponentsService.getComponentById(
        widgetState,
        existingComponent.parentId,
      ) as WidgetComponent;
      if (parent) {
        width = parent.width;
      }
    }
    if (existingComponent) componentToUpdate.width = width;
    componentToUpdate.border = deepCopyOfExisting.border;
    componentToUpdate.radius = deepCopyOfExisting.radius;
    componentToUpdate.margin = deepCopyOfExisting.margin;
    componentToUpdate.background = deepCopyOfExisting.background;
    this.setBorderIfInheritFromEmailAndSubmitComp(deepCopyOfExisting, componentToUpdate);
    return componentToUpdate;
  }

  public static updateLabelStylingFromExisting(componentToUpdate: any, existingComponent: any): any {
    if (!existingComponent) {
      return;
    }
    const deepCopyOfExisting = JSON.parse(JSON.stringify(existingComponent));
    if (deepCopyOfExisting.label && deepCopyOfExisting.parentComponentType !== WidgetComponentType.InternationalPhone) {
      if (!componentToUpdate.label) {
        componentToUpdate.label = {};
      }
      if (!deepCopyOfExisting.label?.text) {
        componentToUpdate.label.text = '';
      }
      componentToUpdate.label = {
        ...deepCopyOfExisting.label,
        text: componentToUpdate.label.text,
      };
    } else if (
      !deepCopyOfExisting.label &&
      deepCopyOfExisting.parentComponentType !== WidgetComponentType.InternationalPhone
    ) {
      componentToUpdate.label = deepCopyOfExisting.label;
    }
    return componentToUpdate;
  }

  public static getExistingComponents(
    types: Array<WidgetComponentType>,
    componentsOnState: Array<any>,
    componentsOnOtherStates?: Array<any>,
    subTypes?: Array<WidgetComponentSubType>,
  ) {
    let foundComponents = this.getAllComponentsOfTypes(types, componentsOnState);
    if (subTypes && foundComponents && foundComponents.length > 0) {
      foundComponents = foundComponents.filter((c) => subTypes.indexOf(c.subType) !== -1);
    }
    if (foundComponents.length === 0) {
      foundComponents = this.getAllComponentsOfTypes(types, componentsOnOtherStates);
      if (subTypes && foundComponents && foundComponents.length > 0) {
        foundComponents = foundComponents.filter((c) => subTypes.indexOf(c.subType) !== -1);
      }
    }
    return foundComponents;
  }

  private static getExistingCompButtonListForStyleUpdate(
    componentToUpdate: any,
    existingComponents: Array<any>,
  ): { existingComponent: any; deepCopyOfExisting: any; deepCopyOfExistingNotChild: any } {
    const sameSubtypeComponents = existingComponents.filter((c) => c.subType === componentToUpdate.subType);
    const existingComponent =
      sameSubtypeComponents.length > 0
        ? sameSubtypeComponents[sameSubtypeComponents.length - 1]
        : existingComponents[existingComponents.length - 1];
    const deepCopyOfExisting = JSON.parse(JSON.stringify(existingComponent));
    let deepCopyOfExistingNotChild;
    if (existingComponent.parentId) {
      const sameSubtypeCompNotChild = sameSubtypeComponents.filter((comp) => !comp.parentId);
      const existingCompNotChild = existingComponents.filter((comp) => !comp.parentId);
      const compNotChild = sameSubtypeCompNotChild.length
        ? sameSubtypeCompNotChild[sameSubtypeCompNotChild.length - 1]
        : existingCompNotChild.length
          ? existingCompNotChild[existingCompNotChild.length - 1]
          : null;
      if (compNotChild) deepCopyOfExistingNotChild = JSON.parse(JSON.stringify(compNotChild));
    }
    return { existingComponent, deepCopyOfExisting, deepCopyOfExistingNotChild };
  }

  public static updateButtonListStylingFromExisting(
    componentToUpdate: any,
    existingComponents: Array<any>,
    isInheritingFromForm,
    isApplyToAll?,
  ): any {
    if (!existingComponents) {
      return componentToUpdate;
    }
    let { existingComponent, deepCopyOfExisting, deepCopyOfExistingNotChild } =
      WidgetComponentHelpers.getExistingCompButtonListForStyleUpdate(componentToUpdate, existingComponents);
    if (!isInheritingFromForm) {
      if (deepCopyOfExisting.textProperties) {
        const newText = componentToUpdate.textProperties.text;
        componentToUpdate.textProperties = deepCopyOfExisting.textProperties;
        componentToUpdate.textProperties.text = newText ? '' + newText : undefined;
        if (deepCopyOfExisting.textProperties.color) {
          componentToUpdate.buttonListProperties.checkedFontColor = deepCopyOfExisting.textProperties.color;
          componentToUpdate.buttonListProperties.uncheckedFontColor = deepCopyOfExisting.textProperties.color;
        }
      }
      componentToUpdate.buttonListProperties = {
        ...deepCopyOfExisting.buttonListProperties,
        fillPerRow: componentToUpdate.buttonListProperties.fillPerRow,
        labelTextMaximum: componentToUpdate.buttonListProperties.labelTextMaximum,
        labelTextMinimum: componentToUpdate.buttonListProperties.labelTextMinimum,
      };
      componentToUpdate.iconUrl = deepCopyOfExisting.iconUrl;
      componentToUpdate.background = deepCopyOfExisting.background;
      componentToUpdate.margin = deepCopyOfExisting.margin;
      componentToUpdate.padding = deepCopyOfExisting.padding;

      componentToUpdate.radius = deepCopyOfExisting.radius;
      if (!componentToUpdate.mobile) {
        componentToUpdate.mobile = {};
      }
      componentToUpdate.mobile = deepCopyOfExisting.mobile;
      if (!componentToUpdate.parentId && deepCopyOfExistingNotChild) {
        componentToUpdate.width = deepCopyOfExistingNotChild.width;
      }
      componentToUpdate.border = deepCopyOfExisting.border;
      if (deepCopyOfExisting.actionType !== ButtonActionType.SwitchState) {
        componentToUpdate.actionType = deepCopyOfExisting.actionType;
        componentToUpdate.actionValue = deepCopyOfExisting.actionValue;
      }
      if (
        this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListRadio) ||
        this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListCheckbox)
      ) {
        componentToUpdate.buttonListProperties.iconColor = deepCopyOfExisting.buttonListProperties.iconColor;
      }

      if (
        this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListStarRating) ||
        this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListSmileyRating) ||
        this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListNumericalRating) ||
        this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListThumbRating) ||
        this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListNpsRating) ||
        this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListCsatNumericalRating) ||
        this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListCsatSmileyRating) ||
        this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListCsatStarRating)
      ) {
        componentToUpdate.buttonListProperties.labelType = deepCopyOfExisting.buttonListProperties.labelType;
        componentToUpdate.buttonListProperties.checkedFontColor =
          deepCopyOfExisting.buttonListProperties.checkedFontColor;
        componentToUpdate.buttonListProperties.uncheckedFontColor =
          deepCopyOfExisting.buttonListProperties.uncheckedFontColor;
      }
    }
    if (isInheritingFromForm) {
      if (existingComponent?.parentComponentType === WidgetComponentType.InternationalPhone) {
        existingComponent = existingComponents.filter(
          (comp) => comp.type === WidgetComponentType.InternationalPhone,
        )[0];
        if (componentToUpdate) deepCopyOfExisting = JSON.parse(JSON.stringify(existingComponent));
      }
      componentToUpdate.margin = deepCopyOfExisting?.margin;
      if (!componentToUpdate.parentId && deepCopyOfExistingNotChild) {
        componentToUpdate.width = deepCopyOfExistingNotChild.width;
      }
      componentToUpdate.alignment = deepCopyOfExisting?.alignment;
    }
    if (isApplyToAll) {
      WidgetComponentHelpers.updateLabelStylingFromExisting(componentToUpdate, existingComponents[0]);
      componentToUpdate.buttonListProperties.fillPerRow = deepCopyOfExisting.buttonListProperties.fillPerRow;
    }
    return componentToUpdate;
  }

  public static updateButtonListSingleStyleFromExisting(
    componentToUpdate: any,
    existingComponents: Array<any>,
    diiferencesInStyle,
  ): any {
    if (!existingComponents) {
      return componentToUpdate;
    }
    const hasDiff = diiferencesInStyle?.hasDifferences && diiferencesInStyle.differences?.length > 0;
    if (!hasDiff) return componentToUpdate;
    const { deepCopyOfExisting, deepCopyOfExistingNotChild } =
      WidgetComponentHelpers.getExistingCompButtonListForStyleUpdate(componentToUpdate, existingComponents);
    diiferencesInStyle.differences.forEach((dif) => {
      const prop = dif.path.split('.')[0];
      const innerProp = dif.path.split('.')[1];
      if (prop === 'textProperties' && innerProp !== 'text') {
        WidgetComponentHelpers.copyProp(componentToUpdate, prop, innerProp, deepCopyOfExisting);
        if (innerProp === 'color') {
          componentToUpdate.buttonListProperties.checkedFontColor = deepCopyOfExisting.textProperties.color;
          componentToUpdate.buttonListProperties.uncheckedFontColor = deepCopyOfExisting.textProperties.color;
        }
      }
      if (prop === 'buttonListProperties' && innerProp !== 'labelTextMaximum' && innerProp !== 'labelTextMinimum') {
        WidgetComponentHelpers.copyProp(componentToUpdate, prop, innerProp, deepCopyOfExisting);
      }
      if (
        prop === 'iconUrl' ||
        prop === 'background' ||
        prop === 'margin' ||
        prop === 'padding' ||
        prop === 'radius' ||
        prop === 'border' ||
        (prop === 'actionType' && deepCopyOfExisting.actionType !== ButtonActionType.SwitchState) ||
        (prop === 'actionValue' && deepCopyOfExisting.actionType !== ButtonActionType.SwitchState)
      ) {
        WidgetComponentHelpers.copyProp(componentToUpdate, prop, innerProp, deepCopyOfExisting);
      }
      if (prop === 'mobile') {
        WidgetComponentHelpers.copyProp(componentToUpdate, prop, innerProp, deepCopyOfExisting);
      }
      if (!componentToUpdate.parentId && deepCopyOfExistingNotChild && prop === 'width') {
        componentToUpdate.width = deepCopyOfExistingNotChild.width;
      }
      if (
        (this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListRadio) ||
          this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListCheckbox)) &&
        prop === 'buttonListProperties' &&
        innerProp === 'iconColor'
      ) {
        WidgetComponentHelpers.copyProp(componentToUpdate, prop, innerProp, deepCopyOfExisting);
      }

      if (
        (this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListStarRating) ||
          this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListSmileyRating) ||
          this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListNumericalRating) ||
          this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListThumbRating) ||
          this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListNpsRating) ||
          this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListCsatNumericalRating) ||
          this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListCsatSmileyRating) ||
          this.isSubType(componentToUpdate.subType, WidgetComponentSubType.ButtonListCsatStarRating)) &&
        prop === 'buttonListProperties' &&
        (innerProp === 'labelType' || innerProp === 'checkedFontColor' || innerProp === 'uncheckedFontColor')
      ) {
        WidgetComponentHelpers.copyProp(componentToUpdate, prop, innerProp, deepCopyOfExisting);
      }
    });
    WidgetComponentHelpers.updateLabelStylingFromExisting(componentToUpdate, existingComponents[0]);
    return componentToUpdate;
  }

  private static updateInstagramButtonStylingFromExisting(componentToUpdate: any, existingComponent: any): any {
    const deepCopyOfExisting = JSON.parse(JSON.stringify(existingComponent));
    if (deepCopyOfExisting.textProperties) {
      const newText = componentToUpdate.textProperties.text;
      const newColor = componentToUpdate.textProperties.color;
      componentToUpdate.textProperties = deepCopyOfExisting.textProperties;
      componentToUpdate.textProperties.text = newText ? '' + newText : undefined;
      componentToUpdate.textProperties.color = newColor ? '' + newColor : undefined;
    }
    componentToUpdate.margin = deepCopyOfExisting.margin;
    componentToUpdate.height = deepCopyOfExisting.height;
    componentToUpdate.alignment = deepCopyOfExisting.alignment;
    return componentToUpdate;
  }

  public static updateCloseButtonStylingFromExisting(componentToUpdate: any, existingComponent: any): any {
    const deepCopyOfExisting = JSON.parse(JSON.stringify(existingComponent));
    if (deepCopyOfExisting.textProperties) {
      const newText = componentToUpdate.textProperties.text;
      const newColor = componentToUpdate.textProperties.color;
      componentToUpdate.textProperties = deepCopyOfExisting.textProperties;
      componentToUpdate.textProperties.text = newText ? '' + newText : undefined;
      componentToUpdate.textProperties.color = newColor ? '' + newColor : undefined;
    }

    componentToUpdate.positionX = deepCopyOfExisting.positionX;
    componentToUpdate.positionY = deepCopyOfExisting.positionY;
    componentToUpdate.positionCorner = deepCopyOfExisting.positionCorner;
    componentToUpdate.background = deepCopyOfExisting.background;
    componentToUpdate.actionScript = deepCopyOfExisting.actionScript;
    componentToUpdate.border = deepCopyOfExisting.border;
    componentToUpdate.radius = deepCopyOfExisting.radius;
    componentToUpdate.padding = deepCopyOfExisting.padding;
    componentToUpdate.shadow = deepCopyOfExisting.shadow;
    componentToUpdate.iconUrl = deepCopyOfExisting.iconUrl;
    if (componentToUpdate.iconUrl) {
      componentToUpdate.iconColor = deepCopyOfExisting.iconColor;
    }
    componentToUpdate.margin = deepCopyOfExisting.margin;
    componentToUpdate.height = deepCopyOfExisting.height;
    componentToUpdate.width = deepCopyOfExisting.width;
    componentToUpdate.alignment = deepCopyOfExisting.alignment;
    return componentToUpdate;
  }

  public static updateCloseButtonSingleStyleFromExisting(
    componentToUpdate: any,
    existingComponent: any,
    diiferencesInStyle,
  ): any {
    const hasDiff = diiferencesInStyle?.hasDifferences && diiferencesInStyle.differences?.length > 0;
    if (!hasDiff) return componentToUpdate;
    const deepCopyOfExisting = JSON.parse(JSON.stringify(existingComponent));

    diiferencesInStyle.differences.forEach((dif) => {
      const prop = dif.path.split('.')[0];
      const prop_2 = dif.path.split('.')[1];
      if (
        prop === 'positionX' ||
        prop === 'positionY' ||
        prop === 'positionCorner' ||
        prop === 'background' ||
        prop === 'actionScript' ||
        prop === 'radius' ||
        prop === 'padding' ||
        prop === 'iconUrl' ||
        prop === 'margin' ||
        prop === 'height' ||
        prop === 'width' ||
        prop === 'border' ||
        prop === 'shadow'
      ) {
        WidgetComponentHelpers.copyProp(componentToUpdate, prop, prop_2, deepCopyOfExisting);
      }
    });
    return componentToUpdate;
  }

  public static updateComponentsStylingFromExisting(
    componentToUpdate: any,
    existingComponent: any,
    languageService,
    widget,
    addToGroup,
    type?,
  ): any {
    const deepCopyOfExisting = JSON.parse(
      JSON.stringify(existingComponent.intlComp ? existingComponent.intlComp : existingComponent),
    );
    const deepCopyOfExistingInputComp = existingComponent.inputComp
      ? JSON.parse(JSON.stringify(existingComponent.inputComp))
      : null;
    if (deepCopyOfExisting.textProperties) {
      const newText = componentToUpdate.textProperties?.text;
      componentToUpdate.textProperties = deepCopyOfExisting.textProperties;
      componentToUpdate.textProperties.text = newText ? '' + newText : undefined;
      if (!componentToUpdate.textProperties.fontSize && !widget.brandVariables) {
        componentToUpdate.textProperties.fontSize = '14px';
      }
    }
    this.setIconColor(componentToUpdate, deepCopyOfExisting);
    this.setDefaultTextAndIconOnInput(deepCopyOfExisting, existingComponent, componentToUpdate, languageService);
    if (
      componentToUpdate.parentComponentType === WidgetComponentType.InternationalPhone ||
      deepCopyOfExisting.parentComponentType === WidgetComponentType.InternationalPhone
    ) {
      return componentToUpdate;
    }
    componentToUpdate.radius = deepCopyOfExisting.radius;
    if (
      componentToUpdate.subtype !== WidgetComponentSubType.InputTextArea &&
      type !== WidgetComponentType.DiscountCode
    ) {
      componentToUpdate.height = deepCopyOfExisting.height;
    }
    if (deepCopyOfExisting.mobile) {
      // A discount-code child is sized by its share of the parent row, which a foreign mobile width breaks.
      if (type === WidgetComponentType.DiscountCode) {
        componentToUpdate.mobile = {
          ...componentToUpdate.mobile,
          fontSize: deepCopyOfExisting.mobile?.fontSize || componentToUpdate.mobile?.fontSize,
          lineHeight:
            deepCopyOfExisting.mobile?.fontSize && deepCopyOfExisting.mobile?.lineHeight
              ? deepCopyOfExisting.mobile?.lineHeight
              : componentToUpdate.mobile?.lineHeight,
        };
      } else {
        componentToUpdate.mobile = deepCopyOfExisting.mobile;
      }
    }
    if (componentToUpdate.type === WidgetComponentType.Button) {
      componentToUpdate.hover = deepCopyOfExisting.hover;
      componentToUpdate.animation = deepCopyOfExisting.animation;
    }
    if (type !== WidgetComponentType.DiscountCode) {
      if (deepCopyOfExistingInputComp) {
        componentToUpdate.padding = deepCopyOfExistingInputComp.padding;
        componentToUpdate.margin = deepCopyOfExistingInputComp.margin;
      } else if (deepCopyOfExisting.type !== WidgetComponentType.InternationalPhone) {
        componentToUpdate.padding = deepCopyOfExisting.padding;
        componentToUpdate.margin = deepCopyOfExisting.margin;
        if (
          deepCopyOfExisting.parentComponentType === WidgetComponentType.Group &&
          deepCopyOfExisting.margin?.left &&
          !addToGroup
        ) {
          componentToUpdate.margin.left = '0px';
        }
      }
      componentToUpdate.border = deepCopyOfExisting.border;
      componentToUpdate.background = deepCopyOfExisting.background;
      if (componentToUpdate.subType == WidgetComponentSubType.DropdownCountry && !componentToUpdate.background?.color) {
        if (!componentToUpdate.background) componentToUpdate.background = {};
        componentToUpdate.background.color = '#ffffff';
      }
      componentToUpdate.shadow = deepCopyOfExisting.shadow;
      //we should set the entire border, not just three sides
      this.setBorderIfInheritFromEmailAndSubmitComp(deepCopyOfExisting, componentToUpdate);
    } else {
      if (componentToUpdate.type === WidgetComponentType.Button) {
        componentToUpdate.border = deepCopyOfExisting.border;
        componentToUpdate.background = deepCopyOfExisting.background;
        componentToUpdate.padding = deepCopyOfExisting.padding;
        componentToUpdate.height = deepCopyOfExisting.height;
      }
    }
    if (!componentToUpdate.parentId && !existingComponent.parentId && type !== WidgetComponentType.DiscountCode) {
      componentToUpdate.width = deepCopyOfExisting.width;
    }
    if (componentToUpdate.type === WidgetComponentType.ButtonList) {
      componentToUpdate.buttonListProperties = deepCopyOfExisting.buttonListProperties;
    }
    componentToUpdate.alignment = deepCopyOfExisting.alignment;

    // check if inputField have different size
    if (
      deepCopyOfExisting.type === WidgetComponentType.InputText ||
      deepCopyOfExisting.type === WidgetComponentType.Dropdown ||
      deepCopyOfExisting.type === WidgetComponentType.Date
    ) {
      let heightExistingComponent = this.getHeightInputField(deepCopyOfExisting);
      if (heightExistingComponent < parseInt(deepCopyOfExisting.height)) {
        heightExistingComponent = parseInt(deepCopyOfExisting.height);
      }
      let heightNewComponent = this.getHeightInputField(componentToUpdate);
      if (heightNewComponent < parseInt(deepCopyOfExisting.height)) {
        heightNewComponent = parseInt(deepCopyOfExisting.height);
      }
      componentToUpdate = this.setSameHeightInputField(
        componentToUpdate,
        heightNewComponent,
        heightExistingComponent,
        deepCopyOfExisting,
        widget,
      );
    }

    return componentToUpdate;
  }

  public static updateComponentsSingleStyleFromExisting(
    componentToUpdate: any,
    existingComponent: any,
    languageService,
    widget,
    diiferencesInStyle?,
  ): any {
    const hasDiff = diiferencesInStyle?.hasDifferences && diiferencesInStyle.differences?.length > 0;
    if (!hasDiff) return componentToUpdate;
    const deepCopyOfExisting = JSON.parse(
      JSON.stringify(existingComponent.intlComp ? existingComponent.intlComp : existingComponent),
    );
    const deepCopyOfExistingInputComp = existingComponent.inputComp
      ? JSON.parse(JSON.stringify(existingComponent.inputComp))
      : null;
    if (
      componentToUpdate.parentComponentType === WidgetComponentType.InternationalPhone ||
      deepCopyOfExisting.parentComponentType === WidgetComponentType.InternationalPhone
    ) {
      return componentToUpdate;
    }

    diiferencesInStyle.differences.forEach((dif) => {
      const prop = dif.path.split('.')[0];
      const innerProp = dif.path.split('.')[1];
      if ((prop === 'textProperties' && innerProp !== 'text') || prop === 'radius') {
        WidgetComponentHelpers.copyProp(componentToUpdate, prop, innerProp, deepCopyOfExisting);
      }
      if (prop === 'iconUrl' || prop === 'iconColor') {
        this.setIconColor(componentToUpdate, deepCopyOfExisting);
      }
      if (componentToUpdate.subtype !== WidgetComponentSubType.InputTextArea && prop === 'height') {
        componentToUpdate.height = deepCopyOfExisting.height;
      }
      if (prop === 'mobile') {
        WidgetComponentHelpers.copyProp(componentToUpdate, prop, innerProp, deepCopyOfExisting);
      }
      if (componentToUpdate.type === WidgetComponentType.Button) {
        if (prop === 'hover' || prop === 'animation') {
          WidgetComponentHelpers.copyProp(componentToUpdate, prop, innerProp, deepCopyOfExisting);
        }
      }

      if (prop === 'border' && componentToUpdate.type === WidgetComponentType.Button) {
        WidgetComponentHelpers.copyProp(componentToUpdate, prop, innerProp, deepCopyOfExisting);
      }
      if (prop === 'background' && componentToUpdate.type === WidgetComponentType.Button) {
        WidgetComponentHelpers.copyProp(componentToUpdate, prop, innerProp, deepCopyOfExisting);
        if (
          componentToUpdate.subType == WidgetComponentSubType.DropdownCountry &&
          !componentToUpdate.background?.color &&
          prop === 'background' &&
          innerProp === 'color'
        ) {
          if (!componentToUpdate.background) componentToUpdate.background = {};
          componentToUpdate.background.color = '#ffffff';
        }
      }
      if (prop === 'background' && componentToUpdate.type === WidgetComponentType.InputText) {
        WidgetComponentHelpers.copyProp(componentToUpdate, prop, innerProp, deepCopyOfExisting);
      }

      if (deepCopyOfExistingInputComp) {
        if (prop === 'padding' || prop === 'margin') {
          WidgetComponentHelpers.copyProp(componentToUpdate, prop, innerProp, deepCopyOfExistingInputComp);
        }
      } else if (deepCopyOfExisting.type !== WidgetComponentType.InternationalPhone) {
        if (prop === 'padding') {
          WidgetComponentHelpers.copyProp(componentToUpdate, prop, innerProp, deepCopyOfExisting);
        }
        if (prop === 'margin') {
          WidgetComponentHelpers.copyProp(componentToUpdate, prop, innerProp, deepCopyOfExisting);
          if (deepCopyOfExisting.parentComponentType === WidgetComponentType.Group && deepCopyOfExisting.margin?.left) {
            componentToUpdate.margin.left = '0px';
          }
        }
      }
      if (prop === 'shadow') {
        WidgetComponentHelpers.copyProp(componentToUpdate, prop, innerProp, deepCopyOfExisting);
      }
      if (!componentToUpdate.parentId && !existingComponent.parentId && prop === 'width') {
        WidgetComponentHelpers.copyProp(componentToUpdate, prop, innerProp, deepCopyOfExisting);
      }
      if (prop === 'alignment') {
        WidgetComponentHelpers.copyProp(componentToUpdate, prop, innerProp, deepCopyOfExisting);
      }
      // check if inputField have different size
      if (prop === 'height') {
        if (
          deepCopyOfExisting.type === WidgetComponentType.InputText ||
          deepCopyOfExisting.type === WidgetComponentType.Dropdown ||
          deepCopyOfExisting.type === WidgetComponentType.Date
        ) {
          let heightExistingComponent = this.getHeightInputField(deepCopyOfExisting);
          if (heightExistingComponent < parseInt(deepCopyOfExisting.height)) {
            heightExistingComponent = parseInt(deepCopyOfExisting.height);
          }
          let heightNewComponent = this.getHeightInputField(componentToUpdate);
          if (heightNewComponent < parseInt(deepCopyOfExisting.height)) {
            heightNewComponent = parseInt(deepCopyOfExisting.height);
          }
          componentToUpdate = this.setSameHeightInputField(
            componentToUpdate,
            heightNewComponent,
            heightExistingComponent,
            deepCopyOfExisting,
            widget,
          );
        }
      }
    });
    return componentToUpdate;
  }

  private static copyProp(target: any, prop: string, prop2: string, source: any): void {
    if (!prop) return;
    if (!target[prop]) target[prop] = {};
    if (!prop2) {
      target[prop] = source[prop];
      return;
    }
    target[prop][prop2] = source[prop]?.[prop2];
  }

  static setIconColor(componentToUpdate, deepCopyOfExisting) {
    if (
      deepCopyOfExisting.iconUrl?.startsWith('$icon/material/') &&
      componentToUpdate.iconUrl?.startsWith('$icon/material/') &&
      deepCopyOfExisting.iconUrl.includes('?')
    ) {
      const splitedIconUrlExisting = deepCopyOfExisting.iconUrl.split('?');
      let splitedIconUrlComponentToUpdate;
      if (componentToUpdate.iconUrl.includes('?')) {
        splitedIconUrlComponentToUpdate = componentToUpdate.iconUrl.split('?');
        componentToUpdate.iconUrl = splitedIconUrlComponentToUpdate[0] + '?' + splitedIconUrlExisting[1];
      } else {
        componentToUpdate.iconUrl = componentToUpdate.iconUrl + '?' + splitedIconUrlExisting[1];
      }
    }
    //exception for Pnone component with default svg icon untill we find the appropriate material icon
    if (
      componentToUpdate.subType === WidgetComponentSubType.InputTextPhone &&
      componentToUpdate.iconUrl &&
      !componentToUpdate.iconUrl?.startsWith('$icon/material/') &&
      deepCopyOfExisting.iconUrl?.startsWith('$icon/material/') &&
      deepCopyOfExisting.iconUrl.includes('?')
    ) {
      const splitedIconUrlExisting = deepCopyOfExisting.iconUrl.split('?');
      const color = splitedIconUrlExisting[1].split('=')[1];
      componentToUpdate.iconColor = color.startsWith('{') ? color : '#' + color;
    }
    if (
      deepCopyOfExisting.subType === WidgetComponentSubType.InputTextPhone &&
      deepCopyOfExisting.iconUrl &&
      deepCopyOfExisting.iconColor &&
      !deepCopyOfExisting.iconUrl?.startsWith('$icon/material/') &&
      componentToUpdate.iconUrl?.startsWith('$icon/material/')
    ) {
      const color = deepCopyOfExisting.iconColor.startsWith('{')
        ? deepCopyOfExisting.iconColor
        : deepCopyOfExisting.iconColor.split('#')[1];
      if (componentToUpdate.iconUrl.includes('?')) {
        const splitedIconUrlComponentToUpdate = componentToUpdate.iconUrl.split('?');
        componentToUpdate.iconUrl = splitedIconUrlComponentToUpdate[0] + '?color=' + color;
      } else {
        componentToUpdate.iconUrl = componentToUpdate.iconUrl + '?color=' + color;
      }
    }
    //exception ends
  }

  public static setDefaultTextAndIconOnInput(
    deepCopyOfExisting,
    existingComponent,
    componentToUpdate,
    languageService,
  ): void {
    if (
      deepCopyOfExisting.type === WidgetComponentType.InputText ||
      deepCopyOfExisting.type === WidgetComponentType.Dropdown ||
      deepCopyOfExisting.type === WidgetComponentType.Date
    ) {
      if (existingComponent.type === WidgetComponentType.InputText) {
        if (existingComponent.textProperties.text?.trim()) {
          // componentToUpdate.textProperties.text = languageService.translateInstantly('campaign.widget.component.input-text-email.placeholder');
          switch (componentToUpdate.subType) {
            case WidgetComponentSubType.InputTextEmail:
              componentToUpdate.textProperties.text = languageService.translateInstantly(
                'campaign.widget.component.input-text-email.placeholder',
              );
              break;
            case WidgetComponentSubType.InputTextFirstName:
              componentToUpdate.textProperties.text = languageService.translateInstantly(
                'campaign.widget.component.input-text-name.first-name-placeholder',
              );
              break;
            case WidgetComponentSubType.InputTextLastName:
              componentToUpdate.textProperties.text = languageService.translateInstantly(
                'campaign.widget.component.input-text-name.last-name-placeholder',
              );
              break;
            case WidgetComponentSubType.InputTextName:
              componentToUpdate.textProperties.text = languageService.translateInstantly(
                'campaign.widget.component.input-text-name.placeholder',
              );
              break;
            case WidgetComponentSubType.InputTextPhone:
              componentToUpdate.textProperties.text = languageService.translateInstantly(
                'campaign.widget.component.input-text-phone.placeholder',
              );
              break;
          }
        }
        if (!existingComponent.iconUrl && componentToUpdate.subType !== WidgetComponentSubType.DropdownRegular) {
          componentToUpdate.iconUrl = null;
        } else {
          switch (componentToUpdate.subtype) {
            case WidgetComponentSubType.InputTextEmail:
              componentToUpdate.iconUrl = '$icon/material/mail';
              break;
            case WidgetComponentSubType.InputTextPassword:
              componentToUpdate.iconUrl = '$icon/material/lock';
              break;
            case WidgetComponentSubType.InputTextFirstName:
              componentToUpdate.iconUrl = '$icon/material/person';
              break;
            case WidgetComponentSubType.InputTextLastName:
              componentToUpdate.iconUrl = '$icon/material/person';
              break;
            case WidgetComponentSubType.InputTextName:
              componentToUpdate.iconUrl = '$icon/material/person';
              break;
            case WidgetComponentSubType.InputTextPhone:
              componentToUpdate.iconUrl = 'phone.svg';
              break;
          }
        }
      }
      if (
        existingComponent.subType === WidgetComponentSubType.DropdownRegular &&
        componentToUpdate.subType === WidgetComponentSubType.DropdownRegular
      ) {
        componentToUpdate.iconUrl = existingComponent.iconUrl;
      }
    }
  }

  public static updateInputWithDifTypeStylingFromExisting(
    originalWidgetComponent,
    componentToUpdate,
    onlyTextArea,
    notTextArea,
  ): any {
    let deepCopyOfExisting;
    if (componentToUpdate.subType === 'InputTextArea') {
      deepCopyOfExisting = onlyTextArea
        ? JSON.parse(JSON.stringify(onlyTextArea))
        : JSON.parse(JSON.stringify(originalWidgetComponent));
    } else {
      deepCopyOfExisting = notTextArea
        ? JSON.parse(JSON.stringify(notTextArea))
        : JSON.parse(JSON.stringify(originalWidgetComponent));
    }
    componentToUpdate.height = deepCopyOfExisting.height;
    componentToUpdate.padding = deepCopyOfExisting.padding;
    return componentToUpdate;
  }

  public static updateComponentInGroupStylingFromExisting(
    componentToUpdate: any,
    existingComponent: any,
    widget: Widget,
    availableType?,
    existingComponentWithLabel?,
  ): any {
    const deepCopyOfExisting = JSON.parse(JSON.stringify(existingComponent));
    let previusComponent = null;
    componentToUpdate.children.forEach((child) => {
      if (
        child.type === existingComponent.type ||
        (availableType && availableType.includes(child.type) && availableType.includes(existingComponent.type))
      ) {
        if (deepCopyOfExisting.textProperties) {
          child.textProperties = {
            ...deepCopyOfExisting.textProperties,
            text: child.textProperties.text,
          };
          if (!child.textProperties.fontSize) {
            child.textProperties.fontSize = '14px';
          }
        }
        if (existingComponent.subType !== WidgetComponentSubType.InputTextArea) {
          child.height = deepCopyOfExisting.height;
          child.padding = deepCopyOfExisting.padding;
        }
        child.background = deepCopyOfExisting.background;
        child.border = deepCopyOfExisting.border;

        if (
          widget.brandVariables &&
          child.type === WidgetComponentType.Button &&
          previusComponent?.type === WidgetComponentType.InputText
        ) {
          // If this is a button next to a textfield, we want only the right-hand-side of the radius from brand variables
          WidgetComponentHelpers.setRadiusFromBrandVariables(child, widget);
        } else {
          child.radius = deepCopyOfExisting.radius;
        }

        child.shadow = deepCopyOfExisting.shadow;
        child.alignment.gorizontal = deepCopyOfExisting.alignment.gorizontal;
        child.hover = deepCopyOfExisting.hover;
        child.animation = deepCopyOfExisting.animation;
        if (
          child.type !== WidgetComponentType.Button &&
          existingComponent.type !== WidgetComponentType.Button &&
          componentToUpdate.children.every((child) => child.type !== 'InputText')
        ) {
          child.alignment.vertical = deepCopyOfExisting.alignment.vertical;
        }
        child = WidgetComponentHelpers.updateLabelStylingFromExisting(child, existingComponentWithLabel);
      }
      previusComponent = child;
    });
    return componentToUpdate;
  }

  public static updateSeparateDiscountStylingFromExisting(
    widgetComponent,
    widget: Widget,
    allComponentsOnOtherStates,
    allComponentsOnState,
    languageService,
  ) {
    const buttonComponents = this.getExistingComponents(
      [WidgetComponentType.Button],
      allComponentsOnState,
      allComponentsOnOtherStates,
      [WidgetComponentSubType.ButtonRegular, WidgetComponentSubType.ButtonSubmit],
    );
    const regularTexts = this.getExistingComponents(
      [WidgetComponentType.Text],
      allComponentsOnState,
      allComponentsOnOtherStates,
      [WidgetComponentSubType.TextRegular],
    );
    let buttonDiscount;
    widgetComponent.children.forEach((child) => {
      if (buttonComponents.length !== 0 && child.type === WidgetComponentType.Button) {
        WidgetComponentHelpers.updateComponentsStylingFromExisting(
          child,
          buttonComponents[buttonComponents.length - 1],
          languageService,
          widget,
          null,
          widgetComponent.type,
        );
        // If this is a button next to a textfield, we want only the right-hand-side of the radius from brand variables
        if (widget.brandVariables) {
          WidgetComponentHelpers.setRadiusFromBrandVariables(child, widget);
        }
        buttonDiscount = { ...child };
      } else if (regularTexts.length !== 0 && child.type === WidgetComponentType.Text) {
        WidgetComponentHelpers.updateComponentsStylingFromExisting(
          child,
          regularTexts[regularTexts.length - 1],
          languageService,
          widget,
          null,
          widgetComponent.type,
        );
      }
    });
    widgetComponent.children.forEach((child) => {
      if (buttonDiscount) {
        if (child.type === WidgetComponentType.Text) {
          if (!child.border) {
            child.border = {};
          }
          child.border.color =
            buttonDiscount?.border?.color ||
            widget.brandVariables?.button?.border?.color ||
            buttonDiscount?.background?.color ||
            widget.brandVariables.button?.backgroundColor ||
            null;
          const childButton = widgetComponent.children?.find((child) => child.type === WidgetComponentType.Button);
          if (childButton) {
            child.height = childButton.height;
            child.padding = childButton.padding;
          }
        }
      }
    });
  }

  private static setRadiusFromBrandVariables(component: WidgetComponent, widget: Widget) {
    component.radius = {
      topLeft: '0px',
      bottomLeft: '0px',
      topRight: widget.brandVariables.button.radius.roundValue,
      bottomRight: widget.brandVariables.button.radius.roundValue,
    };
  }

  private static setRightRadiusFromInputBrandVariables(component: WidgetComponent, widget: Widget) {
    component.radius = {
      topLeft: widget.brandVariables.input.radius.roundValue,
      bottomLeft: widget.brandVariables.input.radius.roundValue,
      topRight: '0px',
      bottomRight: '0px',
    };
  }

  private static setLeftRadiusFromInputBrandVariables(component: WidgetComponent, widget: Widget) {
    component.radius = {
      topLeft: '0px',
      bottomLeft: '0px',
      topRight: widget.brandVariables.input.radius.roundValue,
      bottomRight: widget.brandVariables.input.radius.roundValue,
    };
  }

  public static updateSeparateShareStylingFromExisting(
    widgetComponent,
    allComponentsOnOtherStates,
    allComponentsOnState,
    languageService,
    widget,
  ) {
    const regularTexts = this.getExistingComponents(
      [WidgetComponentType.InputText],
      allComponentsOnState,
      allComponentsOnOtherStates,
      [WidgetComponentSubType.InputTextRegular],
    );
    if (regularTexts.length > 0) {
      WidgetComponentHelpers.updateComponentsStylingFromExisting(
        widgetComponent,
        regularTexts[regularTexts.length - 1],
        languageService,
        widget,
        null,
        widgetComponent.type,
      );
    }
  }

  public static updateDiscountStylingFromExisting(componentToUpdate: any, existingComponent: any) {
    const deepCopyOfExisting = JSON.parse(JSON.stringify(existingComponent));
    componentToUpdate.alignment = deepCopyOfExisting.alignment;
    componentToUpdate.margin = deepCopyOfExisting.margin;
    componentToUpdate.children.forEach((child) => {
      deepCopyOfExisting.children.forEach((copy) => {
        if (child.type === copy.type) {
          if (copy.textProperties) {
            child.textProperties = {
              ...copy.textProperties,
              text: child.textProperties.text,
            };
            if (!child.textProperties.fontSize) {
              child.textProperties.fontSize = '14px';
            }
          }
          child.margin = copy.margin;
          child.background = copy.background;
          child.border = copy.border;
          child.radius = copy.radius;
          child.padding = copy.padding;
          child.height = copy.height;
          child.alignment = copy.alignment;
          child.width = copy.width;
        }
      });
    });
    return componentToUpdate;
  }

  public static updateShareStylingFromExisting(componentToUpdate: any, existingComponent: any) {
    const deepCopyOfExisting = JSON.parse(JSON.stringify(existingComponent));
    if (componentToUpdate.type === componentToUpdate.type) {
      if (componentToUpdate.textProperties) {
        componentToUpdate.textProperties = existingComponent.textProperties;
        if (!componentToUpdate.textProperties.fontSize) {
          componentToUpdate.textProperties.fontSize = '14px';
        }
      }
      componentToUpdate.margin = deepCopyOfExisting.margin;
      componentToUpdate.background = deepCopyOfExisting.background;
      componentToUpdate.border = deepCopyOfExisting.border;
      componentToUpdate.radius = deepCopyOfExisting.radius;
      componentToUpdate.padding = deepCopyOfExisting.padding;
      componentToUpdate.height = deepCopyOfExisting.height;
      componentToUpdate.alignment = deepCopyOfExisting.alignment;
      componentToUpdate.width = deepCopyOfExisting.width;
    }
    return componentToUpdate;
  }

  static setBorderIfInheritFromEmailAndSubmitComp(deepCopyOfExisting, componentToUpdate) {
    if (
      deepCopyOfExisting.parentComponentType === WidgetComponentType.Group &&
      deepCopyOfExisting.subType === WidgetComponentSubType.InputTextEmail
    ) {
      if (
        deepCopyOfExisting.border &&
        !deepCopyOfExisting.border.roundValue &&
        (!deepCopyOfExisting.border.right || deepCopyOfExisting.border.right === '0px') &&
        (componentToUpdate.border.right === '0px' || !componentToUpdate.border.right)
      ) {
        componentToUpdate.border.right = deepCopyOfExisting.border.left;
        componentToUpdate.radius.topRight = deepCopyOfExisting.radius.topLeft;
        componentToUpdate.radius.bottomRight = deepCopyOfExisting.radius.bottomLeft;
      }
    }
  }

  public static getHeightInputField(input) {
    return (
      ((parseInt(input?.padding?.roundValue) || 0) * 2 ||
        (parseInt(input?.padding?.top) || 0) + (parseInt(input?.padding?.bottom) || 0)) +
      (input?.iconUrl && input?.iconSize ? input.iconSize : 0) +
      (input?.border
        ? (parseInt(input?.border?.roundValue) || 0) * 2 ||
          (parseInt(input?.border?.top) || 0) + (parseInt(input?.border?.bottom) || 0)
        : 0)
    );
  }

  public static setSameHeightInputField(
    componentToUpdate,
    heightNewComponent,
    heightExistingComponent,
    deepCopyOfExisting,
    widget,
  ) {
    if (heightNewComponent > heightExistingComponent) {
      const differentSize = (heightNewComponent - heightExistingComponent) / 2;
      if (componentToUpdate.padding && componentToUpdate.padding.roundValue) {
        componentToUpdate.padding.top = parseInt(componentToUpdate.padding.roundValue) - differentSize + 'px';
        componentToUpdate.padding.bottom = parseInt(componentToUpdate.padding.roundValue) - differentSize + 'px';
        componentToUpdate.padding.right = componentToUpdate.padding.roundValue;
        componentToUpdate.padding.left = componentToUpdate.padding.roundValue;
        componentToUpdate.padding.roundValue = '';
        // componentToUpdate.textProperties.lineHeight = '18px';
      } else if (componentToUpdate.padding) {
        componentToUpdate.padding.top = parseInt(componentToUpdate.padding.top) - differentSize / 2 + 'px';
        componentToUpdate.padding.bottom = parseInt(componentToUpdate.padding.bottom) - differentSize / 2 + 'px';
      }
    } else if (heightExistingComponent) {
      if (deepCopyOfExisting.height === null && widget.brandVariables) {
        componentToUpdate.height === null;
      } else {
        componentToUpdate.height = heightExistingComponent + 'px';
      }
    }
    return componentToUpdate;
  }

  public static updateWidgetStateStylingAndSettingsFromExisting(
    stateToUpdate: WidgetState,
    existingState: WidgetState,
  ): WidgetState {
    const deepCopyOfExisting = JSON.parse(JSON.stringify(existingState));
    stateToUpdate.background = deepCopyOfExisting.background;
    stateToUpdate.radius = deepCopyOfExisting.radius;
    stateToUpdate.padding = deepCopyOfExisting.padding;
    stateToUpdate.shadow = deepCopyOfExisting.shadow;
    stateToUpdate.margin = deepCopyOfExisting.margin;
    stateToUpdate.height = deepCopyOfExisting.height;
    stateToUpdate.width = deepCopyOfExisting.width;
    if (deepCopyOfExisting.mobile) {
      if (!stateToUpdate.mobile) stateToUpdate.mobile = {};
      stateToUpdate.mobile.height = deepCopyOfExisting.mobile.height;
      stateToUpdate.mobile.padding = deepCopyOfExisting.mobile.padding;
      stateToUpdate.mobile.margin = deepCopyOfExisting.mobile.margin;
    }

    if (existingState.productTourProperties) {
      stateToUpdate.productTourProperties = existingState.productTourProperties;
      stateToUpdate.productTourProperties.tourConnectorStyle =
        deepCopyOfExisting.productTourProperties.tourConnectorStyle;
      stateToUpdate.productTourProperties.tourConnectorSize =
        deepCopyOfExisting.productTourProperties.tourConnectorSize;
      stateToUpdate.productTourProperties.connectorPosition =
        deepCopyOfExisting.productTourProperties.connectorPosition;
      stateToUpdate.productTourProperties.tourConnectorTargetDecoration =
        deepCopyOfExisting.productTourProperties.tourConnectorTargetDecoration;
      stateToUpdate.productTourProperties.tourConnectorTargetSize =
        deepCopyOfExisting.productTourProperties.tourConnectorTargetSize;
      stateToUpdate.productTourProperties.connectorPosition =
        deepCopyOfExisting.productTourProperties.connectorPosition;
      stateToUpdate.productTourProperties.connectorTargetPosition =
        deepCopyOfExisting.productTourProperties.connectorTargetPosition;
      stateToUpdate.productTourProperties.highlightColor = deepCopyOfExisting.productTourProperties.highlightColor;
    }
    return stateToUpdate;
  }

  public static updateWidgetStateSingleStyleAndSettingsFromExisting(
    stateToUpdate: WidgetState,
    existingState: WidgetState,
    diiferencesInStyle,
  ): WidgetState {
    const hasDiff = diiferencesInStyle?.hasDifferences && diiferencesInStyle.differences?.length > 0;
    if (!hasDiff) return stateToUpdate;
    const deepCopyOfExisting = JSON.parse(JSON.stringify(existingState));
    diiferencesInStyle.differences.forEach((dif) => {
      const prop = dif.path.split('.')[0];
      const innerProp = dif.path.split('.')[1];
      if (
        prop === 'radius' ||
        prop === 'padding' ||
        prop === 'margin' ||
        prop === 'width' ||
        prop === 'height' ||
        prop === 'background' ||
        prop === 'shadow'
      ) {
        WidgetComponentHelpers.copyProp(stateToUpdate, prop, innerProp, deepCopyOfExisting);
      }
      if (prop === 'mobile') {
        if (innerProp === 'height' || innerProp === 'padding' || innerProp === 'margin')
          WidgetComponentHelpers.copyProp(stateToUpdate, prop, innerProp, deepCopyOfExisting);
      }
      if (prop === 'productTourProperties' && existingState.productTourProperties) {
        if (
          innerProp === 'tourConnectorStyle' ||
          innerProp === 'tourConnectorSize' ||
          innerProp === 'connectorPosition' ||
          innerProp === 'tourConnectorTargetDecoration' ||
          innerProp === 'tourConnectorTargetSize' ||
          innerProp === 'connectorTargetPosition' ||
          innerProp === 'highlightColor'
        ) {
          WidgetComponentHelpers.copyProp(stateToUpdate, prop, innerProp, deepCopyOfExisting);
        }
      }
    });
    return stateToUpdate;
  }

  public static updateDeadlineStylingFromExisting(componentToUpdate: any, existingComponent: any): any {
    const deepCopyOfExisting = JSON.parse(JSON.stringify(existingComponent));
    if (deepCopyOfExisting.textProperties) {
      componentToUpdate.textProperties = {
        ...deepCopyOfExisting.textProperties,
        text: componentToUpdate.textProperties?.text,
      };
    }
    componentToUpdate.labelProperties = deepCopyOfExisting.labelProperties;
    const { delimiterText, delimiterColor, background, border, radius, padding, shadow, margin } =
      deepCopyOfExisting.deadlineProperties ?? {};
    componentToUpdate.deadlineProperties = {
      ...componentToUpdate.deadlineProperties,
      delimiterText,
      delimiterColor,
      background,
      border,
      radius,
      padding,
      shadow,
      margin,
    };
    componentToUpdate.background = deepCopyOfExisting.background;
    componentToUpdate.border = deepCopyOfExisting.border;
    componentToUpdate.radius = deepCopyOfExisting.radius;
    componentToUpdate.padding = deepCopyOfExisting.padding;
    componentToUpdate.margin = deepCopyOfExisting.margin;
    componentToUpdate.shadow = deepCopyOfExisting.shadow;
    componentToUpdate.alignment = deepCopyOfExisting.alignment;
    componentToUpdate.mobile = deepCopyOfExisting.mobile;
    if (!componentToUpdate.parentId && !existingComponent.parentId) {
      componentToUpdate.width = deepCopyOfExisting.width;
    }
    return componentToUpdate;
  }

  /**
   * Applicable attribution options for a widget, in enum order, for the "assisting sales"
   * dropdown. Impression, Clickthrough and None are always offered (Clickthrough
   * deliberately, since a promotion click is hard to detect reliably and we never want to
   * hide it); Conversion/CopyCoupon/Share require matching content. The selected value is
   * always kept, and all options are returned when the component tree isn't loaded.
   */
  public static getAvailableAttributionTypes(widget: Widget): AttributionType[] {
    if (!widget || !widget.states) {
      return Object.values(AttributionType);
    }

    const components = this.getAllComponentsOnWidget(widget) ?? [];

    const hasCoupon = components.some(
      (c) =>
        c.subType === WidgetComponentSubType.DiscountCodeRegular ||
        c.subType === WidgetComponentSubType.ShareCouponCode,
    );

    // Detect share via the share-specific subtypes and the ShareCampaign type, never
    // WidgetComponentType.Share: that type is also used by the "Unique Coupon Code"
    // component (subType ShareCouponCode), which would otherwise look like sharing.
    const hasShare =
      !!widget.campaign?.referralCampaignGroup ||
      components.some(
        (c) =>
          c.type === WidgetComponentType.ShareCampaign ||
          c.subType === WidgetComponentSubType.ButtonShare ||
          c.subType === WidgetComponentSubType.ShareCampaignGroup,
      );

    return Object.values(AttributionType).filter((type) => {
      // Never hide the value already selected on this variant.
      if (type === widget.attributionType) {
        return true;
      }
      switch (type) {
        case AttributionType.Conversion: {
          return !!widget.hasInputFields;
        }
        case AttributionType.CopyCoupon: {
          return hasCoupon;
        }
        case AttributionType.Share: {
          return hasShare;
        }
        default: {
          return true;
        }
      }
    });
  }

  public static getAllComponentsOfTypes(
    componentTypes: Array<WidgetComponentType>,
    components: Array<WidgetComponent>,
    componentSubTypes?: Array<WidgetComponentSubType>,
  ): Array<WidgetComponent> {
    if ((!componentTypes || !components) && (!componentSubTypes || !components)) {
      return undefined;
    }
    return components.filter((c) => {
      return componentTypes ? componentTypes.indexOf(c.type) !== -1 : componentSubTypes.indexOf(c.subType) !== -1;
    });
  }

  public static getAllComponentsOnState(
    widgetState: WidgetState,
    ignoreDiscountCodeChildren = false,
  ): Array<WidgetComponent> {
    if (!widgetState || !widgetState.containers) {
      return undefined;
    }
    const components = [] as Array<WidgetComponent>;
    widgetState.containers.forEach((container: WidgetContainer) => {
      if (container.components) {
        container.components.forEach((component: WidgetComponent) => {
          !component.containerId ? (component.containerId = container.id) : null;
          components.push(component);
          if (
            component.children &&
            (ignoreDiscountCodeChildren === false || component.type !== WidgetComponentType.DiscountCode)
          ) {
            WidgetComponentsService.setChildrenProperties(component, component.containerId);
            component.children.forEach((child: WidgetComponent) => {
              !child.containerId ? (child.containerId = container.id) : null;
              components.push(child);
            });
          }
        });
      }
    });
    if (widgetState.floatingContainer && widgetState.floatingContainer.components) {
      components.push.apply(components, widgetState.floatingContainer.components);
    }
    return components;
  }

  public static getAllComponentsOnWidget(
    widget: Widget,
    skipStateId?: number,
    ignoreDiscountCodeChildren = false,
  ): Array<WidgetComponent> {
    if (!widget || !widget.states) {
      return undefined;
    }
    const components = [] as Array<WidgetComponent>;
    widget.states.forEach((state: WidgetState) => {
      if (skipStateId && skipStateId === state.id) {
        return;
      }
      if (state.containers) {
        state.containers.forEach((container: WidgetContainer) => {
          if (container.components) {
            container.components.forEach((component: WidgetComponent) => {
              component.containerId = container.id;
              components.push(component);
              if (
                component.children &&
                (ignoreDiscountCodeChildren === false || component.type !== WidgetComponentType.DiscountCode)
              ) {
                component.children.forEach((child: WidgetComponent) => {
                  child.containerId = container.id;
                  if (child.children?.length > 0) {
                    child.children.forEach((nestedChild: WidgetComponent) => {
                      nestedChild.containerId = container.id;
                    });
                  }
                  components.push(child);
                });
              }
            });
          }
        });
      }
      if (state.floatingContainer && state.floatingContainer.components) {
        components.push.apply(components, state.floatingContainer.components);
      }
    });
    return components;
  }

  public static renderCustomFont(customFont: AccountCustomFont): void {
    const fontName =
      customFont.name.indexOf(',') === -1
        ? customFont.name
        : customFont.name.substring(0, customFont.name.indexOf(',') - 1);
    if (customFont.type === AccountCustomFontType.UploadedFont) {
      const stylesArray = [] as Array<string>;
      customFont.fontFiles.forEach((uploadedFont) => {
        stylesArray.push(
          `@font-face {
          font-family: ${fontName};
          src: url(${uploadedFont.url}) format('${this.translateFontTypeToValue(uploadedFont.type)}');
          ${uploadedFont.weight ? 'font-weight: ' + uploadedFont.weight + ';' : ''}
          ${uploadedFont.style ? 'font-style: ' + uploadedFont.style + ';' : ''}
        }`,
        );
      });

      const node = document.createElement('style');
      node.innerHTML = stylesArray.join('\n');
      document.body.appendChild(node);
    }
  }

  private static translateFontTypeToValue(type: AccountCustomFontFileType): string {
    if (type === AccountCustomFontFileType.Woff) {
      return 'woff';
    } else if (type === AccountCustomFontFileType.Woff2) {
      return 'woff2';
    } else if (type === AccountCustomFontFileType.Otf) {
      return 'opentype';
    } else if (type === AccountCustomFontFileType.Ttf) {
      return 'truetype';
    }
    throw new Error('Unrecognized font type: ' + type);
  }

  public static excludeProps(comp: any): any {
    for (const key in comp) {
      if (key === '$type') {
        delete comp[key];
      }
      if (typeof comp[key] === 'object') {
        for (const key_1 in comp[key]) {
          if (key_1 === '$type') {
            delete comp[key][key_1];
          }
          if (typeof comp[key][key_1] === 'object') {
            for (const key_2 in comp[key][key_1]) {
              if (key_2 === '$type') {
                delete comp[key][key_1][key_2];
              }
            }
          }
        }
      }
    }
    return comp;
  }

  public static compareComponent(originalComponent, component, isChanges, hasUnpublishedChangesBS): boolean {
    if (isChanges || hasUnpublishedChangesBS) {
      return;
    }
    if (originalComponent && component) {
      const comp = WidgetComponentHelpers.excludeProps(component);
      const origComp = WidgetComponentHelpers.excludeProps(originalComponent);
      isChanges = JSON.stringify(comp) !== JSON.stringify(origComp);
      if (component.type === 'Regular' && component.subType !== 'ContainerRegular') {
        for (const key in origComp) {
          if (origComp[key] !== comp[key]) {
            if (typeof comp[key] === 'object') {
              for (const key_1 in origComp[key]) {
                if (origComp[key][key_1] !== comp[key][key_1]) {
                  isChanges = true;
                  return (isChanges = true);
                } else {
                  isChanges = false;
                }
              }
            } else {
              return (isChanges = true);
            }
          }
        }
      }
      return isChanges;
    }
  }

  public static removeExtraPropertyForComparisonState(
    originalComponent,
    component,
    isChanges,
    hasUnpublishedChangesBS,
  ): boolean {
    const unWrittenProperty = [
      'actionAutomationId',
      'actionGoalTriggerbeeLog',
      'actionScript',
      'actionTriggerDelay',
      'actionType',
      'actionValue',
    ];
    unWrittenProperty.forEach((y) => {
      if (!component[y] && !originalComponent[y]) {
        component[y] = originalComponent[y];
      }
      if (!originalComponent[y] && !component[y]) {
        originalComponent[y] = component[y];
      }
    });
    delete originalComponent.containers;
    delete component.containers;
    return (isChanges = WidgetComponentHelpers.compareComponent(
      originalComponent,
      component,
      isChanges,
      hasUnpublishedChangesBS,
    ));
  }

  public static setDefaultLabel(type, subType, languageService) {
    let placeholder: string;
    if (type === WidgetComponentType.InputText) {
      switch (subType) {
        case WidgetComponentSubType.InputTextEmail:
          placeholder = languageService.translateInstantly('campaign.widget.component.input-text-email.label');
          break;
        case WidgetComponentSubType.InputTextPassword:
          placeholder = languageService.translateInstantly('campaign.widget.component.input-text-password.label');
          break;
        case WidgetComponentSubType.InputTextName:
          placeholder = languageService.translateInstantly('campaign.widget.component.input-text-name.label');
          break;
        case WidgetComponentSubType.InputTextFirstName:
          placeholder = languageService.translateInstantly(
            'campaign.widget.component.input-text-name.first-name-label',
          );
          break;
        case WidgetComponentSubType.InputTextLastName:
          placeholder = languageService.translateInstantly('campaign.widget.component.input-text-name.last-name-label');
          break;
        case WidgetComponentSubType.InputTextPhone:
          placeholder = languageService.translateInstantly('campaign.widget.component.input-text-phone.label');
          break;
        case WidgetComponentSubType.InputTextArea:
          placeholder = languageService.translateInstantly('campaign.widget.component.input-textarea.label');
          break;
        case WidgetComponentSubType.InputTextRegular:
          placeholder = languageService.translateInstantly('campaign.widget.component.input-text-regular.label');
          break;
        default:
          placeholder = languageService.translateInstantly('campaign.widget.component.input-text.label');
      }
    } else if (type === WidgetComponentType.ButtonList) {
      switch (subType) {
        case WidgetComponentSubType.ButtonListNumericalRating:
        case WidgetComponentSubType.ButtonListSmileyRating:
        case WidgetComponentSubType.ButtonListStarRating:
        case WidgetComponentSubType.ButtonListThumbRating:
          placeholder = languageService.translateInstantly('campaign.widget.component.rate-label');
          break;
        default:
          placeholder = languageService.translateInstantly('campaign.widget.component.common-label');
      }
    } else if (type === WidgetComponentType.InternationalPhone) {
      placeholder = languageService.translateInstantly('campaign.widget.component.input-text-phone.label');
    } else {
      placeholder = languageService.translateInstantly('campaign.widget.component.common-label');
    }
    return placeholder;
  }

  public static isSubType(subTypeOnComponent: WidgetComponentSubType, subType: WidgetComponentSubType): boolean {
    if (subTypeOnComponent == WidgetComponentSubType.ButtonListCsatStarRating) {
      subTypeOnComponent = WidgetComponentSubType.ButtonListStarRating;
    }
    if (subTypeOnComponent == WidgetComponentSubType.ButtonListCsatSmileyRating) {
      subTypeOnComponent = WidgetComponentSubType.ButtonListSmileyRating;
    }
    if (subTypeOnComponent == WidgetComponentSubType.ButtonListCsatNumericalRating) {
      subTypeOnComponent = WidgetComponentSubType.ButtonListNumericalRating;
    }

    return subType == subTypeOnComponent;
  }

  public static isCSAT(subType): boolean {
    if (
      subType == WidgetComponentSubType.ButtonListCsatStarRating ||
      subType == WidgetComponentSubType.ButtonListCsatSmileyRating ||
      subType == WidgetComponentSubType.ButtonListCsatNumericalRating
    ) {
      return true;
    }
  }

  public static pickTitleForState(state, languageService): string {
    let title = '';
    for (let i = 0; i < state.containers.length; i++) {
      for (let j = 0; j < state.containers[i].components.length; j++) {
        if (
          (state.containers[i].components[j].label?.text || state.containers[i].components[j].textProperties?.text) &&
          state.containers[i].components[j].label?.text !== '' &&
          state.containers[i].components[j].subType !== WidgetComponentSubType.ButtonHide &&
          state.containers[i].components[j].type !== WidgetComponentType.Button
        ) {
          if (state.containers[i].components[j].subType === WidgetComponentSubType.ShareCouponCode) {
            title = document
              .getElementsByTagName('app-widget-component-share')[0]
              ?.getElementsByClassName('text-content')[0]?.innerHTML;
            break;
          } else {
            title =
              state.containers[i].components[j].label?.text || state.containers[i].components[j].textProperties?.text;
            break;
          }
        }
      }
    }

    const allComponents = this.getAllComponentsOnState(state);
    if (
      allComponents.length === 1 &&
      allComponents[0].type === WidgetComponentType.Button &&
      allComponents[0].textProperties?.text &&
      allComponents[0].subType !== WidgetComponentSubType.ButtonHide
    ) {
      title = allComponents[0].textProperties.text;
    }

    if (!title) {
      return (
        languageService.translateInstantly('campaign.widget.sidebar.edit.button-action.state') +
        ' ' +
        (+state.order + 1)
      );
    }
    const htmlTagEnd = [
      '</h1>',
      '</h2>',
      '</h3>',
      '</h4>',
      '</h5>',
      '</h6>',
      '</span>',
      '</p>',
      '</strong>',
      '</a>',
      '</em>',
    ];
    const htmlTagStart = ['<h1', '<h2', '<h3', '<h4', '<h5', '<h6', '<span', '<p', '<strong', '<a', '<em'];
    const htmlTagFull = [
      '<h1>',
      '<h2>',
      '<h3>',
      '<h4>',
      '<h5>',
      '<h6>',
      '<span>',
      '<p>',
      '<strong>',
      '<br />',
      '<a>',
      '<em>',
    ];

    if (htmlTagEnd.some((tag) => title.includes(tag))) {
      htmlTagEnd.forEach((tag) => {
        if (title.includes(tag)) {
          while (title.includes(tag)) {
            title = title.replace(tag, '');
          }
        }
      });
    }
    if (htmlTagFull.some((tag) => title.includes(tag))) {
      htmlTagFull.forEach((tag) => {
        if (title.includes(tag)) {
          while (title.includes(tag)) {
            title = title.replace(tag, '');
          }
        }
      });
    }
    if (htmlTagStart.some((tag) => title.includes(tag))) {
      htmlTagStart.forEach((tag) => {
        if (title.includes(tag)) {
          while (title.includes(tag)) {
            const indexTag = title.indexOf(tag);
            const indexEndTag = title.slice(indexTag).indexOf('">');
            if (indexTag !== -1) {
              const p = title.slice(indexTag, indexTag + indexEndTag + 2);
              title = title.replace(p, '');
            }
          }
        }
      });
    }
    return (title.trim().charAt(0).toUpperCase() + title.slice(1)).replace(/&nbsp;/g, '').replace(/<br>/g, '');
  }

  public static isExistComponentInWidget(widget: Widget, subtype): boolean {
    let result = false;
    widget.states?.forEach((widgetState) => {
      if (widgetState && widgetState.containers) {
        for (let i = 0; i < widgetState.containers.length; i++) {
          if (widgetState.containers[i].components) {
            if (
              widgetState.containers[i].components.some(
                (component) =>
                  component.subType === subtype ||
                  (component.children && component.children.some((child) => child.subType === subtype)),
              )
            ) {
              result = true;
            }
          }
        }
      }
    });
    return result;
  }

  public static getSingleCompsOnWidget(widgetStates) {
    const inputFieldComponents = [];
    const singleComponent = [
      WidgetComponentSubType.InputTextEmail,
      WidgetComponentSubType.InputTextName,
      WidgetComponentSubType.InputTextFirstName,
      WidgetComponentSubType.InputTextLastName,
      WidgetComponentSubType.InputTextPassword,
      WidgetComponentSubType.InternationalPhoneRegular,
      WidgetComponentSubType.ButtonListNpsRating,
      WidgetComponentSubType.ButtonListCsatStarRating,
      WidgetComponentSubType.ButtonListCsatSmileyRating,
      WidgetComponentSubType.ButtonListCsatNumericalRating,
      WidgetComponentSubType.CheckboxConsent,
      WidgetComponentSubType.ButtonSubmit,
      WidgetComponentSubType.DeadlineRegular,
    ];
    widgetStates?.forEach((widgetState) => {
      if (widgetState.containers) {
        widgetState.containers.forEach((container: WidgetContainer) => {
          if (container.components) {
            container.components.forEach((component: WidgetComponent) => {
              if (singleComponent.includes(component.subType)) {
                inputFieldComponents.push(component);
              } else if (component.children) {
                component.children.forEach((child: WidgetComponent) => {
                  if (singleComponent.includes(child.subType)) {
                    inputFieldComponents.push(child);
                  }
                });
              }
            });
          }
        });
      }
    });
    return inputFieldComponents;
  }

  public static getFieldsAndConsent(widget) {
    const dataConsent = { fields: [], consent: [] };
    widget.states.forEach((state) => {
      state.containers.forEach((container) => {
        container.components.forEach((component) => {
          if (
            component.subType === WidgetComponentSubType.InputTextName ||
            component.subType === WidgetComponentSubType.InputTextFirstName ||
            component.subType === WidgetComponentSubType.InputTextLastName ||
            component.subType === WidgetComponentSubType.InputTextEmail ||
            component.subType === WidgetComponentSubType.InputTextPhone ||
            component.subType === WidgetComponentSubType.InputTextPassword
          ) {
            dataConsent.fields.push({
              type: component.subType,
              stateOrder: state.order,
              compOrder: component.order,
            });
          } else if (component.subType === WidgetComponentSubType.CheckboxConsent) {
            dataConsent.consent.push({
              type: component.subType,
              stateOrder: state.order,
              compOrder: component.order,
            });
          }
          if (component.children && component.children.length) {
            component.children.forEach((child: WidgetComponent) => {
              if (
                child.subType === WidgetComponentSubType.InputTextName ||
                child.subType === WidgetComponentSubType.InputTextFirstName ||
                child.subType === WidgetComponentSubType.InputTextLastName ||
                child.subType === WidgetComponentSubType.InputTextEmail ||
                child.subType === WidgetComponentSubType.InputTextPhone ||
                child.subType === WidgetComponentSubType.InputTextPassword
              ) {
                dataConsent.fields.push({
                  type: child.subType,
                  stateOrder: state.order,
                  compOrder: component.order,
                });
              } else if (child.subType === WidgetComponentSubType.CheckboxConsent) {
                dataConsent.consent.push({
                  type: child.subType,
                  stateOrder: state.order,
                  compOrder: component.order,
                });
              }
            });
          }
        });
      });
    });
    return dataConsent;
  }

  public static checkIfPossibleToSplitInputsToSteps(widget) {
    let isSplitInputsToStepsPossibleError;
    const allComponentsOnWidget = WidgetComponentHelpers.getAllComponentsOnWidget(widget);
    const submitButtonComponents = allComponentsOnWidget.filter(
      (comp) => comp.subType === WidgetComponentSubType.ButtonSubmit,
    );
    const backButtonComponents = allComponentsOnWidget.filter((comp) => comp.externalId === 'backButton');
    if (widget) {
      if (submitButtonComponents.length !== 1) {
        isSplitInputsToStepsPossibleError = 'one-submit-button';
        return [false, isSplitInputsToStepsPossibleError, backButtonComponents];
      }
      if (backButtonComponents.length === 0) {
        isSplitInputsToStepsPossibleError = 'no-back-button';
        return [false, isSplitInputsToStepsPossibleError, backButtonComponents];
      }
      if (backButtonComponents.length !== 1) {
        isSplitInputsToStepsPossibleError = 'one-back-button';
        return [false, isSplitInputsToStepsPossibleError, backButtonComponents];
      }
      const allInputComponentsOnWidget = allComponentsOnWidget.filter((component) =>
        WidgetComponentHelpers.isComponentInputField(component),
      );
      if (allInputComponentsOnWidget.length === 0) {
        isSplitInputsToStepsPossibleError = 'no-inputs';
        return [false, isSplitInputsToStepsPossibleError, backButtonComponents];
      }
      const containerIds = allInputComponentsOnWidget
        .map((comp) => comp.containerId)
        .filter((item, i, ar) => ar.indexOf(item) === i);
      if (
        containerIds.length !== 1 ||
        containerIds[0] !== submitButtonComponents[0].containerId ||
        containerIds[0] !== backButtonComponents[0].containerId
      ) {
        isSplitInputsToStepsPossibleError = 'not-single-container';
        return [false, isSplitInputsToStepsPossibleError, backButtonComponents];
      }
      isSplitInputsToStepsPossibleError = undefined;
      return [true, isSplitInputsToStepsPossibleError, backButtonComponents];
    } else {
      isSplitInputsToStepsPossibleError = undefined;
      return [false, isSplitInputsToStepsPossibleError, backButtonComponents];
    }
  }

  public static isComponentNoNeedEdit(component) {
    if (component) {
      return component?.subType === 'PlaceholderRegular' && component?.parentComponentSubType === 'GroupRegular';
    }
  }

  public static isReferralCampaign(campaign: Array<Widget>) {
    return Array.isArray(campaign);
  }

  public static extractReferralCamp(campaignReferral) {
    if (campaignReferral.referringCampaign && !campaignReferral.friendCampaign) {
      return [campaignReferral.referringCampaign];
    } else if (!campaignReferral.referringCampaign && campaignReferral.friendCampaign) {
      return [campaignReferral.friendCampaign];
    } else if (campaignReferral.referringCampaign && campaignReferral.friendCampaign) {
      return [campaignReferral.referringCampaign, campaignReferral.friendCampaign];
    }
  }

  public static isCopiedComponent(componentDescription: ComponentDescription) {
    return componentDescription?.clipId !== undefined && componentDescription?.clipId !== null;
  }

  static componentHasChanges(oldComponent: WidgetComponent, newComponent: WidgetComponent) {
    return JSON.stringify(oldComponent) !== JSON.stringify(newComponent);
  }

  static getComponentsNameBySubType() {
    return {
      ButtonListCheckbox: 'campaign.widget.component.radioButtons.title-checkboxes',
      ButtonListRadio: 'campaign.widget.component.radioButtons.title',
      ButtonListChoices: 'campaign.widget.component.radioButtons.title-choices',
      ButtonListSubscriptionCheckbox: 'campaign.widget.component.subscription-checkbox.title',
      ButtonListStarRating: 'campaign.widget.component.buttonChoices.title-star',
      ButtonListSmileyRating: 'campaign.widget.component.buttonChoices.title-smile',
      ButtonListNumericalRating: 'campaign.widget.component.buttonChoices.title-numerical',
      ButtonListThumbRating: 'campaign.widget.component.buttonChoices.title-thumb',
      ButtonListNpsRating: 'campaign.widget.component.buttonChoices.title-nps',
      ButtonListCsatStarRating: 'campaign.widget.component.buttonChoices.title-csat-star',
      ButtonListCsatSmileyRating: 'campaign.widget.component.buttonChoices.title-csat-smile',
      ButtonListCsatNumericalRating: 'campaign.widget.component.buttonChoices.title-csat-numerical',
    };
  }

  static getStateLayout(widgetState: WidgetState, layout: WidgetLayout) {
    if (widgetState.layout) {
      return widgetState.layout;
    } else {
      return layout;
    }
  }

  static updateQueryParameter(key: string, value: number | string, router) {
    const queryParams = {};
    queryParams[key] = value;
    router.navigate([], {
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  static isElementHasScroll(el) {
    const hasVerticalScroll = el.scrollHeight > el.clientHeight;
    const hasHorizontalScroll = el.scrollWidth > el.clientWidth;
    return { hasVerticalScroll, hasHorizontalScroll };
  }

  public static getSubmitButtonFromWidget(widget: Widget): ButtonComponent | undefined {
    for (const state of widget?.states || []) {
      for (const container of state.containers || []) {
        for (const c of container.components || []) {
          if (WidgetComponentHelpers.isSubmitType(c)) {
            (c as ButtonComponent).containerId = container.id; // TODO: Remove if we fix in back-end so it's already included
            return c as ButtonComponent;
          }

          const childButton = c?.children?.find(
            (child) =>
              child?.subType === WidgetComponentSubType.ButtonSubmit ||
              c.stepNavigationProperties?.lastButtonType === StepNavigationLastButtonType.Submit ||
              child?.actionType === ButtonActionType.SubmitForm,
          ) as ButtonComponent;
          if (childButton) return childButton;
        }
      }
    }
    return undefined;
  }

  public static getComponentByType(widget: Widget, type: WidgetComponentType): WidgetComponent | undefined {
    let foundComponent: WidgetComponent | undefined;
    widget.states?.find((state) => {
      foundComponent = this.getComponentByTypeFromState(state, type);
      return !!foundComponent;
    });

    return foundComponent;
  }

  public static getComponentByTypeFromState(
    state: WidgetState,
    type: WidgetComponentType,
  ): WidgetComponent | undefined {
    return state.containers
      ?.find((container) => container.components.some((comp) => comp.type === type))
      ?.components.find((comp) => comp.type === type);
  }

  public static isButtonListComponent(component): boolean {
    return (
      component.subType === WidgetComponentSubType.ButtonListRadio ||
      component.subType === WidgetComponentSubType.ButtonListCheckbox ||
      component.subType === WidgetComponentSubType.ButtonListChoices ||
      component.subType === WidgetComponentSubType.ButtonListSubscriptionCheckbox
    );
  }

  public static isNumericalNpsComponent(component): boolean {
    return (
      WidgetComponentHelpers.isSubType(component?.subType, WidgetComponentSubType.ButtonListNumericalRating) ||
      WidgetComponentHelpers.isSubType(component?.subType, WidgetComponentSubType.ButtonListNpsRating) ||
      component?.subType === WidgetComponentSubType.ButtonListCsatNumericalRating
    );
  }

  public static convertToCurrentValueByUnit(valueWithUnit: string, value: number, component, metricProp): number {
    let currentEl = document.getElementById(component.id);
    let parentEl = currentEl?.parentElement;
    const elFontSize = parseFloat(getComputedStyle(currentEl)?.fontSize);
    const docFontSize = parseFloat(getComputedStyle(document.documentElement)?.fontSize);
    const parentFontSize = parseFloat(getComputedStyle(parentEl).fontSize);
    if (metricProp === FontMetricsProps.FontSize) {
      if (valueWithUnit.includes('rem') && docFontSize) {
        return +(value * docFontSize).toFixed(2);
      } else if (valueWithUnit.includes('em') && parentFontSize) {
        return +(value * parentFontSize).toFixed(2);
      } else return value;
    } else if (metricProp === FontMetricsProps.LineHeight) {
      if (valueWithUnit.includes('rem') && docFontSize) {
        return Math.round(value * docFontSize * 10) / 10;
      } else if (valueWithUnit.includes('em') && elFontSize) {
        return Math.round(value * elFontSize * 10) / 10;
      } else return value;
    } else if (metricProp === FontMetricsProps.LetterSpacing) {
      if (valueWithUnit.includes('px')) {
        return +(value / elFontSize).toFixed(2);
      } else if (valueWithUnit.includes('rem')) {
        const remInPx = value * docFontSize;
        return +(remInPx / elFontSize).toFixed(2);
      } else return value;
    }
  }

  public static clampSpinTheWheelSize(
    component: WidgetComponent | WidgetState | WidgetContainer,
  ): WidgetComponent | null {
    const wheelWrapper = document.getElementsByClassName('widget-component-spin-the-wheel-wrapper')[0] as HTMLElement;
    const maxWidth = wheelWrapper?.offsetWidth;
    if (!maxWidth) return null;

    if (this.isWidgetState(component)) {
      for (const container of component.containers ?? []) {
        for (const child of container.components ?? []) {
          const clamped = this.clampSpinTheWheelSize(child);
          if (clamped) return clamped;
        }
      }
      return null;
    }

    if (this.isWidgetContainer(component)) {
      for (const child of component.components ?? []) {
        const clamped = this.clampSpinTheWheelSize(child);
        if (clamped) return clamped;
      }
      return null;
    }

    const spinComp = component as WidgetComponent;
    if (spinComp.spinTheWheelProperties?.wheelSize) {
      const clamped = Math.min(spinComp.spinTheWheelProperties.wheelSize, maxWidth);
      if (clamped < spinComp.spinTheWheelProperties.wheelSize) {
        spinComp.spinTheWheelProperties.wheelSize = clamped;
        return spinComp;
      }
      return null;
    }

    if (spinComp.children?.length) {
      for (const child of spinComp.children) {
        const clamped = this.clampSpinTheWheelSize(child);
        if (clamped) return clamped;
      }
    }

    return null;
  }

  static getComponentDifferences(
    oldComponent: WidgetComponent,
    newComponent: WidgetComponent,
  ): {
    hasDifferences: boolean;
    differences: Array<{ path: string; oldValue: any; newValue: any }>;
  } {
    const differences: Array<{ path: string; oldValue: any; newValue: any }> = [];

    const compare = (obj1: any, obj2: any, path: string = '') => {
      if ((obj1 === null || obj1 === undefined) && (obj2 === null || obj2 === undefined)) {
        return;
      }

      if (typeof obj1 !== typeof obj2) {
        differences.push({
          path: path || 'root',
          oldValue: obj1,
          newValue: obj2,
        });
        return;
      }

      if (typeof obj1 !== 'object') {
        if (obj1 !== obj2) {
          differences.push({
            path: path || 'root',
            oldValue: obj1,
            newValue: obj2,
          });
        }
        return;
      }

      if (Array.isArray(obj1) && Array.isArray(obj2)) {
        if (obj1.length !== obj2.length) {
          differences.push({
            path: path ? `${path}.length` : 'length',
            oldValue: obj1.length,
            newValue: obj2.length,
          });
        }
        const maxLength = Math.max(obj1.length, obj2.length);
        for (let i = 0; i < maxLength; i++) {
          compare(obj1[i], obj2[i], path ? `${path}[${i}]` : `[${i}]`);
        }
        return;
      }

      const allKeys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);
      allKeys.forEach((key) => {
        const newPath = path ? `${path}.${key}` : key;
        compare(obj1?.[key], obj2?.[key], newPath);
      });
    };

    compare(oldComponent, newComponent);

    return {
      hasDifferences: differences.length > 0,
      differences: differences,
    };
  }
}

export interface WidgetConditionTypeConfig {
  id?: number;
  type: SessionConditionType;
  icon?: string;
  svgIcon?: string;
  titleKey: string;
  operator?: string;
  descriptionKey: string;
  defaultValue?: string;
  defaultValue2?: string;
  defaultShow: boolean;
  inputWidth?: number;
  inlineEditKey1?: string;
  inlineEditKey2?: string;
  /** Read-only text for a comparison that can't be edited inline. */
  summary?: string;
}

export interface CampaignEventConfig {
  times: number;
  campaignId: number;
}
